import * as THREE from 'three';
// Tells the crash banner which script is ours (inline build: the page URL itself).
window.__tfpAppSrc=import.meta.url;

/* Compact built-in orbit controller (rotate / pan / zoom) — no external addons needed */
class MiniOrbit{
  constructor(cam, dom){
    this.cam=cam; this.dom=dom; this.target=new THREE.Vector3(); this.enabled=true;
    this._sph=new THREE.Spherical();
    let mode=null, px=0, py=0;
    const touches=new Map();           // pointerId -> {x,y}; two touches = pinch zoom / pan
    let pinchDist=0, pinchMid=null;
    const zoomBy=(f)=>{
      const off=this.cam.position.clone().sub(this.target);
      off.multiplyScalar(f);
      if(off.length()<10) off.setLength(10);
      if(off.length()>3500) off.setLength(3500);
      this.cam.position.copy(this.target).add(off); this.cam.lookAt(this.target);
    };
    const panBy=(dx,dy)=>{
      const off=this.cam.position.clone().sub(this.target);
      const dist=off.length();
      const panX=new THREE.Vector3().setFromMatrixColumn(this.cam.matrix,0).multiplyScalar(-dx*dist*0.0016);
      const panY=new THREE.Vector3().setFromMatrixColumn(this.cam.matrix,1).multiplyScalar( dy*dist*0.0016);
      this.target.add(panX).add(panY);
      this.cam.position.copy(this.target).add(off); this.cam.lookAt(this.target);
    };
    dom.addEventListener('contextmenu', e=>e.preventDefault());
    dom.addEventListener('pointerdown', e=>{
      if(!this.enabled) return;
      if(e.pointerType==='touch'){
        touches.set(e.pointerId, {x:e.clientX, y:e.clientY});
        if(touches.size===2){
          const [a,b]=[...touches.values()];
          pinchDist=Math.hypot(a.x-b.x, a.y-b.y);
          pinchMid={x:(a.x+b.x)/2, y:(a.y+b.y)/2};
          mode='pinch';
          try{ dom.setPointerCapture(e.pointerId); }catch(_){}
          return;
        }
      }
      mode = (e.button===2 || e.button===1 || e.shiftKey) ? 'pan' : 'rot';
      px=e.clientX; py=e.clientY;
      try{ dom.setPointerCapture(e.pointerId); }catch(_){}
    });
    dom.addEventListener('pointermove', e=>{
      if(!this.enabled) return;
      if(e.pointerType==='touch' && touches.has(e.pointerId)){
        touches.set(e.pointerId, {x:e.clientX, y:e.clientY});
        if(mode==='pinch'){
          if(touches.size<2) return;
          const [a,b]=[...touches.values()];
          const d=Math.hypot(a.x-b.x, a.y-b.y);
          const mid={x:(a.x+b.x)/2, y:(a.y+b.y)/2};
          if(pinchDist>0 && d>0) zoomBy(pinchDist/d);
          if(pinchMid) panBy(mid.x-pinchMid.x, mid.y-pinchMid.y);
          pinchDist=d; pinchMid=mid;
          return;
        }
      }
      if(!mode || mode==='pinch') return;
      const dx=e.clientX-px, dy=e.clientY-py; px=e.clientX; py=e.clientY;
      const off=this.cam.position.clone().sub(this.target);
      if(mode==='rot'){
        this._sph.setFromVector3(off);
        this._sph.theta -= dx*0.005;
        this._sph.phi   -= dy*0.005;
        this._sph.phi = Math.max(0.05, Math.min(Math.PI-0.05, this._sph.phi));
        off.setFromSpherical(this._sph);
      } else {
        const dist=off.length();
        const panX=new THREE.Vector3().setFromMatrixColumn(this.cam.matrix,0).multiplyScalar(-dx*dist*0.0016);
        const panY=new THREE.Vector3().setFromMatrixColumn(this.cam.matrix,1).multiplyScalar( dy*dist*0.0016);
        this.target.add(panX).add(panY);
      }
      this.cam.position.copy(this.target).add(off);
      this.cam.lookAt(this.target);
    });
    const end=(e)=>{
      if(e && e.pointerType==='touch') touches.delete(e.pointerId);
      if(mode==='pinch'){
        if(touches.size>=2) return;               // still pinching with remaining fingers
        pinchDist=0; pinchMid=null;
        if(touches.size===1){                     // one finger left — hand off to orbit
          const t=[...touches.values()][0];
          mode='rot'; px=t.x; py=t.y;
        } else mode=null;
        return;
      }
      mode=null;
    };
    dom.addEventListener('pointerup', end);
    dom.addEventListener('pointercancel', end);
    dom.addEventListener('wheel', e=>{
      if(!this.enabled) return; e.preventDefault();
      const off=this.cam.position.clone().sub(this.target);
      off.multiplyScalar(e.deltaY>0 ? 1.1 : 0.9);
      if(off.length()<10) off.setLength(10);
      if(off.length()>3500) off.setLength(3500);
      this.cam.position.copy(this.target).add(off);
      this.cam.lookAt(this.target);
    }, {passive:false});
  }
  update(){ this.cam.lookAt(this.target); }
}

/* =====================================================================
   Tree Frog Play — Room Planner.  All units are INCHES.
   World axes: x = plan-east, y = up, z = plan-south.
   ===================================================================== */

const CATALOG = {
  half_panel:   {name:'Half Panel',   w:46,   h:23,   d:1.5,  kind:'panel', holds:10},
  full_panel:   {name:'Full Panel',   w:46,   h:46,   d:1.5,  kind:'panel', holds:20},
  double_panel: {name:'Double Panel', w:46,   h:92,   d:1.5,  kind:'panel', holds:40},
  monkey_bar:   {name:'Monkey Bar',   w:17.58,h:3.25, d:4.125,kind:'bar'},
  bar_run:      {name:'Monkey Bar Run', w:0, h:0,     d:5.625,kind:'run'},
  two_by_four:  {name:'2×4 stringer', w:48,   h:3.5,  d:1.5,  kind:'lumber'},
  mat:          {name:'Climbing Mat', w:72.53,h:36.28,d:4.98, kind:'mat'},
};
const HOLE_SPACING = 16;      // monkey bar mounting holes, on center
const SNAP_R = 1.5;           // magnet radius, inches
const SUPPORT_TOL = 0.75;     // hole-to-framing tolerance

// CPSC Public Playground Safety Handbook derived defaults
// barSpacing: overhead traverse c-to-c max (12" preschool / 15" school-age)
// ladderSpacing: vertical rung ladder c-to-c (12" preschool / 18" school-age)
// firstRung: default first rung height above the landing surface (CPSC caps mount height at 18"/36")
function ageRules(age){
  if(age <= 3) return {barSpacing:12, ladderSpacing:12, firstRung:12, maxGrip:60, takeoff:18, noBars:true};
  if(age <= 5) return {barSpacing:12, ladderSpacing:12, firstRung:12, maxGrip:60, takeoff:18, noBars:false};
  return          {barSpacing:15, ladderSpacing:18, firstRung:16, maxGrip:84, takeoff:36, noBars:false};
}

/* ---------------- state ---------------- */
/* Product hues matched to Tree Frog Play products:
   bar gray #949494, mat gray #BABABA, white #F3F3F3 come straight from the product 3D files;
   the chromatic hold/bar colors are matched to product photos (bright PE-resin shades,
   purple leans orchid, red leans warm). */
/* Product colors sampled from Tree Frog Play product photos (real variant names). */
const PALETTES={
  wall:[['White','#F4F2ED'],['Greige','#D6CFC2'],['Light Gray','#C8CCCE'],['Sage','#B4C0AC'],['Navy','#39465E']],
  floor:[['Light Oak','#CBB490'],['White Oak','#E3D4B8'],['Walnut','#6E5138'],['Gray Wood','#9A9188'],['Charcoal','#6E7276']],
  panel:[['Sanded Birch','birch'],['Textured White','#F2F1EC']],
  hold:[['Black','#3F3F3F'],['White','#EDEDED'],['Blue','#0E93EE'],['Red','#DE241B'],['Yellow','#F4B91D'],['Green','#70B46F'],['Orange','#F06725'],['Purple','#B73673'],['Rainbow','rainbow']],
  bar:[['Charcoal','#454545'],['Pearl White','#EDEAE3'],['Deep Blue Sea','#2841B8'],['Jungle Green','#316A48'],['Purple Potion','#6741C9'],['Cherry Tomato','#E2453C'],['Sky Blue','#63B5E8'],['Mint','#78C494'],['Lavender','#A08CD8'],['Dandelion','#F9D616']],
  mat:[['Mink Gray','#C3C9C9'],['Black Bear','#3F3F3F'],['Navajo Sand','#DBD5C9'],['Russian Blue','#6F7B81'],['Moss Green','#7B9963']],
};
const RAINBOW=['#DE241B','#F06725','#F4B91D','#70B46F','#0E93EE','#B73673'];
/* Hold families — colors sampled from product photos */
const HOLD_STYLES={
  classic:{name:'Classic', def:'#3F3F3F',
    palette:[['Black','#3F3F3F'],['White','#EDEDED'],['Blue','#0E93EE'],['Red','#DE241B'],['Yellow','#F4B91D'],['Green','#70B46F'],['Orange','#F06725'],['Purple','#B73673'],['Rainbow','rainbow']]},
  rock:{name:'Rock', def:'#8A8A8A',
    palette:[['Gray','#8A8A8A'],['Black','#2A2A2A'],['White','#F3F3F3'],['Orange','#C96A2E'],['Brown','#A3705A']]},
  leaf:{name:'Leaf', def:'#4E9C4A',
    palette:[['Green','#4E9C4A'],['Orange','#C96A1E'],['Red','#CE3A1D'],['Brown','#96604F'],['Yellow','#D4B426'],['Fall Mix','fallmix']]},
  wood:{name:'Wood', def:'#D6BEA0',
    palette:[['Beech','#D6BEA0']]},
};
const COLOR_MIXES={ rainbow:RAINBOW, fallmix:['#CE3A1D','#C96A1E','#D4B426','#96604F'] };
/* Shopify catalog map (from products_export) — used by the cart integration phase.
   Variant IDs resolve at runtime via /products/{handle}.js on the store domain. */
const TFP_CATALOG={
  half_panel:{handle:'half-climbing-panel', price:275, colors:{birch:'Birch - Sanded', white:'White - Textured'}},
  full_panel:{handle:'full-climbing-panel', price:495, colors:{birch:'Birch - Sanded', white:'White - Textured'}},
  double_panel:{handle:'double-climbing-panel', price:945, colors:{birch:'Birch - Sanded', white:'White - Textured'}},
  monkey_bar:{handle:'monkey-bar', price:34, colors:['Charcoal','Pearl White','Deep Blue Sea','Jungle Green','Purple Potion','Cherry Tomato','Sky Blue','Mint','Lavender','Dandelion']},
  mat:{handle:'climbing-pad', price:249, colors:['Mink Gray','Black Bear','Navajo Sand','Russian Blue','Moss Green']},
  holds:{classic:{handle:'classic-climbing-holds', price:75}, rock:{handle:'rock-climbing-holds', price:95},
         leaf:{handle:'leaf-climbing-holds', price:95}, wood:{handle:'wooden-climbing-holds', price:75}},
};
const DEFAULT_COLORS={wall:'#F4F2ED',floor:'#CBB490',panel:'birch',hold:'#3F3F3F',bar:'#454545',mat:'#C3C9C9'};


/* Half panels laid horizontally — shared by the auto-layout and the wizard's sizing step. */
const PANEL_W=46, PANEL_H=23;

let state = {
  settings:{ ceil:96, base:3.5, pad:0, age:6,
             studSp:16, studOff:0, joistSp:16, joistOff:0, joistWall:0,
             holdStyle:'classic',
             // null = fill the wall; a number caps the climbing-wall block
             wallCols:null, wallRows:null, matCount:null,
             colors:{...DEFAULT_COLORS} },
  points:[], closed:false,
  wallOpts:[],           // per-wall auto-fill: {p:panels, t:overhead run, s:ladder}
  genStamp:null,         // fingerprint of last auto-generation inputs
  objects:[],            // {id,type,surf:{k:'wall',i}|{k:'ceil'},u,v,rot,len?,spacing?,count?,stringers?,auto?}
};
let nextId = 1;
const undoStack = [];
function pushUndo(){ undoStack.push(JSON.stringify(state)); if(undoStack.length>25) undoStack.shift(); }
let hashTimer=null, loadingHash=false;    // declared early: sketch saves state before the 3D section loads
function undo(){ const s = undoStack.pop(); if(!s) return;
  state = JSON.parse(s); if(typeof selIds!=='undefined') selIds.clear(); refreshAll(); toast('Undone'); }

/* ---------------- small utils ---------------- */
const $ = id => document.getElementById(id);
function toast(msg){ const t=$('toast'); t.textContent=msg; t.classList.add('show');
  clearTimeout(t._t); t._t=setTimeout(()=>t.classList.remove('show'),1800); }

// parse `10' 6"`, `10.5'`, `126`, `126"`  -> inches
function parseLen(str){
  if(typeof str==='number') return str;
  str = String(str).trim(); if(!str) return NaN;
  const ftIn = str.match(/^(-?[\d.]+)\s*'\s*([\d.]+)?\s*"?$/);
  if(ftIn) return parseFloat(ftIn[1])*12 + (ftIn[2]?parseFloat(ftIn[2]):0);
  const inches = str.match(/^(-?[\d.]+)\s*"?$/);
  if(inches) return parseFloat(inches[1]);
  return NaN;
}
function fmtLen(v){
  const neg = v<0?'-':''; v=Math.abs(v);
  const ft=Math.floor(v/12), inch=Math.round((v-ft*12)*100)/100;
  if(ft===0) return `${neg}${inch}"`;
  if(inch===0) return `${neg}${ft}'`;
  return `${neg}${ft}' ${inch}"`;
}
function fmtInches(v){ return `${Math.round(v*100)/100}\"`; }
function fmtFtInOption(v){
  return Number.isInteger(v) ? `${v}"` : `${v.toFixed(1).replace(/\.0$/,'')}"`;
}
function ftInMarkup(pre, minFt, maxFt, inchStep){
  let h='<span class="lenPick">';
  h+='<select id="'+pre+'Ft" aria-label="Feet">';
  for(let f=minFt; f<=maxFt; f++) h+='<option value="'+f+'">'+f+'\'</option>';
  h+='</select><select id="'+pre+'In" aria-label="Inches">';
  for(let i=0; i<12; i+=inchStep) h+='<option value="'+i+'">'+fmtFtInOption(i)+'</option>';
  h+='</select></span>';
  return h;
}
function roundToStep(v, step){ return Math.round(v/step)*step; }
function syncFtInPair(pre, value, inchStep){
  const ft=$(pre+'Ft'), inch=$(pre+'In'); if(!ft || !inch) return;
  let f=Math.floor(value/12);
  let i=roundToStep(value - f*12, inchStep);
  if(i>=12){ f += Math.floor(i/12); i = roundToStep(i%12, inchStep); }
  ft.value=String(f);
  inch.value=String(i);
}
function wireFtIn(pre, get, set, minIn, maxIn, inchStep){
  const ft=$(pre+'Ft'), inch=$(pre+'In'); if(!ft || !inch) return;
  const syncSel=()=>syncFtInPair(pre, get(), inchStep);
  syncSel();
  const onCh=()=>{
    let v=(parseInt(ft.value,10)||0)*12 + (parseFloat(inch.value)||0);
    v=roundToStep(Math.max(minIn, Math.min(maxIn, v)), inchStep);
    set(v); syncSel();
  };
  ft.onchange=onCh;
  inch.onchange=onCh;
}
function mountFtIn(hostId, pre, get, set, minIn, maxIn, inchStep){
  const host=$(hostId); if(!host) return;
  host.innerHTML=ftInMarkup(pre, Math.floor(minIn/12), Math.ceil(maxIn/12), inchStep);
  wireFtIn(pre, get, set, minIn, maxIn, inchStep);
}
function bindLenInput(input, get, set){
  input.value = fmtLen(get());
  input.addEventListener('change', ()=>{
    const v = parseLen(input.value);
    if(isNaN(v)){ input.value = fmtLen(get()); toast('Enter a length like 10\' 6" or 126'); return; }
    set(v); input.value = fmtLen(get());
  });
}

/* ---------------- derived geometry ---------------- */
function walls(){
  const p = state.points, out=[];
  const n = state.closed ? p.length : p.length-1;
  for(let i=0;i<n;i++){
    const a=p[i], b=p[(i+1)%p.length];
    const dx=b.x-a.x, dz=b.z-a.z, len=Math.hypot(dx,dz);
    if(len<1) continue;
    out.push({i:out.length, a, b, len, dir:{x:dx/len, z:dz/len}});
  }
  // interior normal: one CONSISTENT side for the whole chain (never flips wall to wall)
  let flip=1;
  if(state.closed && p.length>2){
    let area=0;
    for(let i=0;i<p.length;i++){ const a=p[i], b=p[(i+1)%p.length]; area += a.x*b.z - b.x*a.z; }
    flip = area>=0 ? 1 : -1;      // left perpendicular faces the polygon interior
  } else if(out.length){
    const c=centroid(); let score=0;
    for(const w of out){
      const mx=(w.a.x+w.b.x)/2, mz=(w.a.z+w.b.z)/2;
      score += (c.x-mx)*(-w.dir.z) + (c.z-mz)*(w.dir.x);
    }
    flip = score>=0 ? 1 : -1;     // majority vote, applied uniformly
  }
  for(const w of out) w.n={x:-w.dir.z*flip, z:w.dir.x*flip};
  return out;
}
function centroid(){
  const p=state.points; if(!p.length) return {x:0,z:0};
  let x=0,z=0; for(const q of p){x+=q.x; z+=q.z;}
  return {x:x/p.length, z:z/p.length};
}
function roomBBox(){
  const p=state.points;
  if(!p.length) return {minx:0,minz:0,maxx:120,maxz:120};
  let minx=1e9,minz=1e9,maxx=-1e9,maxz=-1e9;
  for(const q of p){minx=Math.min(minx,q.x);maxx=Math.max(maxx,q.x);
                    minz=Math.min(minz,q.z);maxz=Math.max(maxz,q.z);}
  if(maxx-minx<24){maxx+=60;minx-=60;}   // give single-wall rooms a floor
  if(maxz-minz<24){maxz+=60;minz-=60;}
  return {minx,minz,maxx,maxz};
}

// dims of an object in its surface plane: w along u, h along v, d = standoff depth
function dims(o){
  const c=CATALOG[o.type];
  let w,h;
  if(o.type==='bar_run'){
    const sp=o.spacing||12, n=Math.max(2,o.count||2), L=(n-1)*sp;
    const alongRun   = o.stringers==='rails' ? Math.max(L+8, L+3.25) : L+3.25;
    const acrossRun  = o.stringers==='per_bar' ? 22 : (o.stringers==='rails' ? 19.5 : CATALOG.monkey_bar.w);
    w=alongRun; h=acrossRun;
  } else {
    w = o.type==='two_by_four' ? (o.len||c.w) : c.w;
    h = c.h;
  }
  if(o.type==='mat'){
    // Mat GLB has long axis along local X (matches CATALOG.w=72.53). At rot=0 the mat's
    // world X extent = long, world Z extent = short. Compute true axis-aligned bounding
    // box at any rotation (including 45°) so collision/bounds checks are accurate.
    const rad = (o.rot||0) * Math.PI / 180;
    const long = c.w, short = c.h;
    w = long*Math.abs(Math.cos(rad)) + short*Math.abs(Math.sin(rad));
    h = long*Math.abs(Math.sin(rad)) + short*Math.abs(Math.cos(rad));
  } else if(o.rot%180===90){
    const t=w; w=h; h=t;
  }
  const d = o.type==='bar_run'
    ? CATALOG.monkey_bar.d + (o.stringers && o.stringers!=='none' ? 1.5 : 0)
    : c.d;
  return {w,h,d};
}

/* =====================================================================
   SKETCH VIEW
   ===================================================================== */
const sk = $('sketchCanvas'); const skWrap=$('sketchCanvasWrap');
const sctx = sk.getContext('2d');
let skDrawing = true, skHover=null, skView=null;   // skView: frozen transform while drawing
let dragIx=-1;
const AXIS_TOL_IN=1.5;
function nearestPointIx(px,py,radiusPx){
  const t=skScale(), p=state.points; let best=-1, bestD=radiusPx;
  for(let i=0;i<p.length;i++){
    const q=toPx(p[i],t); const d=Math.hypot(px-q.x, py-q.y);
    if(d<bestD){ bestD=d; best=i; }
  }
  return best;
}
function moveCornerTo(i, wx, wz){
  const p=state.points, was={...p[i]};
  let nx=wx, nz=wz;
  const nPts=p.length, prev=(i-1+nPts)%nPts, next=(i+1)%nPts;
  const hasPrev = state.closed || i>0;
  const hasNext = state.closed || i<nPts-1;
  // ---- STEP 1: propagate axis-aligned walls (moves neighbors so H/V walls stay H/V) ----
  const propagate=(nbrIx)=>{
    const nb=p[nbrIx];
    const wasHoriz=Math.abs(was.z - nb.z) < AXIS_TOL_IN;
    const wasVert =Math.abs(was.x - nb.x) < AXIS_TOL_IN;
    if(wasHoriz && !wasVert) p[nbrIx]={...nb, z:Math.round(nz)};
    else if(wasVert && !wasHoriz) p[nbrIx]={...nb, x:Math.round(nx)};
  };
  // temporarily place the corner at nx/nz for propagation
  p[i]={x:Math.round(nx), z:Math.round(nz)};
  if(hasPrev) propagate(prev);
  if(hasNext) propagate(next);
  // ---- STEP 2: cardinal snap — pick the corner position that makes both adjacent walls hit 0/45/90/135 ----
  const step=Math.PI/4;
  const dirs=Array.from({length:8},(_,k)=>({dx:Math.cos(k*step), dz:Math.sin(k*step)}));
  const constrain=(nbrIx)=>{
    if(nbrIx<0) return null;
    return p[nbrIx];
  };
  const nA = hasPrev ? p[prev] : null;
  const nB = hasNext ? p[next] : null;
  const cursorX=nx, cursorZ=nz;
  let snapped=null;
  if(nA && nB){
    let bestD=Infinity;
    for(const d1 of dirs) for(const d2 of dirs){
      const det=d1.dx*(-d2.dz) - (-d2.dx)*d1.dz;
      if(Math.abs(det)<1e-6) continue;
      const t1=((nB.x-nA.x)*(-d2.dz) - (-d2.dx)*(nB.z-nA.z))/det;
      const px=nA.x + t1*d1.dx, pz=nA.z + t1*d1.dz;
      const d=Math.hypot(px-cursorX, pz-cursorZ);
      if(d<bestD){ bestD=d; snapped={x:px, z:pz}; }
    }
  } else if(nA || nB){
    const nb=nA||nB;
    const dx=cursorX-nb.x, dz=cursorZ-nb.z;
    const dist=Math.hypot(dx,dz);
    if(dist>=1){
      const ang=Math.round(Math.atan2(dz,dx)/step)*step;
      snapped={x:nb.x+Math.cos(ang)*dist, z:nb.z+Math.sin(ang)*dist};
    }
  }
  if(snapped){
    p[i]={x:Math.round(snapped.x), z:Math.round(snapped.z)};
  } else {
    p[i]={x:Math.round(cursorX), z:Math.round(cursorZ)};
  }
}

function skScale(){
  if(skView) return skView;
  const bb=roomBBox(), pad=60;
  const w=sk.clientWidth, h=sk.clientHeight;
  const spanX=Math.max(bb.maxx-bb.minx, 480), spanZ=Math.max(bb.maxz-bb.minz, 300);
  const s=Math.min((w-pad*2)/spanX,(h-pad*2)/spanZ);
  return {s, ox: w/2 - (bb.minx+bb.maxx)/2*s, oz: h/2 - (bb.minz+bb.maxz)/2*s};
}
const toPx = (pt,t)=>({x:pt.x*t.s+t.ox, y:pt.z*t.s+t.oz});
const toIn = (px,py,t)=>({x:(px-t.ox)/t.s, z:(py-t.oz)/t.s});

function drawSketch(){
  const dpr=window.devicePixelRatio||1;
  sk.width=sk.clientWidth*dpr; sk.height=sk.clientHeight*dpr;
  sctx.setTransform(dpr,0,0,dpr,0,0);
  sctx.clearRect(0,0,sk.clientWidth,sk.clientHeight);
  const t=skScale(), p=state.points;
  // walls
  sctx.lineWidth=6; sctx.lineCap='round'; sctx.strokeStyle='#3f9142';
  sctx.beginPath();
  p.forEach((q,i)=>{ const px=toPx(q,t); i?sctx.lineTo(px.x,px.y):sctx.moveTo(px.x,px.y); });
  if(state.closed && p.length>2){ const px=toPx(p[0],t); sctx.lineTo(px.x,px.y); }
  sctx.stroke();
  // rubber band to hover
  if(skDrawing && p.length && skHover){
    const a=toPx(p[p.length-1],t);
    sctx.lineWidth=2; sctx.setLineDash([6,6]); sctx.strokeStyle='#8aa889';
    if(skHover.closingCorner){
      // preview: last -> corner -> first (both dashed)
      const c=toPx(skHover.closingCorner, t);
      const f=toPx({x:skHover.x, z:skHover.z}, t);
      sctx.beginPath(); sctx.moveTo(a.x,a.y); sctx.lineTo(c.x,c.y); sctx.lineTo(f.x,f.y); sctx.stroke();
      sctx.setLineDash([]);
      // highlight the first point since it's the snap target
      sctx.fillStyle='#94BC74'; sctx.strokeStyle='#1E1E1E'; sctx.lineWidth=1.6;
      sctx.beginPath(); sctx.arc(f.x,f.y,9,0,7); sctx.fill(); sctx.stroke();
    } else {
      const b=toPx(skHover,t);
      sctx.beginPath(); sctx.moveTo(a.x,a.y); sctx.lineTo(b.x,b.y); sctx.stroke();
      sctx.setLineDash([]);
      const len=Math.hypot(skHover.x-p[p.length-1].x, skHover.z-p[p.length-1].z);
      sctx.fillStyle='#25301f'; sctx.font='12px sans-serif';
      sctx.fillText(fmtLen(Math.round(len)), b.x+10, b.y-10);
    }
  }
  // labels
  sctx.fillStyle='#556052'; sctx.font='bold 12px sans-serif';
  for(const w of walls()){
    const m=toPx({x:(w.a.x+w.b.x)/2, z:(w.a.z+w.b.z)/2},t);
    const lx=m.x - w.n.x*26 - 30, ly=m.y - w.n.z*26;
    sctx.fillStyle='#556052'; sctx.font='bold 12px sans-serif';
    sctx.fillText(`Wall ${String.fromCharCode(65+w.i)} · ${fmtLen(Math.round(w.len))}`, lx, ly);
    const opt=(state.wallOpts||[])[w.i];
    if(opt && (opt.p||opt.t||opt.s)){
      drawWallPreviews(sctx, t, w, opt);
    }
  }
  // draw drag handles on top when sketch is not in drawing mode
  if(!skDrawing){
    p.forEach((q,i)=>{ const px=toPx(q,t);
      const isEnd = !state.closed && (i===0 || i===p.length-1);
      const armed = endSnapPending && isEnd;
      sctx.fillStyle=(dragIx===i || armed) ? '#94BC74':'#fff';
      sctx.strokeStyle='#1E1E1E'; sctx.lineWidth=1.6;
      const rad = armed ? 9 : 7;
      sctx.beginPath(); sctx.arc(px.x,px.y,rad,0,7); sctx.fill(); sctx.stroke(); });
  }
}
function drawWallPreviews(ctx, t, w, opt){
  const a=toPx(w.a,t), b=toPx(w.b,t);
  const dx=b.x-a.x, dy=b.y-a.y; const wallPx=Math.hypot(dx,dy);
  if(wallPx<8) return;
  const ux=dx/wallPx, uy=dy/wallPx;
  const nx=w.n.x, ny=w.n.z;
  // pick the perpendicular that points into the room
  const perpA={x:-uy, y:ux}, perpB={x:uy, y:-ux};
  const perp = (perpA.x*nx + perpA.y*ny)>=0 ? perpA : perpB;
  if(opt.p){
    const inset=6, depth=12, len=wallPx*0.9, mid=wallPx/2;
    const t0=mid-len/2, t1=mid+len/2;
    const P=(s,d)=>({x:a.x+ux*s+perp.x*d, y:a.y+uy*s+perp.y*d});
    const p1=P(t0,inset), p2=P(t1,inset), p3=P(t1,inset+depth), p4=P(t0,inset+depth);
    ctx.fillStyle='rgba(230,211,164,.85)';
    ctx.strokeStyle='#7a6a4a'; ctx.lineWidth=1;
    ctx.beginPath(); ctx.moveTo(p1.x,p1.y); ctx.lineTo(p2.x,p2.y); ctx.lineTo(p3.x,p3.y); ctx.lineTo(p4.x,p4.y); ctx.closePath();
    ctx.fill(); ctx.stroke();
  }
  if(opt.t){
    // Overhead bar run: LONG axis parallel to the wall (spans it like panels),
    // individual bars PERPENDICULAR to the wall (extend into the room).
    const runL=wallPx*0.9;                          // along wall — spans it, like panels
    const runD=18;                                  // depth into room
    const startAlong=wallPx/2 - runL/2;
    const inset=8;                                  // gap from wall edge
    const P=(dAlong,dInto)=>({
      x:a.x+ux*(startAlong+dAlong)+perp.x*(inset+dInto),
      y:a.y+uy*(startAlong+dAlong)+perp.y*(inset+dInto),
    });
    ctx.fillStyle='rgba(74,79,86,.08)';
    ctx.strokeStyle='rgba(74,79,86,.35)'; ctx.lineWidth=1; ctx.setLineDash([]);
    ctx.beginPath();
    const c1=P(0,0), c2=P(runL,0), c3=P(runL,runD), c4=P(0,runD);
    ctx.moveTo(c1.x,c1.y); ctx.lineTo(c2.x,c2.y); ctx.lineTo(c3.x,c3.y); ctx.lineTo(c4.x,c4.y); ctx.closePath();
    ctx.fill(); ctx.stroke();
    // individual bars: perpendicular to wall, one per ~16 pixels of run length
    const nBars=Math.max(5, Math.round(runL/16));
    ctx.strokeStyle='#3a3f46'; ctx.lineWidth=2.6; ctx.lineCap='round';
    for(let i=0;i<nBars;i++){
      const f=(i+0.5)/nBars;
      const p1=P(runL*f, 0);
      const p2=P(runL*f, runD);
      ctx.beginPath(); ctx.moveTo(p1.x,p1.y); ctx.lineTo(p2.x,p2.y); ctx.stroke();
    }
    ctx.lineCap='butt';
  }
  if(opt.s){
    // Wall-to-ceiling transfer ladder: compact pictogram; positioned to match generation
    // (aligned with the neighboring wall's overhead run if there is one).
    let anchor=0.80;
    const wsAll=walls();
    const N=wsAll.length;
    const isClosed=state.closed;
    const prevIx = isClosed ? (w.i-1+N)%N : (w.i>0 ? w.i-1 : -1);
    const nextIx = isClosed ? (w.i+1)%N : (w.i<N-1 ? w.i+1 : -1);
    const wo = state.wallOpts||[];
    if(prevIx>=0 && wo[prevIx] && wo[prevIx].t) anchor=0.12;
    else if(nextIx>=0 && wo[nextIx] && wo[nextIx].t) anchor=0.88;
    const railLen=Math.min(34, wallPx*0.28);
    const depth=13;
    const inset=3;
    const centerAlong=wallPx*anchor;
    const P=(dAlong,dInto)=>({
      x:a.x+ux*(centerAlong+dAlong)+perp.x*(inset+dInto),
      y:a.y+uy*(centerAlong+dAlong)+perp.y*(inset+dInto),
    });
    // subtle backing so the ladder pops off the wall line
    ctx.fillStyle='rgba(255,255,255,.85)';
    const b1=P(-railLen/2-2,-1), b2=P(railLen/2+2,-1), b3=P(railLen/2+2,depth+1), b4=P(-railLen/2-2,depth+1);
    ctx.beginPath(); ctx.moveTo(b1.x,b1.y); ctx.lineTo(b2.x,b2.y); ctx.lineTo(b3.x,b3.y); ctx.lineTo(b4.x,b4.y); ctx.closePath();
    ctx.fill();
    // two rails, parallel to the wall
    ctx.strokeStyle='#1E1E1E'; ctx.lineWidth=2.2; ctx.lineCap='round';
    let r1=P(-railLen/2,0), r2=P(railLen/2,0), r3=P(-railLen/2,depth), r4=P(railLen/2,depth);
    ctx.beginPath();
    ctx.moveTo(r1.x,r1.y); ctx.lineTo(r2.x,r2.y);
    ctx.moveTo(r3.x,r3.y); ctx.lineTo(r4.x,r4.y);
    ctx.stroke();
    // rungs, perpendicular to the wall
    ctx.lineWidth=1.6;
    const nRungs=5;
    ctx.beginPath();
    for(let i=0;i<nRungs;i++){
      const f=(i+0.5)/nRungs;
      const p1=P(-railLen/2+railLen*f, 0);
      const p2=P(-railLen/2+railLen*f, depth);
      ctx.moveTo(p1.x,p1.y); ctx.lineTo(p2.x,p2.y);
    }
    ctx.stroke();
    ctx.lineCap='butt';
  }
}

function planClose(last, first, prevWall){
  // Decide how to close near the first point cleanly.
  // Returns { mode: 'direct' | 'snap' | 'corner', ... }
  if(!prevWall) return {mode:'direct'};
  const HORIZ=Math.abs(prevWall.dir.z)<0.1, VERT=Math.abs(prevWall.dir.x)<0.1;
  if(!HORIZ && !VERT) return {mode:'direct'};             // previous wall was diagonal
  const dx=first.x-last.x, dz=first.z-last.z;
  const perp = HORIZ ? Math.abs(dx) : Math.abs(dz);
  const along= HORIZ ? Math.abs(dz) : Math.abs(dx);
  if(perp < 1) return {mode:'direct'};                    // already axis-aligned
  // If the close is nearly axis-aligned, snap the last point rather than inserting an L-corner.
  // Threshold: 24" absolute OR 20% of the parallel distance, whichever is larger.
  const tol = Math.max(24, along*0.2);
  if(perp < tol){
    return {mode:'snap',
      snappedLast: HORIZ ? {x:Math.round(first.x), z:Math.round(last.z)}
                         : {x:Math.round(last.x), z:Math.round(first.z)}};
  }
  return {mode:'corner',
    corner: HORIZ ? {x:Math.round(last.x), z:Math.round(first.z)}
                  : {x:Math.round(first.x), z:Math.round(last.z)}};
}
function skSnapPoint(pt){
  const p=state.points;
  if(!p.length) return {x:Math.round(pt.x), z:Math.round(pt.z)};
  const last=p[p.length-1];
  const dx=pt.x-last.x, dz=pt.z-last.z;
  const dist=Math.hypot(dx,dz);
  if(dist<1) return {x:Math.round(last.x), z:Math.round(last.z)};
  // snap to nearest 45° increment (0, 45, 90, 135, ...)
  const step=Math.PI/4;
  const snapA=Math.round(Math.atan2(dz,dx)/step)*step;
  return {
    x:Math.round(last.x + Math.cos(snapA)*dist),
    z:Math.round(last.z + Math.sin(snapA)*dist),
  };
}

let wasDragging=false;
let endSnapPending=false;
sk.addEventListener('mousedown', e=>{
  if(e.button!==0) return;
  const r=sk.getBoundingClientRect();
  const px=e.clientX-r.left, py=e.clientY-r.top;
  const ix=nearestPointIx(px,py,14);
  if(ix<0) return;
  if(skDrawing && ix===0 && state.points.length>2) return;   // let click close the room
  dragIx=ix; pushUndo(); sk.classList.add('dragging'); e.preventDefault();
});
sk.addEventListener('mousemove', e=>{
  const r=sk.getBoundingClientRect(), t=skScale();
  const px=e.clientX-r.left, py=e.clientY-r.top;
  if(dragIx>=0){
    const w=toIn(px, py, t);
    moveCornerTo(dragIx, w.x, w.z);
    endSnapPending=false;
    if(!state.closed && state.points.length>=3 && (dragIx===0 || dragIx===state.points.length-1)){
      const otherIx = dragIx===0 ? state.points.length-1 : 0;
      const dP=toPx(state.points[dragIx], t), oP=toPx(state.points[otherIx], t);
      if(Math.hypot(dP.x-oP.x, dP.y-oP.y) < 20) endSnapPending=true;
    }
    drawSketch();
    return;
  }
  if(!skDrawing){
    sk.classList.toggle('hovering', nearestPointIx(px,py,14)>=0);
    return;
  }
  const p=state.points;
  if(p.length>2){
    const f=toPx(p[0],t);
    if(Math.hypot(px-f.x, py-f.y)<14){
      const last=p[p.length-1], first=p[0];
      const ws=walls();
      const prevWall = ws.length ? ws[ws.length-1] : null;
      const plan=planClose(last, first, prevWall);
      skHover = plan.mode==='corner'
        ? {x:first.x, z:first.z, closingCorner:plan.corner}
        : {x:first.x, z:first.z};
      drawSketch();
      return;
    }
  }
  skHover = skSnapPoint(toIn(px, py, t));
  drawSketch();
});
window.addEventListener('mouseup', ()=>{
  if(dragIx>=0){
    if(endSnapPending){
      state.points.splice(dragIx,1);
      state.closed=true;
      endSnapPending=false;
    }
    dragIx=-1; sk.classList.remove('dragging');
    wasDragging=true;
    afterSketchChange();
    setTimeout(()=>{ wasDragging=false; }, 60);
  }
});
document.addEventListener('keydown', e=>{
  if(e.key==='Escape' && skDrawing && state.points.length>=2){
    skDrawing=false; skHover=null; skView=null; afterSketchChange();
  }
});
sk.addEventListener('click', e=>{
  if(wasDragging) return;
  if(!skDrawing) return;
  const r=sk.getBoundingClientRect(), t=skScale();
  const px=e.clientX-r.left, py=e.clientY-r.top;
  const p=state.points;
  if(p.length>2){
    const f=toPx(p[0],t);
    if(Math.hypot(px-f.x, py-f.y)<14){
      const last=p[p.length-1], first=p[0];
      const ws=walls();
      const prevWall = ws.length ? ws[ws.length-1] : null;
      const plan = planClose(last, first, prevWall);
      if(plan.mode==='snap') p[p.length-1]=plan.snappedLast;   // snap last point to axis-align, no stub
      else if(plan.mode==='corner') p.push(plan.corner);        // truly diagonal: insert L-corner
      state.closed=true; skDrawing=false; skHover=null; skView=null; afterSketchChange(); return;
    }
  }
  if(nearestPointIx(px,py,14)>=0) return;             // clicked on an existing corner (not first)
  const raw=toIn(px, py, t);
  if(!p.length && !skView) skView = skScale();
  p.push(skSnapPoint(raw));
  afterSketchChange();
});
function updateDrawButtons(){
  const btn=$('finishBtn'), hint=$('drawHint');
  if(!btn) return;
  if(skDrawing){
    btn.textContent='Finish drawing'; btn.disabled=false; btn.title='';
    if(hint) hint.textContent = state.points.length>2
      ? 'Click near the first corner to close, or press Esc / Finish.'
      : 'Click on the grid to place corners.';
  } else if(state.closed){
    btn.textContent='Add more walls'; btn.disabled=true;
    btn.title='Delete a wall first to open the shape, then add more.';
    if(hint) hint.textContent='';
  } else if(state.points.length>=1){
    btn.textContent='Continue drawing'; btn.disabled=false;
    btn.title='Add more walls extending from the end of your path';
    if(hint) hint.textContent='';
  } else {
    btn.textContent='Start drawing'; btn.disabled=false; btn.title='';
    if(hint) hint.textContent='';
  }
}
$('finishBtn').onclick=()=>{
  if(skDrawing){
    if(state.points.length<2) return;   // nothing to finish yet
    skDrawing=false; skHover=null; skView=null; afterSketchChange();
  } else if(!state.closed){
    skDrawing=true; skView=skScale();   // freeze view so drawing doesn't rescale
    afterSketchChange();
  }
};
$('clearBtn').onclick=()=>{ pushUndo(); state.points=[]; state.closed=false; state.objects=[];
  state.wallOpts=[]; state.genStamp=null;
  skDrawing=true; skView=null; afterSketchChange(); };

const PRESETS = {
  single:{pts:[{x:0,z:0},{x:144,z:0}],closed:false},
  l:{pts:[{x:0,z:0},{x:144,z:0},{x:144,z:120}],closed:false},
  u:{pts:[{x:0,z:120},{x:0,z:0},{x:168,z:0},{x:168,z:120}],closed:false},
  room:{pts:[{x:0,z:0},{x:168,z:0},{x:168,z:132},{x:0,z:132}],closed:true},
};
document.querySelectorAll('.presets [data-preset]').forEach(b=>{
  b.onclick=()=>{ pushUndo(); const pr=PRESETS[b.dataset.preset];
    state.points=pr.pts.map(q=>({...q})); state.closed=pr.closed; state.objects=[];
    state.wallOpts=[]; state.genStamp=null;
    skDrawing=false; skView=null; afterSketchChange(); };
});

function renderColorRows(){
  const box=$('colorRows'); box.innerHTML='';
  const C=state.settings.colors;
  const rows=[['wall','Walls & ceiling'],['floor','Floor'],['panel','Panels'],['hold','Holds'],['bar','Monkey bars'],['mat','Mats']];
  const CUSTOM_OK=new Set(['wall','floor','panel']);
  const style=state.settings.holdStyle||'classic';
  const paletteFor=key=> key==='hold' ? HOLD_STYLES[style].palette : PALETTES[key];
  const applyLive=()=>{ syncHash();
    if(!$('planView').classList.contains('hidden')){ buildRoom(); lastRoomKey=roomKeyNow(); refreshObjects(); } };
  // (the hold STYLE selector lives in the Build section now; Colors keeps hold colours)
  for(const [key,label] of rows){
    const row=document.createElement('div'); row.className='colorRow';
    const lab=document.createElement('label'); lab.textContent=label; row.appendChild(lab);
    const chips=document.createElement('div'); chips.className='chips';
    const inPalette = paletteFor(key).some(([,v])=>v===C[key]);
    for(const [name,val] of paletteFor(key)){
      const b=document.createElement('button'); b.className='chip'; b.title=name; b.type='button';
      if(val==='rainbow') b.classList.add('rainbow');
      else if(val==='fallmix') b.classList.add('fallmix');
      else if(val==='birch') b.classList.add('birch');
      else b.style.background=val;
      if(C[key]===val) b.classList.add('on');
      if(colorSoldOut(key, val)){
        b.classList.add('soldOut'); b.title=name+' — out of stock';
        b.onclick=()=>toast(name+' is out of stock — pick another color');
      } else {
        b.onclick=()=>{ C[key]=val; renderColorRows(); applyLive(); };
      }
      chips.appendChild(b);
    }
    if(CUSTOM_OK.has(key)){
      const hexIn=document.createElement('input');
      hexIn.className='hexIn'; hexIn.placeholder='#hex'; hexIn.maxLength=7;
      if(!inPalette && /^#[0-9A-Fa-f]{6}$/.test(C[key])){ hexIn.value=C[key]; hexIn.classList.add('on'); }
      hexIn.onchange=()=>{
        let v=hexIn.value.trim(); if(v && v[0]!=='#') v='#'+v;
        if(!/^#[0-9A-Fa-f]{6}$/.test(v)){ toast('Enter a hex color like #A1B2C3'); hexIn.value=''; return; }
        C[key]=v.toUpperCase(); renderColorRows(); applyLive();
      };
      chips.appendChild(hexIn);
    }
    row.appendChild(chips); box.appendChild(row);
  }
  const note=$('customPanelNote');
  if(note) note.style.display =
    (C.panel!=='birch' && !PALETTES.panel.some(([,v])=>v===C.panel)) ? 'block' : 'none';
}
const PANEL_ICON=`<svg viewBox="0 0 26 26"><rect x="3" y="9" width="20" height="8" fill="#E6D3A4" stroke="#1E1E1E" stroke-width="1"/><circle cx="7" cy="11.5" r=".7" fill="#7a6a4a"/><circle cx="12" cy="11.5" r=".7" fill="#7a6a4a"/><circle cx="17" cy="11.5" r=".7" fill="#7a6a4a"/><circle cx="7" cy="14.5" r=".7" fill="#7a6a4a"/><circle cx="12" cy="14.5" r=".7" fill="#7a6a4a"/><circle cx="17" cy="14.5" r=".7" fill="#7a6a4a"/></svg>`;
const BARRUN_ICON=`<svg viewBox="0 0 26 26"><g stroke="#1E1E1E" stroke-width="1"><rect x="4" y="6" width="18" height="1.6" fill="#4a4f56"/><rect x="4" y="10" width="18" height="1.6" fill="#4a4f56"/><rect x="4" y="14" width="18" height="1.6" fill="#4a4f56"/><rect x="4" y="18" width="18" height="1.6" fill="#4a4f56"/></g></svg>`;
const LADDER_ICON=`<svg viewBox="0 0 26 26"><g stroke="#1E1E1E" stroke-width="1.2" fill="none"><line x1="8" y1="3" x2="8" y2="23"/><line x1="18" y1="3" x2="18" y2="23"/><line x1="8" y1="7" x2="18" y2="7"/><line x1="8" y1="12" x2="18" y2="12"/><line x1="8" y1="17" x2="18" y2="17"/></g></svg>`;
function showInfoPop(anchor, meta){
  document.querySelectorAll('.infoPop').forEach(p=>p.remove());
  const rect=anchor.getBoundingClientRect();
  const pop=document.createElement('div'); pop.className='infoPop';
  pop.innerHTML=`<b>${meta.title}</b><div style="margin-top:5px;">${meta.info}</div>`;
  pop.style.left=Math.max(8,rect.left-8)+'px';
  pop.style.top=(rect.bottom+6)+'px';
  document.body.appendChild(pop);
  setTimeout(()=>{ const closer=(e)=>{ if(!pop.contains(e.target)){ pop.remove(); document.removeEventListener('mousedown',closer); }};
    document.addEventListener('mousedown',closer); }, 0);
}
function renderWallList(){
  const box=$('wallList'), ws=walls();
  box.innerHTML='';
  if(!ws.length){ box.innerHTML='<p class="hint">No walls yet.</p>'; return; }
  if(!state.wallOpts) state.wallOpts=[];
  const OPT_META={
    p:{title:'Climbing wall', icon:PANEL_ICON, sub:'Cover this wall with climbing panels + holds.',
       info:`We fill the wall with 46\u201d \u00d7 23\u201d half panels laid horizontally, add matching climbing holds at every insert, and lay matching climbing pads across the floor beneath. You can swap panel sizes, colors, and hold style in the 3D view.
         <h4>Recommended max height for the bottom edge of the first panel</h4>
         <p>The first hold should be easily reachable at about the child\u2019s knee height when they\u2019re standing on the pad. Heights below assume a 5\u201d Tree Frog climbing pad and the fact that the first row of tee-nuts sits about 3.75\u201d up from the panel\u2019s bottom edge.</p>
         <table><thead><tr><th>Youngest climber</th><th>Bottom of panel</th><th>First hold height</th></tr></thead><tbody>
           <tr><td>2\u20135 years</td><td>12\u201d</td><td>15.75\u201d</td></tr>
           <tr><td>6\u20138 years</td><td>15\u201d</td><td>18.75\u201d</td></tr>
           <tr><td>9+ years</td><td>18\u201d</td><td>21.75\u201d</td></tr>
         </tbody></table>`},
    t:{title:'Overhead monkey bars', icon:BARRUN_ICON, sub:'Add a run of monkey bars across the ceiling from this wall.',
       info:`Places a bar run on the ceiling extending outward from this wall. Bar count, spacing, and stringer style are chosen automatically based on your youngest climber. You can move or resize the run in the 3D view.
         <h4>Recommended overhead bar spacing (center to center)</h4>
         <table><thead><tr><th>Youngest climber</th><th>Bar spacing</th></tr></thead><tbody>
           <tr><td>2\u20135 years</td><td>12\u201d</td></tr>
           <tr><td>6\u20138 years</td><td>15\u201d</td></tr>
           <tr><td>9+ years</td><td>up to 18\u201d</td></tr>
         </tbody></table>
         <h4>Distance from the run to the nearest wall</h4>
         <table><thead><tr><th>Adjacent wall</th><th>Rail offset from wall</th></tr></thead><tbody>
           <tr><td>Wall with climbing panels</td><td>18\u201d minimum</td></tr>
           <tr><td>Bare wall</td><td>12\u201d minimum</td></tr>
         </tbody></table>
         <p>The extra clearance in front of a climbing wall gives kids room to swing past the panels without catching their feet.</p>`},
    s:{title:'Wall-to-ceiling transfer ladder', icon:LADDER_ICON, sub:'A ladder going up this wall to reach the overhead bars.',
       info:`Mounts on THIS wall. If a neighboring wall has an overhead run, the ladder is placed directly beneath that run\u2019s end and aligned with a stud. Otherwise it sits near the end of this wall. Rung spacing follows the youngest climber\u2019s age.
         <h4>Recommended ladder rung spacing (center to center)</h4>
         <table><thead><tr><th>Youngest climber</th><th>Rung spacing</th></tr></thead><tbody>
           <tr><td>2\u20135 years</td><td>12\u201d</td></tr>
           <tr><td>6\u20138 years</td><td>18\u201d</td></tr>
           <tr><td>9+ years</td><td>up to 18\u201d</td></tr>
         </tbody></table>
         <h4>Distance from the ladder to the nearest wall</h4>
         <table><thead><tr><th>Adjacent wall</th><th>Rail offset from wall</th></tr></thead><tbody>
           <tr><td>Wall with climbing panels</td><td>18\u201d minimum</td></tr>
           <tr><td>Bare wall</td><td>12\u201d minimum</td></tr>
         </tbody></table>`},
  };
  ws.forEach(w=>{
    if(!state.wallOpts[w.i]) state.wallOpts[w.i]={p:false,t:false,s:false};
    const opt=state.wallOpts[w.i];
    const blk=document.createElement('div'); blk.className='wallBlk';
    const head=document.createElement('div'); head.className='wallHead';
    const lab=document.createElement('label'); lab.innerHTML=`<b>Wall ${String.fromCharCode(65+w.i)}</b>`;
    head.appendChild(lab);
    const lenId='skWall'+w.i;
    head.insertAdjacentHTML('beforeend', ftInMarkup(lenId, 0, 50, 1));
    const canDel = state.closed || w.i===0 || w.i===ws.length-1;
    if(canDel){
      const del=document.createElement('button'); del.type='button'; del.className='wallDel';
      del.textContent='×';
      del.title = state.closed
        ? 'Remove this wall (opens the room here)'
        : 'Remove this wall';
      del.onclick=()=>deleteWallAt(w.i);
      head.appendChild(del);
    }
    blk.appendChild(head);
    for(const key of ['p','t','s']){
      const meta=OPT_META[key];
      const card=document.createElement('div'); card.className='optCard'+(opt[key]?' on':'');
      const cb=document.createElement('input'); cb.type='checkbox'; cb.checked=!!opt[key];
      const ic=document.createElement('div'); ic.className='optIcon'; ic.innerHTML=meta.icon;
      const body=document.createElement('div'); body.className='optBody';
      const title=document.createElement('div'); title.className='optTitle';
      const tt=document.createElement('span'); tt.textContent=meta.title;
      const info=document.createElement('button'); info.type='button'; info.className='infoBtn'; info.textContent='?';
      info.onclick=(e)=>{ e.stopPropagation(); showInfoPop(info, meta); };
      title.appendChild(tt); title.appendChild(info);
      const sub=document.createElement('div'); sub.className='optSub'; sub.textContent=meta.sub;
      body.appendChild(title); body.appendChild(sub);
      card.appendChild(cb); card.appendChild(ic); card.appendChild(body);
      cb.onchange=()=>{
        opt[key]=cb.checked;
        card.classList.toggle('on', cb.checked);
        drawSketch(); syncHash();
      };
      cb.onclick=e=>e.stopPropagation();          // don't double-fire the card handler
      card.onclick=(e)=>{
        if(e.target===info || e.target.classList.contains('infoBtn')) return;
        if(e.target===cb) return;                 // let the checkbox handle itself
        cb.checked=!cb.checked;
        cb.dispatchEvent(new Event('change'));
      };
      blk.appendChild(card);
    }
    box.appendChild(blk);
    // wire after attach — wireFtIn looks the selects up by id, so they must be in the document
    wireFtIn(lenId, ()=>walls()[w.i].len, len=>{
      pushUndo(); setWallLength(w.i, Math.max(6,len)); afterSketchChange();
    }, 6, 600, 1);
  });
  const note=document.createElement('div'); note.className='layoutHint';
  note.innerHTML='<b>This is just your starting layout.</b> In the next step you can move any piece, swap colors, or add and remove items.';
  box.appendChild(note);
  // joist direction options — default to first horizontal wall unless user explicitly picked one
  const js=$('inJoistWall'); const prev=state.settings.joistWall;
  js.innerHTML='';
  ws.forEach(w=>{ const o=document.createElement('option'); o.value=w.i;
    o.textContent='Wall '+String.fromCharCode(65+w.i); js.appendChild(o); });
  if(!state.settings.joistWallExplicit){
    const horizIx = ws.findIndex(w => Math.abs(w.dir.z) < 0.1);
    state.settings.joistWall = horizIx >= 0 ? horizIx : 0;
  } else {
    state.settings.joistWall = Math.min(prev, ws.length-1);
  }
  js.value = state.settings.joistWall;
}
function deleteWallAt(i){
  pushUndo();
  const N=state.points.length;
  if(state.closed){
    // rotate so wall i becomes the missing closing edge; all corners preserved
    const np=[]; for(let k=0;k<N;k++) np.push(state.points[(i+1+k)%N]);
    state.points=np;
    if(state.wallOpts && state.wallOpts.length){
      const no=[];
      for(let k=0;k<N-1;k++) no.push(state.wallOpts[(i+1+k)%N] || {p:false,t:false,s:false});
      state.wallOpts=no;
    }
    state.closed=false;
  } else {
    // open path: only end walls are deletable — drop the outer endpoint
    if(i===0){
      state.points.shift();
      if(state.wallOpts) state.wallOpts.shift();
    } else {
      state.points.pop();
      if(state.wallOpts) state.wallOpts.pop();
    }
  }
  if(state.points.length<2){ skDrawing=true; skView=null; }
  afterSketchChange();
}
function setWallLength(i, len){
  // move every point after segment i along the segment direction
  const ws=walls(), w=ws[i], delta=len-w.len;
  const p=state.points;
  for(let k=i+1;k<p.length;k++){ p[k].x+=w.dir.x*delta; p[k].z+=w.dir.z*delta; }
  if(state.closed && i===p.length-1){ /* closing wall: adjust first point instead */
    p[0].x-=w.dir.x*delta; p[0].z-=w.dir.z*delta; }
}
function afterSketchChange(){
  const ws=walls();
  if(state.wallOpts && state.wallOpts.length>ws.length) state.wallOpts.length=ws.length;
  state.objects = state.objects.filter(o=>{
    if(o.surf.k==='ceil') return true;
    const w=ws[o.surf.i]; if(!w) return false;
    return o.u>=0 && o.u<=w.len;
  });
  renderWallList(); drawSketch(); updateDrawButtons(); syncHash();
}

/* =====================================================================
   3D SCENE
   ===================================================================== */
const canvas = $('three');
const GL_STUB={ setAnimationLoop(){}, setSize(){}, setPixelRatio(){}, render(){},
                domElement:canvas, shadowMap:{}, getContext(){return null;} };
let renderer=GL_STUB, glOK=false;
let controls={enabled:false, target:new THREE.Vector3(), update(){}};
function glLoop(){
  try{ resize(); controls.update(); updateFades(); updateGuidePulse(); updateWizCardPulse(); renderer.render(scene,camera); }
  catch(err){
    renderer.setAnimationLoop(null);
    if(window.__banner) window.__banner('Render loop error: '+err.message+'\n'+(err.stack||'').split('\n').slice(0,3).join('\n'));
    throw err;
  }
}
function tryInitGL(silent){
  if(glOK) return true;
  try{
    renderer = new THREE.WebGLRenderer({canvas, antialias:true, preserveDrawingBuffer:true});
    renderer.shadowMap.enabled = false;   // shadows off — cleaner for a planning tool
    controls = new MiniOrbit(camera, canvas);
    glOK = true;
    renderer.setAnimationLoop(glLoop);
    return true;
  }catch(err){
    renderer = GL_STUB;
    if(!silent && window.__banner) window.__banner(
      '3D view could not start: '+err.message+'\n'+
      'Close any other tabs or apps using 3D graphics, then click “2 · 3D planner” to retry. '+
      'If it keeps failing, fully quit and reopen your browser — its graphics process needs a reset after a crash. Your sketch and layout are safe in the share link.');
    return false;
  }
}
canvas.addEventListener('webglcontextlost', e=>{ e.preventDefault();
  if(window.__banner) window.__banner('The 3D view lost its graphics context. If it does not recover in a few seconds, copy a share link, then fully quit and reopen your browser.'); });
canvas.addEventListener('webglcontextrestored', ()=>{ toast('3D view recovered'); });
tryInitGL(true);   // silent first attempt; a visible retry happens when opening the 3D planner
const scene = new THREE.Scene();
scene.background = new THREE.Color(0xeef1ea);
const camera = new THREE.PerspectiveCamera(50, 1, 1, 5000);

scene.add(new THREE.HemisphereLight(0xffffff, 0xcdd4c6, 1.05));
const sun = new THREE.DirectionalLight(0xfff6e8, 1.4);
scene.add(sun);

function disposeGroup(g){
  g.traverse(node=>{
    if(node.geometry && node.geometry.userData && node.geometry.userData.own) node.geometry.dispose();
    if(node.material){
      const mats=Array.isArray(node.material)?node.material:[node.material];
      for(const m of mats) if(m && m.userData && m.userData.own) m.dispose();
    }
  });
}
const roomGroup   = new THREE.Group();  // walls, floor, ceiling, framing lines
const objectGroup = new THREE.Group();  // placed products
const helperGroup = new THREE.Group();  // grids
const holdsGroup  = new THREE.Group();  // instanced climbing holds (a few draw calls total)
const guideGroup  = new THREE.Group();  // temporary wizard previews and wall labels
scene.add(roomGroup, objectGroup, helperGroup, holdsGroup, guideGroup);
const GUIDE_TEX={};
let guideMode=null, guideUntil=0;
let HOLD_GEOMS=null;
function holdGeoms(key){
  const style=(state.settings.holdStyle)||'classic';
  if(style!=='classic') return styleGeoms(style)[key]||[];
  if(!HOLD_GEOMS){
    HOLD_GEOMS={};
    for(const k of ['hold_jug','hold_pinch','hold_edge']){
      const tpl=MODEL_TPL[k]; if(!tpl) continue;
      const list=[]; tpl.traverse(m=>{ if(m.isMesh) list.push(m.geometry); });
      HOLD_GEOMS[k]=list;
    }
  }
  return HOLD_GEOMS[key]||[];
}
const HOLD_INST_MAT=new THREE.MeshStandardMaterial({color:0xffffff, roughness:.6});
const HOLD_PLUG_MAT=new THREE.MeshStandardMaterial({color:0x262626, roughness:.55});
let PLUG_GEOM=null;
function plugGeom(){
  if(!PLUG_GEOM){
    PLUG_GEOM=new THREE.CylinderGeometry(0.42, 0.42, 1, 10);
    PLUG_GEOM.rotateX(Math.PI/2);          // axis = z (out from the wall)
    PLUG_GEOM.translate(0,0,0.5);          // base at z=0, unit height
    PLUG_GEOM.scale(1/39.3701, 1/39.3701, 1/39.3701);
  }
  return PLUG_GEOM;
}
// plug height comes from the real geometry top, so the socket always pierces the surface
function holdTopInches(style, key){
  const geoms=styleGeoms(style)[key];
  if(!geoms || !geoms.length) return 1.5;
  const g=geoms[0];
  if(!g.boundingBox) g.computeBoundingBox();
  return g.boundingBox.max.z*39.3701;
}
/* Simplified procedural hold shapes (low-poly stand-ins for the complex real geometry) */
function pseudoNoise(x,y,z,seed){
  const s=Math.sin(x*12.9898 + y*78.233 + z*37.719 + seed*13.7)*43758.5453;
  return s-Math.floor(s);
}
function weldGeom(g){
  // merge duplicated vertices so normals shade smoothly instead of per-facet
  const p=g.attributes.position, map=new Map(), pos=[], idx=[];
  for(let i=0;i<p.count;i++){
    const k=Math.round(p.getX(i)*1e5)+'_'+Math.round(p.getY(i)*1e5)+'_'+Math.round(p.getZ(i)*1e5);
    let j=map.get(k);
    if(j===undefined){ j=pos.length/3; map.set(k,j); pos.push(p.getX(i),p.getY(i),p.getZ(i)); }
    idx.push(j);
  }
  const ng=new THREE.BufferGeometry();
  ng.setAttribute('position', new THREE.Float32BufferAttribute(pos,3));
  ng.setIndex(idx);
  return ng;
}
function smoothGeom(g, iters){
  // Laplacian smoothing: pull each vertex toward the average of its neighbors — rounds off spikes
  const p=g.attributes.position, nv=p.count, ia=g.index.array;
  const adj=Array.from({length:nv},()=>new Set());
  for(let t=0;t<ia.length;t+=3){
    const a=ia[t], b=ia[t+1], c=ia[t+2];
    adj[a].add(b); adj[a].add(c);
    adj[b].add(a); adj[b].add(c);
    adj[c].add(a); adj[c].add(b);
  }
  for(let it=0; it<iters; it++){
    const nx=new Float32Array(nv*3);
    for(let i=0;i<nv;i++){
      let sx=0, sy=0, sz=0, cnt=0;
      for(const j of adj[i]){ sx+=p.getX(j); sy+=p.getY(j); sz+=p.getZ(j); cnt++; }
      nx[i*3]  =p.getX(i)*0.42 + (sx/cnt)*0.58;
      nx[i*3+1]=p.getY(i)*0.42 + (sy/cnt)*0.58;
      nx[i*3+2]=p.getZ(i)*0.42 + (sz/cnt)*0.58;
    }
    for(let i=0;i<nv;i++) p.setXYZ(i, nx[i*3], nx[i*3+1], nx[i*3+2]);
  }
}
function blobGeom(seed,sx,sy,sz,amp,detail,smoothIters){
  let g=new THREE.IcosahedronGeometry(0.5, detail);
  const p=g.attributes.position;
  for(let i=0;i<p.count;i++){
    let x=p.getX(i), y=p.getY(i), z=p.getZ(i);
    const key=[Math.round(x*1e4), Math.round(y*1e4), Math.round(z*1e4)];
    const nz=pseudoNoise(key[0],key[1],key[2],seed);
    const r=1 + amp*(nz-0.5)*2;
    x*=r; y*=r; z*=r;
    if(z<0) z*=0.12;                    // flatten the mounting side
    p.setXYZ(i,x,y,z);
  }
  g=weldGeom(g);                        // indexed => smooth shading + adjacency for smoothing
  smoothGeom(g, smoothIters===undefined?2:smoothIters);
  const q=g.attributes.position;        // re-flatten the base after smoothing
  for(let i=0;i<q.count;i++){ const z=q.getZ(i); if(z<0) q.setZ(i, z*0.15); }
  g.scale(sx, sy, sz*1.8);
  g.computeBoundingBox();
  g.translate(0,0,-g.boundingBox.min.z);
  g.computeVertexNormals();
  g.scale(1/39.3701, 1/39.3701, 1/39.3701);   // instancing matrices carry the inch scale
  g.computeBoundingBox();
  return g;
}
function leafGeom(len,wid,thick){
  const s=new THREE.Shape();
  s.moveTo(-len/2, 0);
  s.quadraticCurveTo(0,  wid*0.72,  len/2, 0);
  s.quadraticCurveTo(0, -wid*0.72, -len/2, 0);
  const g=new THREE.ExtrudeGeometry(s, {depth:thick*0.45, curveSegments:8, steps:1,
    bevelEnabled:true, bevelThickness:thick*0.55, bevelSize:wid*0.16, bevelSegments:2});
  g.computeBoundingBox();
  g.translate(0,0,-g.boundingBox.min.z);
  g.computeBoundingBox();
  g.scale(1, 1, thick/g.boundingBox.max.z);   // extrude bevels both faces — normalize to `thick`
  g.computeVertexNormals();
  g.scale(1/39.3701, 1/39.3701, 1/39.3701);
  g.computeBoundingBox();
  return g;
}
const PROC_GEOMS={};
function styleGeoms(style){
  if(PROC_GEOMS[style]) return PROC_GEOMS[style];
  let g;
  if(style==='rock') g={
    hold_jug:[blobGeom(11, 3.9,3.4,2.0, .26, 2, 2)],
    hold_pinch:[blobGeom(27, 4.8,2.0,1.6, .23, 2, 2)],
    hold_edge:[blobGeom(53, 4.1,2.7,1.2, .24, 2, 2)]};
  else if(style==='wood') g={
    hold_jug:[blobGeom(71, 3.6,3.3,1.9, .03, 3, 1)],
    hold_pinch:[blobGeom(72, 4.7,1.8,1.5, .025, 3, 1)],
    hold_edge:[blobGeom(73, 4.0,2.5,1.2, .03, 3, 1)]};
  else g={   // leaf: same shape, sizes kept close
    hold_jug:[leafGeom(4.4,2.25,1.35)],
    hold_pinch:[leafGeom(4.15,2.1,1.25)],
    hold_edge:[leafGeom(3.9,2.0,1.15)]};
  PROC_GEOMS[style]=g;
  return g;
}
function rebuildHolds(){
  for(const im of [...holdsGroup.children]) if(im.dispose) im.dispose();
  holdsGroup.clear();
  if(!MODEL_TPL.hold_jug) return;
  const panels=state.objects.filter(o=>CATALOG[o.type] && CATALOG[o.type].kind==='panel');
  if(!panels.length){ holdsGroup.visible=holdsOn(); return; }
  const buckets={hold_jug:[], hold_pinch:[], hold_edge:[]};
  const _ht=holdTargets();
  const dummy=new THREE.Object3D();
  const scaleM=new THREE.Matrix4().makeScale(39.3701,39.3701,39.3701);
  for(const o of panels){
    orientMesh(dummy,o); dummy.updateMatrix();
    for(const s of holdSlots(o, _ht.byId[o.id])){
      const m=new THREE.Matrix4().makeTranslation(s.x,s.y,s.z)
        .multiply(new THREE.Matrix4().makeRotationZ(s.rz))
        .multiply(scaleM);
      m.premultiply(dummy.matrix);
      buckets[s.key].push({m, color:s.color});
    }
  }
  const style=state.settings.holdStyle||'classic';
  const plugs=[];
  for(const key of Object.keys(buckets)){
    const list=buckets[key]; if(!list.length) continue;
    for(const geom of holdGeoms(key)){
      const im=new THREE.InstancedMesh(geom, HOLD_INST_MAT, list.length);
      for(let i=0;i<list.length;i++){ im.setMatrixAt(i, list[i].m); im.setColorAt(i, list[i].color); }
      im.instanceMatrix.needsUpdate=true;
      if(im.instanceColor) im.instanceColor.needsUpdate=true;
      holdsGroup.add(im);
    }
    // bolt-hole plugs (classic models have real holes; procedural shapes get a dark plug)
    if(style!=='classic'){
      const h=holdTopInches(style,key)+0.1;      // measured from geometry: always pierces the surface
      const pr = style==='leaf' ? 1.35 : 1;      // the leaf product's center hole is prominent
      for(const it of list){
        const pm=it.m.clone().multiply(new THREE.Matrix4().makeScale(pr,pr,h));
        plugs.push(pm);
      }
    }
  }
  if(plugs.length){
    const pim=new THREE.InstancedMesh(plugGeom(), HOLD_PLUG_MAT, plugs.length);
    for(let i=0;i<plugs.length;i++) pim.setMatrixAt(i, plugs[i]);
    pim.instanceMatrix.needsUpdate=true;
    holdsGroup.add(pim);
  }
  holdsGroup.visible=holdsOn();
}

const MAT = {
  floor: new THREE.MeshStandardMaterial({color:0xcbb490, roughness:.9}),
  base:  new THREE.MeshStandardMaterial({color:0xf3f3ef, roughness:.85}),
  ceil:  new THREE.MeshStandardMaterial({color:0xf7f7f4, roughness:.95, transparent:true, opacity:.35,
                                         side:THREE.DoubleSide, depthWrite:false}),
  birchM: new THREE.MeshStandardMaterial({color:0xE8D5B0, roughness:.72}),
  birchEdgeM: new THREE.MeshStandardMaterial({color:0xC9AE7F, roughness:.8}),
  steelM: new THREE.MeshStandardMaterial({color:0x565b63, roughness:.45, metalness:.55}),
  lumberM: new THREE.MeshStandardMaterial({color:0xd4b183, roughness:.85}),
  outline: new THREE.LineBasicMaterial({color:0x8f7a55, transparent:true, opacity:.85}),
  outlineGray: new THREE.LineBasicMaterial({color:0xA6ABB0, transparent:true, opacity:.9}),
  gridLine: new THREE.LineBasicMaterial({color:0xb9c4b2, transparent:true, opacity:.35}),
  framingLine: new THREE.LineDashedMaterial({color:0x8fae8e, dashSize:3, gapSize:3, transparent:true, opacity:.9}),
  ghost: new THREE.MeshStandardMaterial({color:0x3f9142, transparent:true, opacity:.5}),
  ghostBad: new THREE.MeshStandardMaterial({color:0xc0392b, transparent:true, opacity:.5}),
  pick:  new THREE.MeshBasicMaterial({transparent:true, opacity:0, depthWrite:false, colorWrite:false,
                                      side:THREE.DoubleSide}),
};
const COLOR_MATS={};
function colorMat(hex, rough, metal){
  const key=hex+'|'+rough+'|'+(metal||0);
  if(!COLOR_MATS[key]) COLOR_MATS[key]=new THREE.MeshStandardMaterial({color:new THREE.Color(hex), roughness:rough, metalness:metal||0});
  return COLOR_MATS[key];
}
function panelMat(hex){
  // vertical surfaces catch less light — lift painted panels so white reads white next to gray mats
  const key='panel|'+hex;
  if(!COLOR_MATS[key]){
    const c=new THREE.Color(hex);
    COLOR_MATS[key]=new THREE.MeshStandardMaterial({
      color:c, roughness:.85,
      emissive:c.clone().multiplyScalar(0.22),
    });
  }
  return COLOR_MATS[key];
}
function resolveHex(val, idx){ return val==='rainbow' ? RAINBOW[(idx||0)%RAINBOW.length] : val; }
const WALL_T = 4;                 // rendered wall thickness
let wallMeshes = [], pickPlanes = [], ceilPick = null, ceilMesh = null;

/* -------- embedded GLB product models -------- */
function parseGLB(buf){
  const dv=new DataView(buf);
  if(dv.getUint32(0,true)!==0x46546C67) throw new Error('not a GLB file');
  const total=dv.getUint32(8,true);
  let off=12, json=null, bin=null;
  while(off<total){
    const clen=dv.getUint32(off,true), ctype=dv.getUint32(off+4,true);
    const chunk=buf.slice(off+8, off+8+clen);
    if(ctype===0x4E4F534A) json=JSON.parse(new TextDecoder().decode(chunk));
    else if(ctype===0x004E4942) bin=chunk;
    off+=8+clen;
  }
  const COMP={5120:Int8Array,5121:Uint8Array,5122:Int16Array,5123:Uint16Array,5125:Uint32Array,5126:Float32Array};
  const NCOMP={SCALAR:1,VEC2:2,VEC3:3,VEC4:4};
  function acc(i){
    const a=json.accessors[i], bv=json.bufferViews[a.bufferView];
    const T=COMP[a.componentType], n=NCOMP[a.type];
    const start=(bv.byteOffset||0)+(a.byteOffset||0);
    const stride=bv.byteStride;
    if(!stride || stride===n*T.BYTES_PER_ELEMENT)
      return new T(bin, start, a.count*n);
    const out=new T(a.count*n);                    // de-interleave strided data
    for(let k=0;k<a.count;k++){
      const src=new T(bin, start+k*stride, n);
      out.set(src, k*n);
    }
    return out;
  }
  const matCache={};
  function material(mi){
    if(mi==null) return new THREE.MeshStandardMaterial({color:0x999999, roughness:.8});
    if(matCache[mi]) return matCache[mi];
    const pbr=(json.materials[mi]||{}).pbrMetallicRoughness||{};
    const c=pbr.baseColorFactor||[.8,.8,.8,1];
    matCache[mi]=new THREE.MeshStandardMaterial({
      color:new THREE.Color(c[0],c[1],c[2]),
      metalness:Math.min(pbr.metallicFactor??0,.4), roughness:pbr.roughnessFactor??.75});
    return matCache[mi];
  }
  const root=new THREE.Group();
  function addNode(ni,parent){
    const n=json.nodes[ni], obj=new THREE.Group();
    if(n.matrix) obj.applyMatrix4(new THREE.Matrix4().fromArray(n.matrix));
    else{
      if(n.translation) obj.position.fromArray(n.translation);
      if(n.rotation) obj.quaternion.fromArray(n.rotation);
      if(n.scale) obj.scale.fromArray(n.scale);
    }
    if(n.mesh!=null){
      for(const p of json.meshes[n.mesh].primitives){
        const g=new THREE.BufferGeometry();
        g.setAttribute('position', new THREE.BufferAttribute(acc(p.attributes.POSITION),3));
        if(p.attributes.NORMAL!=null) g.setAttribute('normal', new THREE.BufferAttribute(acc(p.attributes.NORMAL),3));
        else g.computeVertexNormals();
        if(p.indices!=null) g.setIndex(new THREE.BufferAttribute(acc(p.indices),1));
        obj.add(new THREE.Mesh(g, material(p.material)));
      }
    }
    (n.children||[]).forEach(ci=>addNode(ci,obj));
    parent.add(obj);
  }
  (json.scenes[json.scene||0].nodes||[]).forEach(ni=>addNode(ni,root));
  return root;
}
const MODEL_TPL={};
function initModels(){
  const M2IN=39.3701;
  const rotZ={};                       // models already match catalog orientation
  const zShift={mat:2.523};            // mat is modeled centered; lift so its bottom sits at z=0
  for(const [type,b64] of Object.entries(window.GLB_B64||{})){
    try{
      const bytes=Uint8Array.from(atob(b64), ch=>ch.charCodeAt(0));
      const g=parseGLB(bytes.buffer);
      g.scale.setScalar(M2IN);
      if(rotZ[type]) g.rotation.z=rotZ[type];
      if(zShift[type]) g.position.z=zShift[type];
      const wrap=new THREE.Group(); wrap.add(g);
      MODEL_TPL[type]=wrap;
    }catch(err){
      if(window.__banner) window.__banner('Model load failed ('+type+'): '+err.message+' — using simple shape');
    }
  }
}
initModels();
const holdsOn = ()=> $('tglHolds').classList.contains('on');
function mulberry32(a){return function(){a|=0;a=a+0x6D2B79F5|0;let t=Math.imul(a^a>>>15,1|a);t=t+Math.imul(t^t>>>7,61|t)^t;return((t^t>>>14)>>>0)/4294967296;}}
const HOLD_KEYS={j:'hold_jug',p:'hold_pinch',e:'hold_edge'};
// darker outline so panel seams read clearly (holds render separately, instanced)
function addPanelDressing(g, o, c){
  const w=c.w, h=c.h, z=c.d+0.06;
  const pts=[new THREE.Vector3(-w/2,-h/2,z), new THREE.Vector3(w/2,-h/2,z),
             new THREE.Vector3(w/2,h/2,z),  new THREE.Vector3(-w/2,h/2,z)];
  const loopGeom=new THREE.BufferGeometry().setFromPoints(pts);
  loopGeom.userData.own=true;
  const C=state.settings.colors||DEFAULT_COLORS;
  g.add(new THREE.LineLoop(loopGeom, C.panel==='birch' ? MAT.outline : MAT.outlineGray));
}
// hold layout for a panel: 40% jugs, 30% pinches, 30% edges; seeded by id so it never reshuffles
function holdSlots(o, countOverride){
  const c=CATALOG[o.type], count=(countOverride!=null?countOverride:(c.holds||10)), w=c.w, h=c.h;
  const rng=mulberry32((o.id*2654435761)>>>0);
  const nj=Math.round(count*0.4), np=Math.round(count*0.3), ne=count-nj-np;
  const types=[...Array(nj).fill('j'), ...Array(np).fill('p'), ...Array(ne).fill('e')];
  for(let i=types.length-1;i>0;i--){ const k=Math.floor(rng()*(i+1)); [types[i],types[k]]=[types[k],types[i]]; }
  // panels have a fixed grid of threaded inserts: 6 across × 3 per 23" of height
  const cols=6, rows=Math.max(1, Math.round(h/23)*3);
  const cells=[];
  for(let ci=0;ci<cols;ci++) for(let ri=0;ri<rows;ri++) cells.push([ci,ri]);
  for(let i=cells.length-1;i>0;i--){ const k=Math.floor(rng()*(i+1)); [cells[i],cells[k]]=[cells[k],cells[i]]; }
  const cw=w/cols, chh=h/rows;
  const holdC=(state.settings.colors||DEFAULT_COLORS).hold;
  const out=[];
  for(let i=0;i<count && i<cells.length;i++){
    const [ci,ri]=cells[i];
    out.push({
      key:HOLD_KEYS[types[i]],
      x:-w/2 + (ci+0.5)*cw,               // exactly on the insert — holds only mount on the grid
      y:-h/2 + (ri+0.5)*chh,
      z:c.d+0.02,
      rz:rng()*Math.PI*2,                  // holds can spin around their bolt
      color:new THREE.Color(COLOR_MIXES[holdC] ? COLOR_MIXES[holdC][Math.floor(rng()*COLOR_MIXES[holdC].length)] : holdC),
    });
  }
  return out;
}

/* Hold density: default 1 set of 10 per half-panel-equivalent, adjusted in whole
   sets via state.settings.holdAdj. Distributed proportionally across panels and
   capped at each panel's insert grid (6 across x 3 per 23" of height). */
function holdCap(o){ const c=CATALOG[o.type]; return 6*Math.max(1, Math.round(c.h/23)*3); }
function holdTargets(){
  const panels=state.objects.filter(o=>CATALOG[o.type] && CATALOG[o.type].kind==='panel');
  const byId={};
  if(!panels.length) return {byId, total:0, def:0, cap:0};
  const def=panels.reduce((s,o)=>s+(CATALOG[o.type].holds||0),0);
  const capTotal=panels.reduce((s,o)=>s+holdCap(o),0);
  const total=Math.max(0, Math.min(capTotal, def+(state.settings.holdAdj||0)*10));
  const shares=panels.map(o=>({o, exact: def ? total*(CATALOG[o.type].holds||0)/def : 0}));
  let assigned=0;
  for(const s of shares){ s.n=Math.min(holdCap(s.o), Math.floor(s.exact)); assigned+=s.n; }
  let rem=total-assigned, guard=0;
  shares.sort((a,b)=>(b.exact-b.n)-(a.exact-a.n));
  while(rem>0 && guard++<200){
    let moved=false;
    for(const s of shares){ if(rem<=0) break; if(s.n<holdCap(s.o)){ s.n++; rem--; moved=true; } }
    if(!moved) break;
  }
  let tot=0; for(const s of shares){ byId[s.o.id]=s.n; tot+=s.n; }
  return {byId, total:tot, def, cap:capTotal};
}

function surfacePoint(surf, u, v, standoff){
  // convert surface coords -> world position (center of an object standoff/2 off the surface)
  const S = state.settings;
  if(surf.k==='wall'){
    const w = walls()[surf.i];
    return new THREE.Vector3(
      w.a.x + w.dir.x*u + w.n.x*(standoff),
      v,
      w.a.z + w.dir.z*u + w.n.z*(standoff));
  }
  if(surf.k==='floor') return new THREE.Vector3(u, standoff, v);   // floor: u=x, v=z
  return new THREE.Vector3(u, S.ceil - standoff, v);   // ceiling: u=x, v=z
}
function wallQuat(w){ // rotate a +Z-facing box to face wall normal
  const q=new THREE.Quaternion();
  q.setFromUnitVectors(new THREE.Vector3(0,0,1), new THREE.Vector3(w.n.x,0,w.n.z));
  return q;
}

function buildRoom(){
  disposeGroup(roomGroup);
  roomGroup.clear(); wallMeshes=[]; pickPlanes=[];
  const S=state.settings, ws=walls(), bb=roomBBox();
  const C=S.colors||DEFAULT_COLORS;
  MAT.floor.color.set(C.floor);
  MAT.ceil.color.set(C.wall);
  MAT.base.color.set(C.wall);         // baseboard matches the walls
  MAT.lumberM.color.set(C.wall);      // stringers match the wall/ceiling color
  // floor — extend 3' beyond the room walls so mats aren't cramped at the edges
  const FLOOR_PAD=36;
  const floor=new THREE.Mesh(
    new THREE.BoxGeometry(bb.maxx-bb.minx+WALL_T*2+FLOOR_PAD*2, 1, bb.maxz-bb.minz+WALL_T*2+FLOOR_PAD*2), MAT.floor);
  floor.geometry.userData.own=true;
  floor.position.set((bb.minx+bb.maxx)/2, -0.5, (bb.minz+bb.maxz)/2);
  floor.receiveShadow=true; roomGroup.add(floor);
  sun.position.set((bb.minx+bb.maxx)/2+140, S.ceil+180, (bb.minz+bb.maxz)/2+90);
  sun.target.position.set((bb.minx+bb.maxx)/2, 0, (bb.minz+bb.maxz)/2);
  roomGroup.add(sun.target);

  for(const w of ws){
    const mat=new THREE.MeshStandardMaterial({color:new THREE.Color(C.wall), roughness:.95, transparent:true});
    mat.userData.own=true;
    const mesh=new THREE.Mesh(new THREE.BoxGeometry(w.len, S.ceil, WALL_T), mat);
    mesh.geometry.userData.own=true;
    const mid={x:(w.a.x+w.b.x)/2, z:(w.a.z+w.b.z)/2};
    mesh.position.set(mid.x - w.n.x*WALL_T/2, S.ceil/2, mid.z - w.n.z*WALL_T/2);
    mesh.quaternion.copy(wallQuat(w));
    mesh.receiveShadow=true;
    mesh.userData.wall=w.i;
    roomGroup.add(mesh); wallMeshes.push(mesh);
    // baseboard strip on interior face
    if(S.base>0){
      const bbMesh=new THREE.Mesh(new THREE.BoxGeometry(w.len, S.base, 0.6), MAT.base);
      bbMesh.geometry.userData.own=true;
      bbMesh.position.set(mid.x + w.n.x*0.31, S.base/2, mid.z + w.n.z*0.31);
      bbMesh.quaternion.copy(wallQuat(w));
      roomGroup.add(bbMesh);
      mesh.userData.baseMesh = bbMesh;
    }
    // invisible pick plane on the interior face
    const pick=new THREE.Mesh(new THREE.PlaneGeometry(w.len, S.ceil), MAT.pick);
    pick.geometry.userData.own=true;
    pick.position.set(mid.x + w.n.x*0.05, S.ceil/2, mid.z + w.n.z*0.05);
    pick.quaternion.copy(wallQuat(w));
    pick.userData.surf={k:'wall', i:w.i};
    roomGroup.add(pick); pickPlanes.push(pick);
  }
  // ceiling (visual + pick)
  ceilMesh=new THREE.Mesh(new THREE.PlaneGeometry(bb.maxx-bb.minx, bb.maxz-bb.minz), MAT.ceil);
  ceilMesh.geometry.userData.own=true;
  ceilMesh.rotation.x=Math.PI/2;
  ceilMesh.position.set((bb.minx+bb.maxx)/2, S.ceil, (bb.minz+bb.maxz)/2);
  roomGroup.add(ceilMesh);
  ceilPick=new THREE.Mesh(new THREE.PlaneGeometry(bb.maxx-bb.minx, bb.maxz-bb.minz), MAT.pick);
  ceilPick.geometry.userData.own=true;
  ceilPick.rotation.x=Math.PI/2;
  ceilPick.position.copy(ceilMesh.position).y -= 0.05;
  ceilPick.userData.surf={k:'ceil'};
  roomGroup.add(ceilPick); pickPlanes.push(ceilPick);
  // floor pick plane (for mats)
  const floorPick=new THREE.Mesh(new THREE.PlaneGeometry(bb.maxx-bb.minx+WALL_T*2, bb.maxz-bb.minz+WALL_T*2), MAT.pick);
  floorPick.geometry.userData.own=true;
  floorPick.rotation.x=-Math.PI/2;
  floorPick.position.set((bb.minx+bb.maxx)/2, 0.06, (bb.minz+bb.maxz)/2);
  floorPick.userData.surf={k:'floor'};
  roomGroup.add(floorPick); pickPlanes.push(floorPick);

  buildFramingLines();
  buildGrids();
  buildCamButtons();
  refreshGuides();
}

/* -------- framing centerlines (studs & joists) -------- */
let framingGroup=null;
function joistBasis(){
  const ws=walls(), S=state.settings;
  const w = ws[Math.min(S.joistWall, ws.length-1)] || {dir:{x:1,z:0}};
  const d={x:w.dir.x, z:w.dir.z};            // joists run along d
  const n={x:-d.z, z:d.x};                   // spacing measured along n
  const bb=roomBBox();
  const corners=[[bb.minx,bb.minz],[bb.maxx,bb.minz],[bb.minx,bb.maxz],[bb.maxx,bb.maxz]];
  let lo=1e9, hi=-1e9, loD=1e9, hiD=-1e9;
  for(const [x,z] of corners){
    const pn=x*n.x+z*n.z, pd=x*d.x+z*d.z;
    lo=Math.min(lo,pn); hi=Math.max(hi,pn); loD=Math.min(loD,pd); hiD=Math.max(hiD,pd);
  }
  return {d,n,lo,hi,loD,hiD, base: lo + S.joistOff, sp:S.joistSp};
}
function nearestLineDist(coord, base, sp){
  const m = ((coord-base)%sp+sp)%sp;
  return Math.min(m, sp-m);
}
function buildFramingLines(){
  if(framingGroup){ disposeGroup(framingGroup); roomGroup.remove(framingGroup); }
  framingGroup=new THREE.Group();
  const S=state.settings, ws=walls();
  const mat=MAT.framingLine;
  // wall studs (vertical)
  for(const w of ws){
    const pts=[];
    for(let u=S.studOff; u<=w.len; u+=S.studSp){
      if(u<0) continue;
      const p1=surfacePoint({k:'wall',i:w.i}, u, 0, 0.15);
      const p2=surfacePoint({k:'wall',i:w.i}, u, S.ceil, 0.15);
      pts.push(p1,p2);
    }
    if(pts.length){
      const g=new THREE.BufferGeometry().setFromPoints(pts);
      g.userData.own=true;
      const ln=new THREE.LineSegments(g, mat); ln.computeLineDistances();
      framingGroup.add(ln);
    }
  }
  // ceiling joists
  const jb=joistBasis(), pts=[];
  for(let c=jb.base; c<=jb.hi; c+=jb.sp){
    if(c<jb.lo) continue;
    pts.push(new THREE.Vector3(jb.n.x*c+jb.d.x*jb.loD, S.ceil-0.15, jb.n.z*c+jb.d.z*jb.loD),
             new THREE.Vector3(jb.n.x*c+jb.d.x*jb.hiD, S.ceil-0.15, jb.n.z*c+jb.d.z*jb.hiD));
  }
  if(pts.length){
    const g=new THREE.BufferGeometry().setFromPoints(pts);
    g.userData.own=true;
    const ln=new THREE.LineSegments(g, mat); ln.computeLineDistances();
    framingGroup.add(ln);
  }
  framingGroup.visible = $('tglStuds').classList.contains('on');
  roomGroup.add(framingGroup);
}

/* -------- 1" grids -------- */
function buildGrids(){
  disposeGroup(helperGroup);
  helperGroup.clear();
  const S=state.settings, ws=walls();
  const mat=MAT.gridLine;
  function gridFor(surf, W, H){
    const pts=[];
    for(let u=0;u<=W;u+=1){ pts.push(surfacePoint(surf,u,0,0.1), surfacePoint(surf,u,H,0.1)); }
    for(let v=0;v<=H;v+=1){ pts.push(surfacePoint(surf,0,v,0.1), surfacePoint(surf,W,v,0.1)); }
    const g=new THREE.BufferGeometry().setFromPoints(pts);
    g.userData.own=true;
    helperGroup.add(new THREE.LineSegments(g,mat));
  }
  for(const w of ws) gridFor({k:'wall',i:w.i}, Math.floor(w.len), S.ceil);
  const bb=roomBBox(), pts=[];
  for(let x=Math.ceil(bb.minx); x<=bb.maxx; x+=1)
    pts.push(new THREE.Vector3(x,S.ceil-0.1,bb.minz), new THREE.Vector3(x,S.ceil-0.1,bb.maxz));
  for(let z=Math.ceil(bb.minz); z<=bb.maxz; z+=1)
    pts.push(new THREE.Vector3(bb.minx,S.ceil-0.1,z), new THREE.Vector3(bb.maxx,S.ceil-0.1,z));
  const g=new THREE.BufferGeometry().setFromPoints(pts);
  g.userData.own=true;
  helperGroup.add(new THREE.LineSegments(g,mat));
  helperGroup.visible = $('tglGrid').classList.contains('on');
}

/* -------- product meshes -------- */
function styledClone(tpl, ghost, badGhost, bad, override){
  const inst=tpl.clone(true);
  inst.traverse(m=>{
    if(m.isMesh){
      m.castShadow=!ghost;
      if(ghost) m.material = badGhost?MAT.ghostBad:MAT.ghost;
      else{
        if(override) m.material=override;
        if(bad){ m.material=m.material.clone(); m.material.userData.own=true; m.material.color.lerp(new THREE.Color(0xc0392b),.45); }
      }
    }
  });
  return inst;
}
// single monkey bar, long axis along local X, mounting face at z=0
function barUnit(ghost, badGhost, bad, tint, override){
  if(MODEL_TPL.monkey_bar) return styledClone(MODEL_TPL.monkey_bar, ghost, badGhost, bad, override);
  const grp=new THREE.Group();
  const mat = ghost? (badGhost?MAT.ghostBad:MAT.ghost) : tint(MAT.steelM);
  const L=CATALOG.monkey_bar.w, stand=CATALOG.monkey_bar.d;
  const foot=new THREE.BoxGeometry(2.6, 3.25, .5); foot.userData.own=true;
  const f1=new THREE.Mesh(foot,mat), f2=new THREE.Mesh(foot,mat);
  f1.position.set(-HOLE_SPACING/2, 0, .25); f2.position.set(HOLE_SPACING/2, 0, .25);
  const postG=new THREE.CylinderGeometry(.5,.5,stand-1,12); postG.userData.own=true;
  const p1=new THREE.Mesh(postG,mat), p2=new THREE.Mesh(postG,mat);
  p1.rotation.x=Math.PI/2; p2.rotation.x=Math.PI/2;
  p1.position.set(-HOLE_SPACING/2+1.2, 0, (stand-1)/2+.3);
  p2.position.set( HOLE_SPACING/2-1.2, 0, (stand-1)/2+.3);
  const barG=new THREE.CylinderGeometry(.5,.5,L-2,14); barG.userData.own=true;
  const bar=new THREE.Mesh(barG,mat);
  bar.rotation.z=Math.PI/2; bar.position.set(0,0,stand-.5);
  [f1,f2,p1,p2,bar].forEach(m=>{m.castShadow=!ghost; grp.add(m);});
  return grp;
}
// run layout constants
const RAIL_MARGIN=8, PER_BAR_LEN=22;
/* Rotation step: mats and monkey-bar runs rotate in 45° increments (to align with
   diagonal walls). Panels, single monkey bars, lumber, and wall ladders use 90°. */
function rotStep(o){
  const type = (typeof o==='string') ? o : (o && o.type);
  return (type==='mat' || type==='bar_run' || type==='barrun') ? 45 : 90;
}
function rotWrap(rot, o){
  const step=rotStep(o);
  const mod = step===45 ? 360 : 180;
  return ((Math.round(rot) % mod) + mod) % mod;
}
function runLayout(o){
  const sp=o.spacing||12, n=Math.max(2,o.count||2), L=(n-1)*sp;
  return {sp, n, L, railLen:L+RAIL_MARGIN, hasStringers:o.stringers && o.stringers!=='none'};
}
function makeMesh(o, ghost){
  const c=CATALOG[o.type], dm=dims(o);
  const g=new THREE.Group();
  const bad = !ghost && o._unsupported;
  const tint = m => { if(bad){ m=m.clone(); m.userData.own=true; m.color.lerp(new THREE.Color(0xc0392b), .45);} return m; };
  const tpl = MODEL_TPL[o.type];
  let fromModel=false;
  if(c.kind==='run'){
    // whole run built along local X (run axis); bars perpendicular (along local Y)
    const rl=runLayout(o);
    const zBar = rl.hasStringers ? 1.5 : 0;
    const lm = ghost? (o._bad?MAT.ghostBad:MAT.ghost) : tint(MAT.lumberM);
    const barC=(state.settings.colors||DEFAULT_COLORS).bar;
    for(let i=0;i<rl.n;i++){
      const b=barUnit(ghost, o._bad, bad, tint, ghost?null:colorMat(resolveHex(barC,i), .5, .4));
      b.rotation.z=Math.PI/2;
      b.position.set(-rl.L/2 + i*rl.sp, 0, zBar);
      g.add(b);
    }
    if(o.stringers==='rails'){
      for(const s of [-HOLE_SPACING/2, HOLE_SPACING/2]){
        const r=new THREE.Mesh(new THREE.BoxGeometry(rl.railLen, 3.5, 1.5), lm); r.geometry.userData.own=true;
        r.position.set(0, s, .75); r.castShadow=!ghost; g.add(r);
      }
    } else if(o.stringers==='per_bar'){
      for(let i=0;i<rl.n;i++){
        const r=new THREE.Mesh(new THREE.BoxGeometry(3.5, PER_BAR_LEN, 1.5), lm); r.geometry.userData.own=true;
        r.position.set(-rl.L/2 + i*rl.sp, 0, .75); r.castShadow=!ghost; g.add(r);
      }
    }
  } else if(tpl){
    fromModel=true;
    const C=state.settings.colors||DEFAULT_COLORS;
    let override=null;
    if(!ghost){
      if(c.kind==='panel' && C.panel!=='birch') override=panelMat(C.panel);
      else if(c.kind==='mat') override=colorMat(C.mat, .88);
    }
    g.add(styledClone(tpl, ghost, o._bad, bad, override));
    if(c.kind==='panel' && !ghost) addPanelDressing(g, o, c);
  } else if(c.kind==='panel'){
    const body=new THREE.Mesh(new THREE.BoxGeometry(dm.w-.3, dm.h-.3, dm.d),
      ghost? (o._bad?MAT.ghostBad:MAT.ghost) : tint(MAT.birchM));
    const frame=new THREE.Mesh(new THREE.BoxGeometry(dm.w, dm.h, dm.d-.15),
      ghost? (o._bad?MAT.ghostBad:MAT.ghost) : tint(MAT.birchEdgeM));
    body.geometry.userData.own=true; frame.geometry.userData.own=true;
    body.position.z = .08;
    body.castShadow=frame.castShadow=!ghost;
    g.add(frame, body);
    if(!ghost) addPanelDressing(g, o, c);
  } else if(c.kind==='lumber'){
    const m=new THREE.Mesh(new THREE.BoxGeometry(dm.w, dm.h, dm.d),
      ghost? (o._bad?MAT.ghostBad:MAT.ghost) : tint(MAT.lumberM));
    m.geometry.userData.own=true;
    m.castShadow=!ghost; g.add(m);
  } else { // single monkey bar
    const barC=(state.settings.colors||DEFAULT_COLORS).bar;
    g.add(barUnit(ghost, o._bad, bad, tint, ghost?null:colorMat(resolveHex(barC,o.id), .5, .4)));
  }
  orientMesh(g, o);
  g.userData.id=o.id;
  return g;
}
// position + rotate a mesh group for its object — reused by the lightweight drag path
function orientMesh(g, o){
  const c=CATALOG[o.type], dm=dims(o);
  const fromModel = !!MODEL_TPL[o.type] && c.kind!=='run';
  const zoff = (fromModel || c.kind==='bar' || c.kind==='run') ? 0.05 : dm.d/2 + 0.05;
  g.rotation.set(0,0,0);
  const stand = mountStandoff(o);
  if(o.surf.k==='wall'){
    const w=walls()[o.surf.i];
    g.quaternion.copy(wallQuat(w));
    if(o.rot%180===90) g.rotateZ(Math.PI/2);
  } else if(o.surf.k==='floor'){
    g.rotation.x = -Math.PI/2;                    // +Z now points up from the floor
    g.rotateZ((o.rot||0) * Math.PI / 180);        // arbitrary in-plane rotation (mats can be at 45°)
  } else {
    g.rotation.x = Math.PI/2;                     // +Z now points down from ceiling
    g.rotateZ((o.rot||0) * Math.PI / 180);        // arbitrary in-plane rotation
  }
  g.position.copy(surfacePoint(o.surf, o.u, o.v, stand + zoff));
}
function updateMeshTransform(o){
  const m=meshById(o.id); if(m) orientMesh(m,o);
}
function updateHelpers(){
  for(const c of objectGroup.children){
    if(!c.userData.helper) continue;
    const m=meshById(c.userData.targetId);
    if(m){ m.updateWorldMatrix(true,true); c.box.setFromObject(m).expandByScalar(1.0); }
  }
}
// extra standoff when a monkey bar sits on a 2x4 stringer
function mountStandoff(o){
  if(o.type==='monkey_bar' && o._onStringer) return 1.5;
  return 0;
}
function rebuildObjects(){
  disposeGroup(objectGroup);
  objectGroup.clear();
  computeSupport();
  for(const o of state.objects) objectGroup.add(makeMesh(o,false));
  rebuildHolds();
  for(const id of selIds){
    const m=meshById(id);
    if(m){
      m.updateWorldMatrix(true,true);
      const b3=new THREE.Box3().setFromObject(m).expandByScalar(1.0);
      const h=new THREE.Box3Helper(b3, 0xFF8A00);
      h.geometry.userData.own=true; h.material.userData.own=true;
      h.material.depthTest=false;           // selection reads through everything
      h.renderOrder=999;
      h.userData.helper=true; h.userData.targetId=id;
      objectGroup.add(h);
    }
  }
}
const objById = id => state.objects.find(o=>o.id===id);
const meshById = id => objectGroup.children.find(m=>m.userData.id===id);

/* -------- camera -------- */
function frameRoom(){
  const bb=roomBBox(), S=state.settings;
  const cx=(bb.minx+bb.maxx)/2, cz=(bb.minz+bb.maxz)/2;
  let span=Math.max(bb.maxx-bb.minx, bb.maxz-bb.minz, S.ceil);
  if(camera.aspect<1) span*=Math.min(1.5, 1/(camera.aspect*0.95));
  camera.position.set(cx+span*.9, S.ceil*1.4, cz+span*1.1);
  controls.target.set(cx, S.ceil*.45, cz);
  controls.update();
}
function buildCamButtons(){
  const row=$('camRow'); row.innerHTML='';
  const ws=walls(), S=state.settings, bb=roomBBox();
  ws.forEach(w=>{
    const b=document.createElement('button');
    b.textContent='Wall '+String.fromCharCode(65+w.i);
    b.onclick=()=>{
      const mid={x:(w.a.x+w.b.x)/2, z:(w.a.z+w.b.z)/2};
      const d=Math.max(w.len, S.ceil)*1.15;
      camera.position.set(mid.x+w.n.x*d, S.ceil*.55, mid.z+w.n.z*d);
      controls.target.set(mid.x, S.ceil*.5, mid.z); controls.update();
    };
    row.appendChild(b);
  });
  const iso=document.createElement('button'); iso.textContent='Corner';
  iso.onclick=frameRoom; row.appendChild(iso);
  const top=document.createElement('button'); top.textContent='Top';
  top.onclick=()=>{
    const cx=(bb.minx+bb.maxx)/2, cz=(bb.minz+bb.maxz)/2;
    camera.position.set(cx, S.ceil*2.6, cz+1);
    controls.target.set(cx,0,cz); controls.update();
  };
  row.appendChild(top);
}
function guideBadge(text){
  if(GUIDE_TEX[text]) return GUIDE_TEX[text];
  const can=document.createElement('canvas');
  can.width=192; can.height=192;
  const cx=can.getContext('2d');
  cx.fillStyle='rgba(148,188,116,.96)';
  cx.beginPath(); cx.arc(96,96,72,0,Math.PI*2); cx.fill();
  cx.lineWidth=10; cx.strokeStyle='rgba(30,30,30,.92)'; cx.stroke();
  cx.fillStyle='#1E1E1E';
  cx.font='700 92px Poppins, sans-serif';
  cx.textAlign='center';
  cx.textBaseline='middle';
  cx.fillText(text,96,102);
  const tex=new THREE.CanvasTexture(can);
  GUIDE_TEX[text]=tex;
  return tex;
}
function clearGuides(){
  disposeGroup(guideGroup);
  guideGroup.clear();
}
function paintGuideMesh(root){
  root.traverse(node=>{
    if(!node.isMesh || !node.material) return;
    const mats=Array.isArray(node.material) ? node.material : [node.material];
    const clones=mats.map(mat=>{
      const copy=mat.clone();
      copy.userData.own=true;
      copy.transparent=true;
      copy.depthTest=false;
      copy.depthWrite=false;
      copy.opacity=Math.min(copy.opacity ?? 1, .72);
      if(copy.color) copy.color.set(0x3f9142);
      return copy;
    });
    node.material=Array.isArray(node.material) ? clones : clones[0];
    node.renderOrder=998;
  });
}
function addWallLetterGuides(){
  const S=state.settings;
  for(const w of walls()){
    // sprites always face the camera, so the letters read correctly from any angle
    const mat=new THREE.SpriteMaterial({map:guideBadge(String.fromCharCode(65+w.i)),
      transparent:true, depthTest:false, opacity:.95});
    mat.userData.own=true;
    const spr=new THREE.Sprite(mat);
    spr.scale.set(20,20,1);
    spr.renderOrder=999;
    spr.userData.wallIdx=w.i;          // clickable: jumps to this wall's row in the Room panel
    const mid={x:(w.a.x+w.b.x)/2, z:(w.a.z+w.b.z)/2};
    spr.position.set(mid.x + w.n.x*6, Math.max(S.base+18, Math.min(S.ceil-16, S.ceil*.6)), mid.z + w.n.z*6);
    guideGroup.add(spr);
  }
}
/* Overlay a pulsing green copy on objects already in the design; true if any matched. */
function guideExistingFlash(filter){
  const found=state.objects.filter(filter);
  for(const o of found){
    const g=makeMesh({...o, id:'guide-'+o.id}, true);
    paintGuideMesh(g);
    guideGroup.add(g);
  }
  return found.length>0;
}
function guideBarPreview(){
  // flash the bars already in the room; only draw a ghost run when there are none yet
  if(guideExistingFlash(o=>(o.type==='bar_run'||o.type==='monkey_bar') && o.surf && o.surf.k==='ceil')) return;
  const ws=walls(), w=ws[1] || ws[0];
  if(!w) return;
  const rules=ageRules(state.settings.age);
  const opt=((state.wallOpts||[])[w.i]||{});
  const off=(opt.p ? 18 : 12) + CATALOG.monkey_bar.w/2;
  const count=Math.max(2, Math.min(5, Math.floor((w.len-10)/rules.barSpacing)+1));
  const wAngle=Math.round(Math.atan2(w.dir.z, w.dir.x) * 180 / Math.PI);
  const guide=makeMesh({
    id:'guide-bars', type:'bar_run', surf:{k:'ceil'}, rot:wAngle,
    spacing:rules.barSpacing, count, stringers:'rails',
    u:w.a.x + w.dir.x*(w.len/2) + w.n.x*off,
    v:w.a.z + w.dir.z*(w.len/2) + w.n.z*off
  }, true);
  paintGuideMesh(guide);
  guideGroup.add(guide);
}
function guideLadderPreview(){
  // flash the ladder already in the room; only draw a ghost when there is none yet
  if(guideExistingFlash(o=>o.type==='bar_run' && o.surf && o.surf.k==='wall')) return;
  const ws=walls(), w=ws[2] || ws[0];
  if(!w) return;
  const S=state.settings, rules=ageRules(S.age);
  const autoPadH=((state.wallOpts||[]).some(o=>o&&o.p) || state.objects.some(o=>o.type==='mat')) ? CATALOG.mat.d : 0;
  const bottom=Math.max(S.base+6, rules.firstRung + autoPadH);
  const top=S.ceil-10;
  const sp=rules.ladderSpacing;
  const count=Math.max(2, Math.min(5, Math.floor((top-bottom)/sp)+1));
  const L=(count-1)*sp;
  const guide=makeMesh({
    id:'guide-ladder', type:'bar_run', surf:{k:'wall',i:w.i}, rot:90,
    spacing:sp, count, stringers:'rails',
    u:Math.min(w.len-11, Math.max(11, w.len*.72)),
    v:bottom + L/2
  }, true);
  paintGuideMesh(guide);
  guideGroup.add(guide);
}
/* Green wash over the wall the tutorial wants clicked, so the target is obvious. */
function guideBlankWallFlash(){
  const i=(typeof blankWallIdx==='function') ? blankWallIdx() : 0;
  const w=walls()[i]; if(!w) return;
  const S=state.settings, off=1.0;
  const geo=new THREE.PlaneGeometry(w.len, Math.max(1, S.ceil-S.base));
  geo.userData.own=true;
  const mat=new THREE.MeshBasicMaterial({color:0x3f9142, transparent:true, opacity:.4,
    depthTest:false, side:THREE.DoubleSide});
  mat.userData.own=true;
  const m=new THREE.Mesh(geo, mat);
  m.renderOrder=997;
  m.position.set((w.a.x+w.b.x)/2 + w.n.x*off, S.base+(S.ceil-S.base)/2, (w.a.z+w.b.z)/2 + w.n.z*off);
  m.quaternion.copy(wallQuat(w));
  guideGroup.add(m);
}
function refreshGuides(){
  clearGuides();
  if(!guideMode || guideUntil <= performance.now()) return;
  if(guideMode==='walls') addWallLetterGuides();
  if(guideMode==='bars') guideBarPreview();
  if(guideMode==='ladder') guideLadderPreview();
  if(guideMode==='mats') guideExistingFlash(o=>o.type==='mat');
  if(guideMode==='blankwall') guideBlankWallFlash();
}
function showGuides(mode, ms){
  guideMode=mode;
  guideUntil = ms==null ? Infinity : performance.now()+ms;   // no ms → stays until guideOff()
  refreshGuides();
}
function guideOff(){ guideMode=null; guideUntil=0; clearGuides(); }
function updateGuidePulse(){
  if(!guideMode) return;
  if(guideUntil <= performance.now()){
    guideMode=null;
    clearGuides();
    return;
  }
  const pulse=.5 + .45*Math.abs(Math.sin(performance.now()/450));   // a touch slower than the wizard arrow
  guideGroup.traverse(node=>{
    if(!(node.isMesh || node.isSprite) || !node.material) return;
    const mats=Array.isArray(node.material) ? node.material : [node.material];
    for(const mat of mats){
      if(!mat.transparent) continue;
      const maxAlpha=node.renderOrder>=999 ? .95 : .72;
      mat.opacity=Math.max(.2, maxAlpha*pulse);
    }
  });
}

/* -------- per-frame: dollhouse fading -------- */
function updateFades(){
  const S=state.settings, ws=walls();
  wallMeshes.forEach(m=>{
    const w=ws[m.userData.wall]; if(!w) return;
    const mid={x:(w.a.x+w.b.x)/2, z:(w.a.z+w.b.z)/2};
    const outside=(camera.position.x-mid.x)*w.n.x + (camera.position.z-mid.z)*w.n.z < 0;
    m.material.opacity = outside? .1 : 1;
    if(m.userData.baseMesh) m.userData.baseMesh.visible = !outside;
  });
  if(ceilMesh) ceilMesh.visible = camera.position.y < state.settings.ceil + 6;
}
// 3x phones would otherwise render 9x the pixels — cap for battery and frame rate
const DPR=Math.min(window.devicePixelRatio||1, 2);
function resize(){
  const w=canvas.clientWidth, h=canvas.clientHeight;
  if(w===0 || h===0) return;                      // viewport hidden — skip
  if(canvas.width!==Math.floor(w*DPR) || canvas.height!==Math.floor(h*DPR)){
    renderer.setPixelRatio(DPR);
    renderer.setSize(w,h,false);
    camera.aspect=w/h; camera.updateProjectionMatrix();
  }
}


/* =====================================================================
   PLACEMENT, SNAPPING & VALIDITY
   ===================================================================== */
const snapOn = ()=> $('tglSnap').classList.contains('on');

function allowedSurface(type, surf){
  const kind=CATALOG[type] ? CATALOG[type].kind : null;
  if(kind==='panel') return surf.k==='wall';
  if(kind==='mat')   return surf.k==='floor';
  return surf.k!=='floor';        // bars, runs, lumber: walls & ceiling only
}
function surfaceSize(surf){
  const S=state.settings;
  if(surf.k==='wall'){ const w=walls()[surf.i]; return {u0:0, u1:w.len, v0:0, v1:S.ceil}; }
  // Floor extends 3' beyond the room walls so mats can be placed close to the edges
  const bb=roomBBox(), FLOOR_PAD=36;
  return {u0:bb.minx-FLOOR_PAD, u1:bb.maxx+FLOOR_PAD, v0:bb.minz-FLOOR_PAD, v1:bb.maxz+FLOOR_PAD};
}
function inBounds(o,u,v){
  const dm=dims(o), s=surfaceSize(o.surf), S=state.settings;
  if(u-dm.w/2 < s.u0-0.01 || u+dm.w/2 > s.u1+0.01) return false;
  if(o.surf.k==='wall'){
    const floorMin = S.base;                     // nothing overlaps the baseboard
    if(v-dm.h/2 < floorMin-0.01 || v+dm.h/2 > S.ceil+0.01) return false;
  } else {
    if(v-dm.h/2 < s.v0-0.01 || v+dm.h/2 > s.v1+0.01) return false;
  }
  return true;
}
function rectsOverlap(o1,u1,v1,o2){
  const a=dims(o1), b=dims(o2);
  return Math.abs(u1-o2.u) < (a.w+b.w)/2 - 0.05 &&
         Math.abs(v1-o2.v) < (a.h+b.h)/2 - 0.05;
}
function sameSurf(s1,s2){ return s1.k===s2.k && (s1.k==='ceil' || s1.i===s2.i); }
function collides(o,u,v,excl){
  for(const q of state.objects){
    if(q.id===o.id || (excl && excl.has(q.id)) || !sameSurf(q.surf,o.surf)) continue;
    const pair=[o.type,q.type];
    if(pair.includes('monkey_bar') && pair.includes('two_by_four')) continue; // bars mount ON stringers
    if(rectsOverlap(o,u,v,q)) return true;
  }
  return false;
}

// mounting-hole points of a monkey bar, in surface coords
function holePts(o,u,v){
  if(o.rot%180===0) return [{u:u-HOLE_SPACING/2,v},{u:u+HOLE_SPACING/2,v}];
  return [{u,v:v-HOLE_SPACING/2},{u,v:v+HOLE_SPACING/2}];
}
// framing coordinate of a surface point (distance measured across stud/joist lines)
function framingCoord(surf, u, v){
  const S=state.settings;
  if(surf.k==='wall') return {c:u, base:S.studOff, sp:S.studSp};
  const jb=joistBasis();
  return {c: u*jb.n.x + v*jb.n.z, base: jb.base, sp: jb.sp};
}
// unit direction (in surface u,v space) that moves across the framing lines
function framingAxis(surf){
  if(surf.k==='wall') return {u:1,v:0};
  const jb=joistBasis(); return {u:jb.n.x, v:jb.n.z};
}

function snapPos(o, u, v, free, excl){
  const dm=dims(o), hw=dm.w/2, hh=dm.h/2, S=state.settings;
  if(!free && snapOn()){
    // ---- priority 1: edges of neighbours (flush abutment + edge alignment)
    let bu=null, bv=null;   // {val, d}
    for(const q of state.objects){
      if(q.id===o.id || (excl && excl.has(q.id)) || !sameSurf(q.surf,o.surf)) continue;
      const qd=dims(q), qhw=qd.w/2, qhh=qd.h/2;
      const nearV = Math.abs(v-q.v) < hh+qhh+SNAP_R;
      const nearU = Math.abs(u-q.u) < hw+qhw+SNAP_R;
      if(nearV) for(const cand of [q.u+qhw+hw, q.u-qhw-hw, q.u-qhw+hw, q.u+qhw-hw, q.u]){
        const d=Math.abs(u-cand); if(d<SNAP_R && (!bu||d<bu.d)) bu={val:cand,d};
      }
      if(nearU) for(const cand of [q.v+qhh+hh, q.v-qhh-hh, q.v-qhh+hh, q.v+qhh-hh, q.v]){
        const d=Math.abs(v-cand); if(d<SNAP_R && (!bv||d<bv.d)) bv={val:cand,d};
      }
    }
    // ---- priority 2: framing lines (bars, lumber & bare runs)
    if(o.type==='monkey_bar' || o.type==='two_by_four' || (o.type==='bar_run' && (!o.stringers || o.stringers==='none'))){
      let anchors;
      if(o.type==='monkey_bar') anchors=holePts(o,u,v);
      else if(o.type==='two_by_four') anchors=[{u,v}];
      else {
        anchors=[];
        const {B,centers}=runPts({...o,u,v});
        for(const ct of centers)
          for(const s of [-HOLE_SPACING/2, HOLE_SPACING/2])
            anchors.push({u:ct.u+B.u*s, v:ct.v+B.v*s});
      }
      let best=null;
      for(const a of anchors){
        const f=framingCoord(o.surf, a.u, a.v);
        const m=((f.c-f.base)%f.sp+f.sp)%f.sp;
        const delta = m < f.sp/2 ? -m : f.sp-m;
        if(Math.abs(delta)<SNAP_R && (best===null || Math.abs(delta)<Math.abs(best))) best=delta;
      }
      if(best!==null){
        const ax=framingAxis(o.surf);
        if(o.surf.k==='wall'){ if(!bu) bu={val:u+best, d:Math.abs(best)}; }
        else{
          if(Math.abs(ax.u)>0.01 && !bu) bu={val:u+ax.u*best, d:Math.abs(best)};
          if(Math.abs(ax.v)>0.01 && !bv) bv={val:v+ax.v*best, d:Math.abs(best)};
        }
      }
    }
    // ---- priority 3: 1" grid (snap edges to whole inches)
    u = bu ? bu.val : Math.round(u-hw)+hw;
    v = bv ? bv.val : Math.round(v-hh)+hh;
  }
  // clamp to bounds
  const s=surfaceSize(o.surf);
  u=Math.min(Math.max(u, s.u0+hw), s.u1-hw);
  if(o.surf.k==='wall') v=Math.min(Math.max(v, S.base+hh), S.ceil-hh);
  else v=Math.min(Math.max(v, s.v0+hh), s.v1-hh);
  return {u,v};
}

// axis vectors + bar centers of a run, in surface (u,v) space
function runPts(o){
  const rl=runLayout(o);
  const rr=((o.rot||0)*Math.PI/180);
  const A = {u:Math.cos(rr), v:Math.sin(rr)};    // run axis
  const B = {u:-Math.sin(rr), v:Math.cos(rr)};   // bar axis (perpendicular)
  const centers=[];
  for(let i=0;i<rl.n;i++){
    const t=-rl.L/2 + i*rl.sp;
    centers.push({u:o.u + A.u*t, v:o.v + A.v*t});
  }
  return {A,B,centers,rl};
}
// how many framing centerlines a (u,v)-axis-aligned rect crosses
function crossCount(surf, cu, cv, halfU, halfV){
  const f=framingCoord(surf,cu,cv), ax=framingAxis(surf);
  const e=Math.abs(ax.u)*halfU + Math.abs(ax.v)*halfV;
  const lo=f.c-e, hi=f.c+e;
  return Math.max(0, Math.floor((hi-f.base)/f.sp) - Math.ceil((lo-f.base)/f.sp) + 1);
}
// does a bar_run land on framing under its current stringer mode?
const ALIGN_TOL = 1.0;   // rail/2x4 centerline within 1" of a stud/joist centerline counts as "on it"
function runSupportOK(o){
  const {A,B,centers,rl}=runPts(o);
  o._railsAligned=false;
  if(o.stringers==='rails'){
    const ax=framingAxis(o.surf);
    for(const s of [-HOLE_SPACING/2, HOLE_SPACING/2]){
      const cu=o.u+B.u*s, cv=o.v+B.v*s;
      const halfU=Math.abs(A.u)*rl.railLen/2 + Math.abs(B.u)*1.75;
      const halfV=Math.abs(A.v)*rl.railLen/2 + Math.abs(B.v)*1.75;
      if(crossCount(o.surf,cu,cv,halfU,halfV) >= 2) continue;
      // rail parallel to the framing: OK if centered on a line
      const f=framingCoord(o.surf,cu,cv);
      const e=Math.abs(ax.u)*halfU + Math.abs(ax.v)*halfV;
      if(e<=2.5 && nearestLineDist(f.c,f.base,f.sp)<=ALIGN_TOL){ o._railsAligned=true; continue; }
      return false;
    }
    return true;
  }
  if(o.stringers==='per_bar'){
    for(const ct of centers){
      const halfU=Math.abs(B.u)*PER_BAR_LEN/2 + Math.abs(A.u)*1.75;
      const halfV=Math.abs(B.v)*PER_BAR_LEN/2 + Math.abs(A.v)*1.75;
      if(crossCount(o.surf,ct.u,ct.v,halfU,halfV) < 2) return false;
    }
    return true;
  }
  for(const ct of centers)
    for(const s of [-HOLE_SPACING/2, HOLE_SPACING/2]){
      const f=framingCoord(o.surf, ct.u+B.u*s, ct.v+B.v*s);
      if(nearestLineDist(f.c, f.base, f.sp) > SUPPORT_TOL) return false;
    }
  return true;
}

/* -------- support (framing) checks -------- */
function computeSupport(){
  const S=state.settings;
  const lumber = state.objects.filter(o=>o.type==='two_by_four');
  for(const o of state.objects){ o._unsupported=false; o._onStringer=false; }
  for(const o of lumber){
    const dm=dims(o);
    o._crossings = crossCount(o.surf, o.u, o.v, dm.w/2, dm.h/2);
    o._aligned=false;
    if(o._crossings < 2){
      // parallel to framing: OK if centered on a line
      const f=framingCoord(o.surf,o.u,o.v), ax=framingAxis(o.surf);
      const e=Math.abs(ax.u)*dm.w/2 + Math.abs(ax.v)*dm.h/2;
      if(e<=2.5 && nearestLineDist(f.c,f.base,f.sp)<=ALIGN_TOL){ o._aligned=true; o._unsupported=false; continue; }
    }
    o._unsupported = o._crossings < 2;
  }
  for(const o of state.objects){
    if(o.type==='bar_run'){
      o._unsupported = !runSupportOK(o);
      continue;
    }
    if(o.type!=='monkey_bar') continue;
    let allOnStringer=true, ok=true;
    for(const p of holePts(o,o.u,o.v)){
      const f=framingCoord(o.surf,p.u,p.v);
      const onFraming = nearestLineDist(f.c, f.base, f.sp) <= SUPPORT_TOL;
      let onStringer=false;
      for(const q of lumber){
        if(!sameSurf(q.surf,o.surf) || q._unsupported) continue;
        const qd=dims(q);
        if(Math.abs(p.u-q.u)<=qd.w/2 && Math.abs(p.v-q.v)<=qd.h/2){ onStringer=true; break; }
      }
      if(!onStringer) allOnStringer=false;
      if(!onFraming && !onStringer) ok=false;
    }
    o._onStringer = allOnStringer;
    o._unsupported = !ok;
  }
}

/* =====================================================================
   POINTER INTERACTION
   ===================================================================== */
const raycaster=new THREE.Raycaster();
let placing=null, ghost=null, runAnchor=null, runGhosts=null;
let selIds=new Set(), drag=null;
const selSingle = ()=> selIds.size===1 ? objById([...selIds][0]) : null;
// shift-click: whole connected array of touching panels
function connectedGroup(id){
  const o=objById(id);
  if(!o || CATALOG[o.type].kind!=='panel') return new Set([id]);
  const set=new Set([id]); const queue=[o];
  while(queue.length){
    const a=queue.pop(), ad=dims(a);
    for(const b of state.objects){
      if(set.has(b.id) || CATALOG[b.type].kind!=='panel' || !sameSurf(a.surf,b.surf)) continue;
      const bd=dims(b);
      if(Math.abs(a.u-b.u) < (ad.w+bd.w)/2 + 1 && Math.abs(a.v-b.v) < (ad.h+bd.h)/2 + 1){
        set.add(b.id); queue.push(b);
      }
    }
  }
  return set;
}

function pointerRay(e){
  const r=canvas.getBoundingClientRect();
  raycaster.setFromCamera(new THREE.Vector2(
    ((e.clientX-r.left)/r.width)*2-1, -((e.clientY-r.top)/r.height)*2+1), camera);
}
function hitSurface(e, type, onlySurf){
  pointerRay(e);
  const hits=raycaster.intersectObjects(pickPlanes,false);
  for(const h of hits){
    const surf=h.object.userData.surf;
    if(onlySurf && !sameSurf(surf,onlySurf)) continue;
    if(type && !allowedSurface(type,surf)) continue;
    if(surf.k==='wall'){   // ignore walls the camera is outside of (they're faded out)
      const w=walls()[surf.i];
      const mid={x:(w.a.x+w.b.x)/2, z:(w.a.z+w.b.z)/2};
      if((camera.position.x-mid.x)*w.n.x + (camera.position.z-mid.z)*w.n.z < 0) continue;
    }
    let u,v;
    if(surf.k==='wall'){
      const w=walls()[surf.i];
      u=(h.point.x-w.a.x)*w.dir.x + (h.point.z-w.a.z)*w.dir.z;
      v=h.point.y;
    } else { u=h.point.x; v=h.point.z; }
    return {surf,u,v};
  }
  return null;
}
/* Wall letter badges are pickable while they're on screen, so clicking "B" in the
   room jumps to Wall B's length row in the Room panel. */
function hitWallBadge(e){
  if(guideMode!=='walls' || !guideGroup.children.length) return null;
  pointerRay(e);
  const hits=raycaster.intersectObjects(guideGroup.children,false);
  for(const h of hits){
    if(h.object && h.object.userData.wallIdx!==undefined) return h.object.userData.wallIdx;
  }
  return null;
}
function focusWallRow(i){
  openRail('secRoom');
  syncRoomSection();
  if(wiz.active) wizFocusSet('secRoom');
  const row=document.querySelector('#rmWallList .field[data-wall="'+i+'"]');
  if(!row) return;
  document.querySelectorAll('#rmWallList .field.rowFlash').forEach(r=>r.classList.remove('rowFlash'));
  row.classList.add('rowFlash');
  row.scrollIntoView({block:'nearest', behavior:'smooth'});
  const sel=row.querySelector('select');
  if(sel) sel.focus();
}
function hitObject(e){
  pointerRay(e);
  const hits=raycaster.intersectObjects(objectGroup.children,true);
  for(const h of hits){
    let m=h.object;
    while(m && m.userData.id===undefined) m=m.parent;
    if(m && !m.userData.helper && m.userData.id!==undefined) return m.userData.id;
  }
  return null;
}

function setPlacing(type){
  placing=type; runAnchor=null; runForceRot=null;
  document.querySelectorAll('.prod').forEach(b=>b.classList.toggle('on', b.dataset.type===placing));
  clearGhost();
  const tip=$('placeTip'), runOpts=$('runOpts');
  runOpts.classList.toggle('hidden', placing!=='barrun');
  if(placing==='barrun') $('runSp').value = ageRules(state.settings.age).barSpacing + '"';
  if(!placing){ tip.classList.add('hidden'); return; }
  tip.classList.remove('hidden');
  tip.textContent = placing==='barrun'
    ? 'Bar Run — click a start point on a wall or the ceiling, move to stretch the run, click to confirm'
    : `Placing ${CATALOG[placing].name} — click ${CATALOG[placing].kind==='panel'?'a wall':CATALOG[placing].kind==='mat'?'the floor':'a wall or the ceiling'} · R to rotate · Esc when done`;
}
document.querySelectorAll('.prod').forEach(b=> b.onclick=()=>{
  setPlacing(placing===b.dataset.type?null:b.dataset.type);
  if(placing) tutNotify('pick', b.dataset.type);
});

let ghostRot=0;
function clearGhost(){
  if(ghost){ disposeGroup(ghost); scene.remove(ghost); ghost=null; }
  if(runGhosts){ disposeGroup(runGhosts); scene.remove(runGhosts); runGhosts=null; }
}
function updateGhost(e){
  clearGhost();
  if(placing==='barrun'){ updateRunGhost(e); return; }
  const hit=hitSurface(e, placing);
  if(!hit) return;
  const temp={id:-1, type:placing, surf:hit.surf, rot:ghostRot, len:48};
  const p=snapPos(temp, hit.u, hit.v, e.altKey);
  temp.u=p.u; temp.v=p.v;
  temp._bad = !inBounds(temp,p.u,p.v) || collides(temp,p.u,p.v);
  temp._lastHit=hit;
  ghost=makeMesh(temp,true); ghost.userData.temp=temp; scene.add(ghost);
}
let runForceRot=null, lastRunRot=0;
function runOptions(){
  const def=ageRules(state.settings.age).barSpacing;
  let sp=parseLen($('runSp').value); if(isNaN(sp)||sp<6) sp=def;
  return {spacing:sp, stringers:$('runStr').value};
}
function runAxisFrom(anchor, cur){
  const du=cur.u-anchor.u, dv=cur.v-anchor.v;
  return Math.abs(du)>=Math.abs(dv) ? {rot:0, len:du} : {rot:90, len:dv};
}
function updateRunGhost(e){
  const hit=hitSurface(e,'monkey_bar');
  if(!hit) return;
  const opt=runOptions();
  if(!runAnchor){   // preview a single bar at the cursor
    runGhosts=new THREE.Group();
    const t={id:-1,type:'monkey_bar',surf:hit.surf,u:hit.u,v:hit.v,rot:0};
    runGhosts.add(makeMesh(t,true)); scene.add(runGhosts); runGhosts.userData.hit=hit; return;
  }
  if(!sameSurf(hit.surf, runAnchor.surf)) return;   // run stays on its starting surface
  const ax=runAxisFrom(runAnchor,hit);
  const rot = runForceRot!==null ? runForceRot : ax.rot;
  lastRunRot=rot;
  const along = rot===0 ? hit.u-runAnchor.u : hit.v-runAnchor.v;
  const count=Math.max(2, Math.round(Math.abs(along)/opt.spacing)+1);
  const L=(count-1)*opt.spacing, sign=Math.sign(along)||1;
  const t={id:-1, type:'bar_run', surf:runAnchor.surf, rot,
           spacing:opt.spacing, count, stringers:opt.stringers,
           u: runAnchor.u + (rot===0 ? sign*L/2 : 0),
           v: runAnchor.v + (rot===90? sign*L/2 : 0)};
  t._bad = !inBounds(t,t.u,t.v) || collides(t,t.u,t.v);
  runGhosts=new THREE.Group();
  runGhosts.add(makeMesh(t,true));
  runGhosts.userData.run=t;
  scene.add(runGhosts);
  $('placeTip').textContent=`Bar Run — ${count} bars @ ${opt.spacing}" o.c.`+
    (opt.stringers==='none'?'':opt.stringers==='rails'?' on 2 rails':' on per-bar stringers')+
    ' · click to confirm · R flips direction · Esc cancels';
}

let lastMoveEvt=null;
canvas.addEventListener('pointermove', e=>{
  if(!placing && !drag){
    canvas.style.cursor = hitWallBadge(e)!==null ? 'pointer' : '';
  }
  lastMoveEvt=e;
  if(placing){ updateGhost(e); return; }
  if(drag){
    const prim=objById(drag.primary); if(!prim) return;
    if(drag.ids.length>1){
      // group drag: stay on the same surface, move as one
      const hit=hitSurface(e, prim.type, prim.surf);
      if(!hit) return;
      const p=snapPos(prim, hit.u+drag.offU, hit.v+drag.offV, e.altKey, drag.excl);
      const du=p.u-drag.starts[prim.id].u, dv=p.v-drag.starts[prim.id].v;
      let ok=true;
      for(const id of drag.ids){
        const o=objById(id); if(!o){ ok=false; break; }
        const st=drag.starts[id];
        if(!inBounds(o, st.u+du, st.v+dv) || collides(o, st.u+du, st.v+dv, drag.excl)){ ok=false; break; }
      }
      if(ok){
        for(const id of drag.ids){ const o=objById(id), st=drag.starts[id]; o.u=st.u+du; o.v=st.v+dv;
          updateMeshTransform(o); }
        updateHelpers();
      }
    } else {
      const o=prim;
      const hit=hitSurface(e, o.type);
      if(!hit) return;
      if(!sameSurf(hit.surf,o.surf)){ o.surf=hit.surf; drag.offU=0; drag.offV=0; }
      const p=snapPos(o, hit.u+drag.offU, hit.v+drag.offV, e.altKey);
      if(inBounds(o,p.u,p.v) && !collides(o,p.u,p.v)){ o.u=p.u; o.v=p.v;
        updateMeshTransform(o); updateHelpers(); }
    }
    // no rebuilds while dragging — full refresh happens on pointerup
  }
});
canvas.addEventListener('pointerdown', e=>{
  if(e.button!==0) return;
  const badge=hitWallBadge(e);
  if(badge!==null){ focusWallRow(badge); return; }
  if(isMob()) return;    // phones are viewer-only: orbit/pinch yes, place/select/drag no
  if(placing==='barrun'){
    const g=runGhosts;
    if(!runAnchor){
      const hit=g && g.userData.hit;
      if(hit){ runAnchor={surf:hit.surf, u:hit.u, v:hit.v}; }
      return;
    }
    if(g && g.userData.run && !g.userData.run._bad){
      const t=g.userData.run;
      pushUndo();
      const o={id:nextId++, type:'bar_run', surf:t.surf, u:t.u, v:t.v, rot:t.rot,
               spacing:t.spacing, count:t.count, stringers:t.stringers};
      state.objects.push(o);
      selIds=new Set([o.id]);
      setPlacing(null); refreshObjects(); toast('Bar run placed — it moves as one unit');
    }
    return;
  }
  if(placing){
    if(ghost && ghost.userData.temp && !ghost.userData.temp._bad){
      const t=ghost.userData.temp;
      const o={id:nextId, type:t.type, surf:t.surf, u:t.u, v:t.v, rot:t.rot};
      if(t.type==='two_by_four') o.len=48;
      // panels default their bottom edge to the top of the baseboard when placed near it
      if(CATALOG[t.type].kind==='panel'){
        const hh=dims(o).h/2;
        if(Math.abs((o.v-hh)-state.settings.base) < 6){
          const v2=state.settings.base+hh;
          if(inBounds(o,o.u,v2) && !collides(o,o.u,v2)) o.v=v2;
        }
      }
      if(!inBounds(o,o.u,o.v) || collides(o,o.u,o.v)) return;  // re-check at click time
      pushUndo(); nextId++;
      state.objects.push(o);
      selIds=new Set([o.id]); refreshObjects();
      tutNotify('place', o);
    }
    return;
  }
  const id=hitObject(e);
  if(id!==null){
    if(e.shiftKey) selIds = connectedGroup(id);
    else if(!selIds.has(id) || selIds.size<=1) selIds = new Set([id]);
    // (clicking a member of an existing multi-selection keeps the group for dragging)
    const prim=objById(id);
    const hit=hitSurface(e, prim.type, selIds.size>1 ? prim.surf : null);
    pushUndo();
    rebuildObjects(); refreshPanels();      // rebuild once with the new selection, then start dragging
    const starts={};
    for(const sid of selIds){ const o=objById(sid); if(o) starts[sid]={u:o.u, v:o.v}; }
    drag={ids:[...selIds], primary:id, excl:new Set(selIds), starts,
          offU: hit&&sameSurf(hit.surf,prim.surf)? prim.u-hit.u : 0,
          offV: hit&&sameSurf(hit.surf,prim.surf)? prim.v-hit.v : 0};
    if([...selIds].some(sid=>{const o=objById(sid); return o && CATALOG[o.type].kind==='panel';}))
      holdsGroup.visible=false;                  // holds are world-space instanced; they return on release
    controls.enabled=false;
  } else { selIds.clear(); rebuildObjects(); refreshPanels(); }
});
window.addEventListener('pointerup', ()=>{
  if(drag){ drag=null; controls.enabled=true; refreshObjects(); tutNotify('drag'); }
});

window.addEventListener('keydown', e=>{
  if(e.target.tagName==='INPUT' || e.target.tagName==='SELECT') return;
  if((e.metaKey||e.ctrlKey) && e.key.toLowerCase()==='z'){ e.preventDefault(); undo(); return; }
  if(e.key==='Escape'){ setPlacing(null); selIds.clear(); rebuildObjects(); refreshPanels(); return; }
  if(e.key.toLowerCase()==='r'){
    if(placing==='barrun'){
      if(runAnchor){ runForceRot = rotWrap((runForceRot===null? lastRunRot : runForceRot)+rotStep('barrun'),'barrun');
        if(lastMoveEvt) updateGhost(lastMoveEvt); }
      return;
    }
    if(placing){ ghostRot=rotWrap(ghostRot+rotStep(placing), placing);
      if(lastMoveEvt) updateGhost(lastMoveEvt); return; }
    const o=selSingle(); if(o){ pushUndo(); o.rot=rotWrap(o.rot+rotStep(o), o);
      const p=snapPos(o,o.u,o.v,true); o.u=p.u; o.v=p.v; refreshObjects(); } return;
  }
  if(e.key.toLowerCase()==='d'){
    const o=selSingle(); if(o){ pushUndo();
      const dm=dims(o);
      const c={...o, id:nextId++, u:o.u+dm.w, surf:{...o.surf}};
      delete c.auto;
      const p=snapPos(c,c.u,c.v,false); c.u=p.u; c.v=p.v;
      if(inBounds(c,c.u,c.v) && !collides(c,c.u,c.v)){ state.objects.push(c); selIds=new Set([c.id]); refreshObjects(); }
      else toast('No room to duplicate'); } return;
  }
  if(e.key==='Backspace'||e.key==='Delete'){
    if(selIds.size){ pushUndo(); state.objects=state.objects.filter(o=>!selIds.has(o.id));
      selIds.clear(); refreshObjects(); } return;
  }
  const step=e.shiftKey? .25 : 1;
  const nud={ArrowLeft:[-step,0],ArrowRight:[step,0],ArrowUp:[0,step],ArrowDown:[0,-step]}[e.key];
  if(nud){
    e.preventDefault();
    if(!selIds.size) return;
    const objs=[...selIds].map(objById).filter(Boolean);
    if(!objs.length) return;
    pushUndo();
    const excl=new Set(selIds);
    let ok=true;
    const moves=objs.map(o=>{
      const dv = o.surf.k==='wall' ? nud[1] : -nud[1];
      return {o, u:o.u+nud[0], v:o.v+dv};
    });
    for(const m of moves) if(!inBounds(m.o,m.u,m.v) || collides(m.o,m.u,m.v,excl)){ ok=false; break; }
    if(ok){ for(const m of moves){ m.o.u=m.u; m.o.v=m.v; } refreshObjects(); }
    else undoStack.pop();
  }
});

/* =====================================================================
   AUTO-POPULATE — generate layouts from per-wall checkboxes
   ===================================================================== */
function genFingerprint(){
  const S=state.settings;
  return JSON.stringify({o:state.wallOpts, p:state.points, c:state.closed,
    h:S.ceil, b:S.base, a:S.age, ss:S.studSp, so:S.studOff, js:S.joistSp, jo:S.joistOff, jw:S.joistWall,
    wc:S.wallCols, wr:S.wallRows, mc:S.matCount});
}
function alignRunToFraming(run){
  // shift the whole run along the framing axis so its mounting lands on lines
  const {A,B,centers}=runPts(run);
  const ax=framingAxis(run.surf);
  if(Math.abs(ax.u)<0.01 && Math.abs(ax.v)<0.01) return;
  let delta=0;
  if(!run.stringers || run.stringers==='none'){
    // put one mounting hole exactly on a line (holes are 16" o.c., matching 16" framing)
    const f=framingCoord(run.surf, centers[0].u+B.u*HOLE_SPACING/2, centers[0].v+B.v*HOLE_SPACING/2);
    const m=((f.c-f.base)%f.sp+f.sp)%f.sp;
    delta = m<f.sp/2 ? -m : f.sp-m;
  } else if(run.stringers==='per_bar'){
    // center each stringer midway between lines so it crosses two
    const f=framingCoord(run.surf, centers[0].u, centers[0].v);
    if(PER_BAR_LEN < f.sp+3.5) return;             // stringer too short to ever cross two lines
    const m=((f.c-f.base)%f.sp+f.sp)%f.sp;
    delta = f.sp/2 - m;
    if(delta> f.sp/2) delta-=f.sp;
    if(delta<-f.sp/2) delta+=f.sp;
  } else if(run.stringers==='rails'){
    // rails parallel to the framing lines: center rail 1 on a line
    // (rail 2 lands on a line too when framing is 16" o.c., matching the 16" rail gap)
    const par = Math.abs(A.u*ax.u + A.v*ax.v) < 0.05;
    if(!par) return;                                // rails cross the lines — no shift needed
    const f=framingCoord(run.surf, run.u+B.u*HOLE_SPACING/2, run.v+B.v*HOLE_SPACING/2);
    if(HOLE_SPACING % f.sp !== 0 && f.sp % HOLE_SPACING !== 0) return;   // 16" rails on 24" o.c.: impossible
    const m=((f.c-f.base)%f.sp+f.sp)%f.sp;
    delta = m<f.sp/2 ? -m : f.sp-m;
  } else return;
  run.u+=ax.u*delta; run.v+=ax.v*delta;
}
function placeRunWithSupport(run, noShift){
  // shrink until it fits the surface
  let guard=0;
  while(run.count>2 && !inBounds(run, run.u, run.v) && guard++<60) run.count--;
  const home={u:run.u, v:run.v};
  // prefer two rails, then per-bar, then bare
  for(const mode of ['rails','per_bar']){
    run.stringers=mode; run.u=home.u; run.v=home.v;
    if(!noShift) alignRunToFraming(run);           // noShift: position is deliberate (e.g. under a ladder)
    const p=snapPos(run, run.u, run.v, true);      // clamp back into bounds
    run.u=p.u; run.v=p.v;
    if(runSupportOK(run)){ state.objects.push(run); return; }
  }
  run.stringers='rails'; run.u=home.u; run.v=home.v;
  const p=snapPos(run, run.u, run.v, true); run.u=p.u; run.v=p.v;
  state.objects.push(run);   // best effort — will show red with guidance
}
function autoPopulate(){
  const stamp=genFingerprint();
  if(state.genStamp===stamp) return;          // nothing changed since last generation
  const hadAuto = state.objects.some(o=>o.auto);
  const wantsAuto = (state.wallOpts||[]).some(o=>o && (o.p||o.t||o.s));
  if(!hadAuto && !wantsAuto){ state.genStamp=stamp; return; }
  pushUndo();
  state.objects = state.objects.filter(o=>!o.auto);
  const S=state.settings, rules=ageRules(S.age), ws=walls();
  // pads raise the landing surface automatically when the design will include mats
  const autoPadH = ((state.wallOpts||[]).some(o=>o&&o.p) || state.objects.some(o=>o.type==='mat')) ? CATALOG.mat.d : 0;
  const ptEq=(p,q)=> Math.abs(p.x-q.x)+Math.abs(p.z-q.z) < 0.5;
  const mod=(a,m)=>((a%m)+m)%m;
  const opts=i=>(state.wallOpts||[])[i]||{};
  const wallAt=(pt,notI)=> ws.find(x=> x.i!==notI && (ptEq(x.a,pt)||ptEq(x.b,pt)));
  // ladder u position on wall X so its rails land on X's studs, nearest a target distance
  function studAlignedU(X, cornerAtA, target){
    if(S.studSp!==16) return target;          // 16"-apart rails need 16" o.c. studs; otherwise keep target
    const congru = cornerAtA ? mod(S.studOff+8,16) : mod(X.len-S.studOff-8,16);
    let v = congru + Math.round((target-congru)/16)*16;
    if(v<12) v+=16;
    return v;
  }

  /* ---- plan overhead runs: offset from their wall, aligned to a transfer-ladder wall's studs ---- */
  const runPlans={};    // wall index -> {off, ladderAtA, ladderAtB}
  for(const w of ws){
    if(!opts(w.i).t) continue;
    let off=(opts(w.i).p?18:12)+CATALOG.monkey_bar.w/2;
    let ladderAtA=false, ladderAtB=false, studRef=null;
    for(const [pt,end] of [[w.a,'a'],[w.b,'b']]){
      const nb=wallAt(pt,w.i);
      if(nb && opts(nb.i).s){
        if(end==='a') ladderAtA=true; else ladderAtB=true;
        if(!studRef) studRef={pt, wall:nb};
      }
    }
    if(studRef) off = studAlignedU(studRef.wall, ptEq(studRef.wall.a, studRef.pt), off);
    runPlans[w.i]={off, ladderAtA, ladderAtB};
  }

  /* ---- pass 1a: overhead runs ---- */
  for(const w of ws){
    const plan=runPlans[w.i]; if(!plan) continue;
    const sp=rules.barSpacing;
    const tied = plan.ladderAtA || plan.ladderAtB;
    let count, tCenter;
    if(tied){
      const t0=6, t1=w.len-6;                  // end bars stop ~6" from the corner, right above the ladder rungs
      count=Math.max(2, Math.floor((t1-t0)/sp)+1);
      const L=(count-1)*sp;
      tCenter = plan.ladderAtB ? t1 - L/2 : t0 + L/2;
    } else {
      count=Math.max(2, Math.floor((w.len-10)/sp)+1);
      tCenter=w.len/2;
    }
    const wAngle = Math.round(Math.atan2(w.dir.z, w.dir.x) * 180 / Math.PI);
    placeRunWithSupport({id:nextId++, type:'bar_run', auto:true,
      surf:{k:'ceil'},
      rot: wAngle,
      spacing:sp, count, stringers:'rails',
      u: w.a.x + w.dir.x*tCenter + w.n.x*plan.off,
      v: w.a.z + w.dir.z*tCenter + w.n.z*plan.off}, tied);
  }

  /* ---- pass 1b: ladders — always on the wall whose box is checked ---- */
  for(const w of ws){
    if(!opts(w.i).s) continue;
    // look for an overhead run on a neighboring wall that shares a corner with this one
    let tie=null;
    for(const pt of [w.a, w.b]){
      const nb=wallAt(pt,w.i);
      if(nb && runPlans[nb.i]){ tie={pt, off:runPlans[nb.i].off}; break; }
    }
    const sp=rules.ladderSpacing;
    const bottom=Math.max(S.base+6, rules.firstRung + autoPadH);
    const top=S.ceil-10;
    const count=Math.max(2, Math.floor((top-bottom)/sp)+1);
    const L=(count-1)*sp;
    if(tie){
      const cornerAtA=ptEq(w.a, tie.pt);
      const ladderU = cornerAtA ? tie.off : w.len - tie.off;   // directly beneath the neighboring run
      placeRunWithSupport({id:nextId++, type:'bar_run', auto:true,
        surf:{k:'wall',i:w.i}, rot:90, spacing:sp, count, stringers:'rails',
        u:ladderU, v:bottom + L/2}, true);
    } else if(w.len>=30){
      // no neighboring run: simple ladder near the end of this wall, snapped to studs
      const ladderU = studAlignedU(w, false, w.len-13);
      placeRunWithSupport({id:nextId++, type:'bar_run', auto:true,
        surf:{k:'wall',i:w.i}, rot:90, spacing:sp, count, stringers:'rails',
        u:Math.min(ladderU, w.len-11), v:bottom + L/2});
    }
  }

  /* ---- pass 2: climbing panels (skip cells occupied by ladders etc.) ---- */
  for(const w of ws){
    const opt=opts(w.i); if(!opt.p) continue;
    // The wizard can cap the block; null means fill the wall. blockLeft keeps it centred either way.
    const maxCols=Math.floor(w.len/PANEL_W);
    const maxRows=Math.max(1, Math.floor((S.ceil - S.base)/PANEL_H));
    const cols=S.wallCols==null ? maxCols : Math.max(1, Math.min(maxCols, S.wallCols));
    const rows=S.wallRows==null ? maxRows : Math.max(1, Math.min(maxRows, S.wallRows));
    if(cols<=0) continue;
    const blockLeft=(w.len - cols*PANEL_W)/2;
    for(let cI=0;cI<cols;cI++) for(let r=0;r<rows;r++){
      const o={id:nextId, type:'half_panel', auto:true,
        surf:{k:'wall',i:w.i}, rot:0,
        u:blockLeft + PANEL_W/2 + cI*PANEL_W, v:S.base + PANEL_H/2 + r*PANEL_H};
      if(!inBounds(o,o.u,o.v) || collides(o,o.u,o.v)) continue;
      nextId++; state.objects.push(o);
    }
  }

  /* ---- pass 3: climbing mats ----
     Mats fill in the order a person would lay them: along the climbing wall
     first, then wrapping onto the other walls (each rotated to run parallel to
     its own wall, so a corner reads as an L), then successive rows working in
     from the walls. Every wall centres the mats it actually receives. */
  {
    const MW=CATALOG.mat.w, MH=CATALOG.mat.h;
    const BB_THICK=0.75;                     // baseboard sticks ~3/4" out from the wall face
    const clear=BB_THICK + 0.5;              // clear the baseboards by half an inch
    const panelWalls=ws.filter(x=>opts(x.i).p);
    const primary=panelWalls[0] || ws[1] || ws[0];
    if(primary){
      const order=[primary, ...ws.filter(x=>x.i!==primary.i)];
      // default: cover the panel block, plus one more if the wall allows
      const primCols=(()=>{ const mc=Math.floor(primary.len/PANEL_W);
        return S.wallCols==null ? mc : Math.max(1, Math.min(mc, S.wallCols)); })();
      const alongPrimary=Math.max(1, Math.floor((primary.len-clear*2)/MW));
      let want=Math.max(1, Math.min(alongPrimary, Math.ceil((primCols*PANEL_W)/MW)));
      if(alongPrimary >= want+1) want += 1;
      if(S.matCount!=null) want=Math.max(0, S.matCount);   // no ceiling — fill the floor if asked

      // Fill wall by wall, row by row. Each wall lays out only the mats it
      // actually receives, centred on that wall — so one remaining mat sits
      // centred instead of parked in the first slot of a longer row.
      const placed=[];
      let remaining=want;
      for(let row=0; row<8 && remaining>0; row++){
        for(const x of order){
          if(remaining<=0) break;
          const cap=Math.floor((x.len - clear*2)/MW);
          if(cap<=0) continue;
          const k=Math.min(cap, remaining);
          const rot=rotWrap(Math.round(-Math.atan2(x.dir.z, x.dir.x)*180/Math.PI), {type:'mat'});
          const off=MH/2 + clear + row*MH;
          for(let i=0;i<k;i++){
            const t=x.len/2 + (i-(k-1)/2)*MW;   // centred for the count on THIS wall
            const m={id:nextId, type:'mat', auto:true, surf:{k:'floor'}, rot,
              u:x.a.x+x.dir.x*t+x.n.x*off, v:x.a.z+x.dir.z*t+x.n.z*off};
            if(!inBounds(m,m.u,m.v)) continue;
            if(state.objects.some(q => q.type!=='mat' && sameSurf(q.surf,m.surf) && rectsOverlap(m,m.u,m.v,q))) continue;
            if(placed.some(q => rectsOverlap(m,m.u,m.v,q))) continue;   // deliberate tiling — no doubling up
            nextId++; state.objects.push(m); placed.push(m); remaining--;
          }
        }
      }
    }
  }
  state.genStamp=stamp;
}

/* =====================================================================
   PANELS: warnings, BOM, selection bar
   ===================================================================== */
function refreshWarnings(){
  const S=state.settings, rules=ageRules(S.age), out=[];
  const bars=state.objects.filter(o=>o.type==='monkey_bar');
  const runs=state.objects.filter(o=>o.type==='bar_run');
  const totalBars = bars.length + runs.reduce((a,r)=>a+(r.count||0),0);
  if(totalBars && rules.noBars)
    out.push({t:'warn', m:`Overhead monkey bars and ladders aren\u2019t recommended for children under 4. Your youngest climber is set to ${S.age} \u2014 either adjust the age setting or remove the bars/ladder.`});
  const unsup=state.objects.filter(o=>o._unsupported);
  for(const o of unsup){
    const where = o.surf.k==='ceil' ? 'ceiling' : 'Wall '+String.fromCharCode(65+o.surf.i);
    const why = o.type==='two_by_four' ? ' \u2014 each 2\u00d74 needs to cross at least 2 studs or joists, or sit centered on one running the same direction. Move it or adjust the framing offset.'
      : o.type==='bar_run' ? (o.stringers==='rails' ? ' \u2014 the rails need to cross studs or joists, or sit centered on them. Try nudging the run or adjusting the framing offset.'
        : ' \u2014 the per-bar stringers each need to cross at least 2 studs or joists. Try switching to rails or nudging the run.')
      : ' \u2014 the mounting holes must hit a stud, a joist, or a supported 2\u00d74 stringer behind them.';
    out.push({t:'warn', m:`Your ${CATALOG[o.type].name} on ${where} isn\u2019t landing on framing${why}.`});
  }
  // install note for anything relying on centered-on-framing alignment
  if(state.objects.some(o=>(o.type==='bar_run' && !o._unsupported && o._railsAligned) ||
                           (o.type==='two_by_four' && !o._unsupported && o._aligned)))
    out.push({t:'info', m:`Some of your 2×4 stringers run parallel to your framing and are placed to sit centered on a stud or joist. Make sure they line up exactly with the framing when you install.`});
  // grip height for ceiling bars (singles and runs)
  const grips=[];
  for(const o of bars) if(o.surf.k==='ceil') grips.push(S.ceil - CATALOG.monkey_bar.d - (o._onStringer?1.5:0));
  for(const o of runs) if(o.surf.k==='ceil') grips.push(S.ceil - CATALOG.monkey_bar.d - (o.stringers && o.stringers!=='none' ?1.5:0));
  if(grips.length){
    const above=Math.max(...grips) - effectivePad();
    if(above > rules.maxGrip)
      out.push({t:'info', m:`Heads up: your bars sit ${fmtLen(Math.round(above*10)/10)} above the landing surface \u2014 higher than the ${rules.maxGrip}" typically suggested for your youngest climber. Ceiling height is what it is, so just keep an eye on this one: make sure your kids are capable of using the bars safely, always spot them while they climb, and consider extra pads below (each pad adds 5" to the landing surface).`});
  }
  // bar-to-bar spacing: entrapment applies everywhere; reach defaults differ overhead vs ladder
  let minOver=null, minLad=null;
  const noteGap=(v,surfK)=>{ if(v<=0.5) return;
    if(surfK==='ceil'){ if(minOver===null||v<minOver) minOver=v; }
    else { if(minLad===null||v<minLad) minLad=v; } };
  for(const r of runs) noteGap(r.spacing, r.surf.k);
  for(let i=0;i<bars.length;i++) for(let j=i+1;j<bars.length;j++){
    const a=bars[i], b=bars[j];
    if(!sameSurf(a.surf,b.surf) || a.rot!==b.rot) continue;
    const alongBar = a.rot%180===0 ? Math.abs(a.u-b.u) : Math.abs(a.v-b.v);
    const acrossBar= a.rot%180===0 ? Math.abs(a.v-b.v) : Math.abs(a.u-b.u);
    if(alongBar<12) noteGap(acrossBar, a.surf.k);
  }
  const minAny = Math.min(minOver??1e9, minLad??1e9);
  if(minAny<10 && minAny<1e9)
    out.push({t:'warn', m:`Your bars are ${fmtLen(Math.round(minAny*10)/10)} apart. Anything between 3.5" and 9" apart is a head-entrapment risk \u2014 please space bars at 10" or more.`});
  if(minOver!==null && minOver>rules.barSpacing+0.1)
    out.push({t:'info', m:`Your overhead bar spacing is ${fmtLen(Math.round(minOver*10)/10)}. For your youngest climber, ${rules.barSpacing}" or closer is easier to reach across.`});
  if(minLad!==null && minLad>rules.ladderSpacing+0.1)
    out.push({t:'info', m:`Your ladder rungs are ${fmtLen(Math.round(minLad*10)/10)} apart. For your youngest climber, ${rules.ladderSpacing}" is a comfortable spacing.`});
  // climbing wall height limit: recommend professional design above 10'
  const panelsAll=state.objects.filter(o=>CATALOG[o.type].kind==='panel');
  if(panelsAll.length){
    const highest=Math.max(...panelsAll.map(o=> o.v+dims(o).h/2));
    if(highest > 120)
      out.push({t:'warn', m:`Your climbing wall reaches ${fmtLen(Math.round(highest))} high. We don\u2019t recommend going higher than 10' without having a professional design your climbing wall and safety systems.`});
  }
  // take-off height
  const panels=panelsAll;
  if(panels.length){
    const lowest=Math.min(...panels.map(o=> o.v-dims(o).h/2));
    if(lowest - effectivePad() > rules.takeoff)
      out.push({t:'info', m:`Your lowest panel edge sits ${fmtLen(Math.round(lowest))} above the floor. For your youngest climber, keep it at ${rules.takeoff}" or less above the landing surface so they can reach the first hold safely.`});
  }
  const box=$('warnList');
  box.innerHTML = out.length
    ? out.map(w=>`<div class="warnItem ${w.t==='info'?'info':''}">${w.m}</div>`).join('')
    : '<div class="allclear">✓ No issues found</div>';
  refreshGuidelines();
}

function effectivePad(){ return state.objects.some(o=>o.type==='mat') ? CATALOG.mat.d : 0; }
function bomCounts(){
  const counts={}; const lumber=[]; let holds=0;
  for(const o of state.objects){
    if(o.type==='two_by_four'){ lumber.push(o.len||48); continue; }
    if(o.type==='bar_run'){
      counts.monkey_bar=(counts.monkey_bar||0)+o.count;
      const rl=runLayout(o);
      if(o.stringers==='rails'){ lumber.push(rl.railLen, rl.railLen); }
      else if(o.stringers==='per_bar'){ for(let i=0;i<rl.n;i++) lumber.push(PER_BAR_LEN); }
      continue;
    }
    if(CATALOG[o.type] && CATALOG[o.type].kind==='panel') holds += CATALOG[o.type].holds||0;
    counts[o.type]=(counts[o.type]||0)+1;
  }
  if(holds) holds=holdTargets().total;
  return {counts, lumber, holds};
}
function holdSetInfo(holds){
  if(!holds) return null;
  const style=state.settings.holdStyle||'classic';
  const cfg=HOLD_STYLES[style];
  const cur=(state.settings.colors||DEFAULT_COLORS).hold;
  const found=cfg.palette.find(([,v])=>v===cur);
  const colorName=found?found[0]:'Custom';
  return {sets:Math.ceil(holds/10),
          label:`${cfg.name} Climbing Holds (Set of 10${cfg.palette.length>1?', '+colorName:''})`};
}
function bomText(){
  const {counts, lumber, holds}=bomCounts();
  const lines=[];
  for(const t of ['half_panel','full_panel','double_panel','monkey_bar','mat'])
    if(counts[t]) lines.push(`${counts[t]} × ${CATALOG[t].name}`);
  const hs=holdSetInfo(holds);
  if(hs) lines.push(`${hs.sets} × ${hs.label}`);
  if(lumber.length){
    lines.push(`${lumber.length} × 2x4 stringer (${lumber.map(l=>fmtLen(l)).join(', ')}) — customer-supplied lumber, not sold by Tree Frog Play`);
  }
  const ws=walls(), S=state.settings;
  lines.push('', `Room: ${ws.map(w=>'Wall '+String.fromCharCode(65+w.i)+' '+fmtLen(Math.round(w.len))).join(', ')}`,
             `Ceiling ${fmtLen(S.ceil)} · baseboard ${fmtLen(S.base)} · pads ${effectivePad()?'5\" (included)':'none'}`);
  return lines.join('\n');
}
function refreshGuidelines(){
  const el=$('guideContent'); if(!el) return;
  el.innerHTML = `
    <h4>Climbing panels</h4>
    <p>The first hold should be roughly at the child\u2019s knee height when they\u2019re standing on the pad. Heights assume a 5\u201d pad and 3.75\u201d from panel bottom edge to first hold.</p>
    <table><thead><tr><th>Age</th><th>Panel bottom edge</th><th>First hold</th></tr></thead><tbody>
      <tr><td>2\u20135</td><td>12\u201d</td><td>15.75\u201d</td></tr>
      <tr><td>6\u20138</td><td>15\u201d</td><td>18.75\u201d</td></tr>
      <tr><td>9+</td><td>18\u201d</td><td>21.75\u201d</td></tr>
    </tbody></table>
    <h4>Climbing holds</h4>
    <p>Holds ship with 3/8\u201d\u201316 bolts and washers. Rotate to change grip difficulty; harder holds (small edges, pinches) suit older or stronger climbers.</p>
    <h4>Overhead monkey bars</h4>
    <table><thead><tr><th>Age</th><th>Bar spacing</th></tr></thead><tbody>
      <tr><td>2\u20135</td><td>12\u201d</td></tr>
      <tr><td>6\u20138</td><td>15\u201d</td></tr>
      <tr><td>9+</td><td>up to 18\u201d</td></tr>
    </tbody></table>
    <p>Keep the nearest rail 18\u201d from a climbing wall or 12\u201d from a bare wall. Anything between 3.5\u201d and 9\u201d apart is a head-entrapment risk.</p>
    <h4>Climbing pads</h4>
    <p>Cover as much of the fall zone as you can. Pads are 72\u201d\u00d736\u201d\u00d75\u201d; the planner auto-places them beneath every climbing wall. Add extras anywhere a child could fall.</p>`;
}
function refreshBom(){
  const items=buildCartPlan();
  const {lumber}=bomCounts();
  let html;
  if(!items.length){ html='<p class="hint">Nothing placed yet.</p>'; }
  else {
    html='<table>';
    let subtotal=0;
    for(const it of items){
      const line=it.qty*it.price; subtotal+=line;
      html+=`<tr><td class="name">${it.label}</td><td class="qty">\u00d7${it.qty}</td><td class="price">$${line.toLocaleString()}</td></tr>`;
    }
    if(lumber.length){
      html+=`<tr><td class="name" style="color:var(--sub);">2\u00d74 stringers <span style="font-size:.72rem;">(customer-supplied)</span></td><td class="qty">\u00d7${lumber.length}</td><td class="price">\u2014</td></tr>`;
    }
    html+=`<tr class="total"><td class="name">Estimated total</td><td class="qty"></td><td class="price">$${subtotal.toLocaleString()}</td></tr>`;
    html+='</table>';
    if(lumber.length)
      html+=`<div class="lumberNote">2\u00d74 lengths: ${lumber.map(l=>fmtLen(l)).join(', ')}.</div>`;
  }
  const ws=walls(), S=state.settings;
  html+=`<div class="roomSum">${ws.map(w=>'Wall '+String.fromCharCode(65+w.i)+': '+fmtLen(Math.round(w.len))).join(' \u00b7 ')}<br>
         Ceiling ${fmtLen(S.ceil)} \u00b7 baseboard ${fmtLen(S.base)} \u00b7 pads ${effectivePad()?'5\" (included)':'none'}</div>`;
  $('bomTable').innerHTML=html;
  $('bomPrice').textContent='';
  updateHoldRow();
  if(typeof renderBuildControls==='function') renderBuildControls();
}
/* "Copy BOM as text" was removed — bomText() is still used by the PDF export. */

/* =====================================================================
   SHOPIFY CART + PDF + CUSTOMER MODE
   ===================================================================== */
function syncFramingVis(){
  if(typeof framingGroup==='undefined' || !framingGroup) return;
  framingGroup.visible = $('tglStuds').classList.contains('on');
}

/* Welcome modal — first-load walkthrough */
function openWelcome(){
  const s=state.settings;
  $('wcAge').value=String(s.age);
  $('wcCeil').value=fmtLen(s.ceil);
  $('wcBase').value=fmtLen(s.base);
  $('welcome').classList.remove('hidden');
}
function closeWelcome(save){
  if(save){
    state.settings.age=+$('wcAge').value;
    const feed=(id,val)=>{ const el=$(id); if(el){ el.value=val; el.dispatchEvent(new Event('change')); } };
    feed('inCeil', $('wcCeil').value);
    feed('inBase', $('wcBase').value);
    if($('ageSel')) $('ageSel').value=String(state.settings.age);
    refreshPanels(); syncHash();
  }
  $('welcome').classList.add('hidden');
  sessionStorage.setItem('tfp_welcome_seen','1');
}
$('wcGo').onclick=()=>closeWelcome(true);
$('wcClose').onclick=()=>closeWelcome(false);
/* NOTE: the welcome modal has no trigger — the guided wizard replaced it as the
   first-run experience. openWelcome() is kept so it can be re-wired if wanted. */
function buildCartPlan(){
  const {counts, holds}=bomCounts();
  const C=state.settings.colors||DEFAULT_COLORS;
  const items=[];
  const panelOpt = C.panel==='birch' ? 'Birch - Sanded' : 'White - Textured';
  for(const t of ['half_panel','full_panel','double_panel'])
    if(counts[t]) items.push({handle:TFP_CATALOG[t].handle, option:panelOpt, qty:counts[t],
      label:`${CATALOG[t].name} — ${panelOpt}`, price:TFP_CATALOG[t].price});
  if(counts.monkey_bar){
    const nm=(PALETTES.bar.find(([,v])=>v===C.bar)||['Charcoal'])[0];
    items.push({handle:TFP_CATALOG.monkey_bar.handle, option:nm, qty:counts.monkey_bar,
      label:`Monkey Bar (16") — ${nm}`, price:TFP_CATALOG.monkey_bar.price});
  }
  if(counts.mat){
    const nm=(PALETTES.mat.find(([,v])=>v===C.mat)||['Mink Gray'])[0];
    items.push({handle:TFP_CATALOG.mat.handle, option:nm, qty:counts.mat,
      label:`Indoor Climbing Pad — ${nm}`, price:TFP_CATALOG.mat.price});
  }
  if(holds){
    const style=state.settings.holdStyle||'classic', cfg=HOLD_STYLES[style], hc=TFP_CATALOG.holds[style];
    const nm=(cfg.palette.find(([,v])=>v===C.hold)||cfg.palette[0])[0];
    items.push({handle:hc.handle, option: style==='wood'? null : nm, qty:Math.ceil(holds/10),
      label:`${cfg.name} Climbing Holds (Set of 10)${style==='wood'?'':' — '+nm}`, price:hc.price});
  }
  return items;
}
function planPrice(items){ return items.reduce((s,i)=>s+i.qty*i.price,0); }
const variantCache={};
async function resolveVariant(base, handle, option){
  if(!variantCache[handle])
    variantCache[handle]=fetch(base+'/products/'+handle+'.js').then(r=>{
      if(!r.ok) throw new Error('product lookup failed'); return r.json(); });
  const p=await variantCache[handle];
  if(option==null) return p.variants[0] && p.variants[0].id;
  const v=p.variants.find(v=> v.title===option || v.public_title===option ||
                              (v.options && v.options.includes(option)));
  return v && v.id;
}
/* ---------- live stock (Shopify storefront) ----------
   Fetch each catalog product's /products/{handle}.js so sold-out colors gray out
   and never reach the cart. Same-origin on treefrogplay.com; off-store the fetch
   just fails and everything stays available (graceful — stock is a live feature). */
const STOCK={ prod:Object.create(null) };
const _skey = s => String(s==null?'':s).trim().toLowerCase();
async function loadStock(){
  const onStore=/^(www\.)?treefrogplay\.com$/i.test(location.hostname);
  const base=onStore ? '' : 'https://treefrogplay.com';
  const handles=new Set();
  for(const t of ['half_panel','full_panel','double_panel','monkey_bar','mat']) handles.add(TFP_CATALOG[t].handle);
  for(const k in TFP_CATALOG.holds) handles.add(TFP_CATALOG.holds[k].handle);
  await Promise.all([...handles].map(async h=>{
    try{
      if(!variantCache[h]) variantCache[h]=fetch(base+'/products/'+h+'.js').then(r=>{ if(!r.ok) throw 0; return r.json(); });
      const p=await variantCache[h], v=Object.create(null);
      for(const va of (p.variants||[])){
        const a=!!va.available;
        v[_skey(va.title)]=a;
        if(va.public_title) v[_skey(va.public_title)]=a;
        for(const o of (va.options||[])) if(v[_skey(o)]===undefined) v[_skey(o)]=a;
      }
      STOCK.prod[h]={ any:(p.variants||[]).some(x=>x.available), v };
    }catch(_){/* unknown handle -> treated as available */}
  }));
  try{ renderColorRows(); }catch(_){}
  try{ if(typeof renderBuildControls==='function') renderBuildControls(); }catch(_){}
}
// entire product sold out? (unknown handle -> false, i.e. allowed)
function prodSoldOut(handle){ const p=STOCK.prod[handle]; return !!p && !p.any; }
// specific variant (by option/title) sold out? (unknown -> false)
function optSoldOut(handle, opt){
  const p=STOCK.prod[handle]; if(!p) return false;
  if(opt==null) return !p.any;
  return p.v[_skey(opt)]===false;
}
// colorKey + palette value -> the Shopify product+option it maps to, or null if it
// isn't a stocked product colour (wall/floor are paint; wood holds have no colours).
function colorStock(key, val){
  if(key==='bar'){ const nm=(PALETTES.bar.find(([,v])=>v===val)||[])[0]; return nm && {handle:TFP_CATALOG.monkey_bar.handle, opt:nm}; }
  if(key==='mat'){ const nm=(PALETTES.mat.find(([,v])=>v===val)||[])[0]; return nm && {handle:TFP_CATALOG.mat.handle, opt:nm}; }
  if(key==='panel'){ const opt=(val==='birch')?'Birch - Sanded':'White - Textured';
    return {handle:['half_panel','full_panel','double_panel'].map(t=>TFP_CATALOG[t].handle), opt}; }
  if(key==='hold'){ const style=state.settings.holdStyle||'classic'; if(style==='wood') return null;
    const nm=(HOLD_STYLES[style].palette.find(([,v])=>v===val)||[])[0];
    return nm && {handle:TFP_CATALOG.holds[style].handle, opt:nm}; }
  return null;
}
function colorSoldOut(key, val){
  const s=colorStock(key,val); if(!s) return false;
  // a panel FINISH is only "sold out" if it's gone on every panel size
  if(Array.isArray(s.handle)) return s.handle.every(h=>optSoldOut(h, s.opt));
  return optSoldOut(s.handle, s.opt);
}
async function addToCart(){
  const items=buildCartPlan();
  if(!items.length){ toast('Nothing in the design yet'); return; }
  // backstop: never try to add a sold-out variant (the swatch grey-out can miss
  // finish-per-size combos; this catches everything and names the culprit).
  const oos=items.filter(it=>optSoldOut(it.handle, it.option));
  if(oos.length){ toast('Sold out: '+oos[0].label+' — choose another and try again'); return; }
  // exact match only: on a planner.* subdomain the same-origin cart API would
  // hit the wrong host, so subdomains use the permalink route instead
  const onStore=/^(www\.)?treefrogplay\.com$/i.test(location.hostname);
  const base=onStore ? '' : 'https://treefrogplay.com';
  toast('Looking up products\u2026');
  try{
    const resolved=[];
    for(const it of items){
      const id=await resolveVariant(base, it.handle, it.option);
      if(!id) throw new Error('no variant: '+it.label);
      resolved.push({id, quantity:it.qty});
    }
    if(onStore){
      const r=await fetch('/cart/add.js',{method:'POST',
        headers:{'Content-Type':'application/json'},
        body:JSON.stringify({items:resolved})});
      if(!r.ok) throw new Error('cart add failed');
      // attach the design link to the cart so the order carries the customer's layout
      fetch('/cart/update.js',{method:'POST',
        headers:{'Content-Type':'application/json'},
        body:JSON.stringify({attributes:{'Playroom Planner design': location.href}})}).catch(()=>{});
      toast('Added to cart!');
      window.open('/cart','_blank');
    } else {
      // storefront=true keeps Shopify on the cart page; without it the /cart/
      // permalink jumps the customer straight to checkout.
      const url=base+'/cart/'+resolved.map(r=>r.id+':'+r.quantity).join(',')+
        '?storefront=true'+
        '&attributes[Playroom+Planner+design]='+encodeURIComponent(location.href);
      window.open(url,'_blank');
    }
  }catch(err){
    toast(onStore ? ('Cart error: '+err.message)
      : 'Couldn\u2019t reach the store from here \u2014 host this tool on treefrogplay.com for one-click carts, or copy the BOM.');
  }
}
$('btnCart').onclick=addToCart;

function stringerNotes(){
  const notes=[];
  for(const o of state.objects.filter(o=>o.type==='bar_run')){
    const rl=runLayout(o);
    const where=o.surf.k==='ceil' ? 'Ceiling monkey bar run'
      : 'Wall ladder (Wall '+String.fromCharCode(65+o.surf.i)+')';
    if(o.stringers==='rails')
      notes.push(`${where}: two flat 2\u00d74 rails, each ${fmtLen(rl.railLen)} long, mounted 16" apart (center to center). Each rail must cross two or more joists/studs, or sit centered directly on one running the same direction \u2014 rails must line up with the framing.`);
    else if(o.stringers==='per_bar')
      notes.push(`${where}: one 22" flat 2\u00d74 behind each of the ${rl.n} bars, run parallel to the bar so it crosses two framing members.`);
    else
      notes.push(`${where}: bars mount directly to framing \u2014 every mounting hole must land on a joist/stud centerline.`);
  }
  return notes;
}
async function loadJsPDF(){
  if(window.jspdf && window.jspdf.jsPDF) return window.jspdf.jsPDF;
  const CDNs=[
    'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js',
    'https://unpkg.com/jspdf@2.5.1/dist/jspdf.umd.min.js',
    'https://cdn.jsdelivr.net/npm/jspdf@2.5.1/dist/jspdf.umd.min.js',
  ];
  let lastErr=null;
  for(const url of CDNs){
    try{
      await new Promise((resolve, reject)=>{
        const s=document.createElement('script');
        s.src=url; s.crossOrigin='anonymous';
        const t=setTimeout(()=>reject(new Error('timeout')), 10000);
        s.onload=()=>{ clearTimeout(t); resolve(); };
        s.onerror=(e)=>{ clearTimeout(t); reject(new Error('script error')); };
        document.head.appendChild(s);
      });
      if(window.jspdf && window.jspdf.jsPDF) return window.jspdf.jsPDF;
    }catch(e){
      lastErr=e;
      console.warn('PDF CDN failed:', url, e.message);
    }
  }
  throw lastErr || new Error('All CDNs failed');
}
async function makePdf(){
  if(!glOK || $('planView').classList.contains('hidden')){ toast('Open the 3D planner first'); return; }
  const ws=walls(); const S=state.settings;
  if(!ws.length){ toast('Draw your room first'); return; }
  toast('Building PDF\u2026');
  let jsPDF;
  try{ jsPDF=await loadJsPDF(); }
  catch(e){
    console.error('PDF library load failed:', e);
    const msg = location.protocol==='file:'
      ? 'Could not load the PDF library. Serve this file over http:// instead of file:// (any local server works).'
      : ('Could not load the PDF library: '+(e && e.message || 'network error')+'. Check the browser console for details.');
    toast(msg);
    return;
  }

  /* --- capture four views (locked to 3:2 landscape aspect for consistent PDF layout) --- */
  const bb=roomBBox();
  const cx=(bb.minx+bb.maxx)/2, cz=(bb.minz+bb.maxz)/2;
  const spanX=Math.max(bb.maxx-bb.minx, 60), spanZ=Math.max(bb.maxz-bb.minz, 60);
  const saveP=camera.position.clone(), saveT=controls.target.clone();
  const savedSize=new THREE.Vector2();
  renderer.getSize(savedSize);
  const savedAspect=camera.aspect;
  const capW=1200, capH=800;
  renderer.setSize(capW, capH, false);
  camera.aspect=capW/capH;
  camera.updateProjectionMatrix();
  const aspect = capW / capH;
  const fovRad = camera.fov * Math.PI / 180;
  const halfV = Math.tan(fovRad/2);
  const halfH = halfV * aspect;
  const captureAt=(px,py,pz, tx,ty,tz)=>{
    camera.position.set(px,py,pz);
    controls.target.set(tx,ty,tz);
    controls.update(); updateFades();
    renderer.render(scene,camera);
    return canvas.toDataURL('image/jpeg', 0.9);
  };
  // Top-down: fit spanX × spanZ with generous padding for landing surface + ceiling items
  const dTop = Math.max(spanZ/(2*halfV), spanX/(2*halfH)) * 1.7;
  const shotTop = captureAt(cx, dTop, cz+0.5, cx, 0, cz);
  // Pick the "main" wall for elevation (most panels; fallback to longest)
  let mainWall=null, maxScore=-1;
  for(const w of ws){
    const opt=(state.wallOpts||[])[w.i]||{};
    const pOnWall=state.objects.filter(o=>o.surf.k==='wall' && o.surf.i===w.i && CATALOG[o.type] && CATALOG[o.type].kind==='panel').length;
    const score = pOnWall + (opt.p?3:0);
    if(score>maxScore){ maxScore=score; mainWall=w; }
  }
  if(!mainWall){ mainWall = ws.slice().sort((a,b)=>b.len-a.len)[0]; }
  const wcx=mainWall.a.x + mainWall.dir.x*mainWall.len/2;
  const wcz=mainWall.a.z + mainWall.dir.z*mainWall.len/2;
  const dFront = Math.max(mainWall.len/(2*halfH), S.ceil/(2*halfV)) * 1.35;
  const shotFront = captureAt(
    wcx + mainWall.n.x*dFront, S.ceil*0.5, wcz + mainWall.n.z*dFront,
    wcx, S.ceil*0.5, wcz);
  const sphereR = 0.5 * Math.sqrt(spanX*spanX + S.ceil*S.ceil + spanZ*spanZ);
  const dIso = sphereR / Math.sin(fovRad/2) * 1.05;
  const shotIsoR = captureAt(cx + dIso*0.6,  dIso*0.55, cz + dIso*0.6,  cx, S.ceil*0.35, cz);
  const shotIsoL = captureAt(cx - dIso*0.6,  dIso*0.55, cz + dIso*0.6,  cx, S.ceil*0.35, cz);
  // restore camera + render size
  renderer.setSize(savedSize.x, savedSize.y, false);
  camera.aspect=savedAspect;
  camera.updateProjectionMatrix();
  camera.position.copy(saveP); controls.target.copy(saveT); controls.update(); updateFades();
  renderer.render(scene,camera);

  /* --- build PDF --- */
  const doc=new jsPDF({unit:'pt', format:'letter'});
  const PW=612, PH=792, M=46, cw=PW-M*2;
  const ar=canvas.height/canvas.width;
  const GREEN=[148,188,116], GREEN_DK=[124,165,92], INK=[30,30,30], SUB=[97,103,95], LINK=[50,100,170];
  const setC=c=>doc.setTextColor(c[0],c[1],c[2]);
  const setF=c=>doc.setFillColor(c[0],c[1],c[2]);
  const wrap=(t,y,mw)=>{ const lines=doc.splitTextToSize(t, mw||cw); doc.text(lines,M,y); return y + lines.length*14; };
  const need=(y,h)=>{ if(y+h > PH-M){ doc.addPage(); return M+20; } return y; };
  const section=(title, newPage)=>{
    if(newPage) doc.addPage();
    setF(GREEN); doc.rect(0,0,PW,44,'F');
    setC([255,255,255]); doc.setFont('helvetica','bold'); doc.setFontSize(16);
    doc.text(title, M, 28);
    setC(INK); doc.setFont('helvetica','normal'); doc.setFontSize(10.5);
    return 66;
  };

  /* ---- PAGE 1: COVER ---- */
  setF(GREEN); doc.rect(0,0,PW,130,'F');
  setC([255,255,255]); doc.setFont('helvetica','bold'); doc.setFontSize(28);
  doc.text('Your Tree Frog Playroom', M, 64);
  doc.setFont('helvetica','normal'); doc.setFontSize(11);
  doc.text('treefrogplay.com', M, 96);
  doc.setFontSize(9);
  doc.text(new Date().toLocaleDateString(), PW-M, 96, {align:'right'});
  let y=170;
  setC(GREEN_DK); doc.setFont('helvetica','bold'); doc.setFontSize(11);
  doc.text('DESIGN SUMMARY', M, y); y+=18;
  setC(INK); doc.setFont('helvetica','normal'); doc.setFontSize(10.5);
  const wallSum = ws.map(w=>'Wall '+String.fromCharCode(65+w.i)+': '+fmtLen(Math.round(w.len))).join('   \u2022   ');
  y = wrap(wallSum, y) + 4;
  y = wrap(`Ceiling: ${fmtLen(S.ceil)}   |   Baseboard: ${fmtLen(S.base)}   |   Youngest climber: ${S.age} years`, y) + 4;
  const panelCt = state.objects.filter(o=>CATALOG[o.type] && CATALOG[o.type].kind==='panel').length;
  const barCt = state.objects.filter(o=>o.type==='bar_run').reduce((s,r)=>s+(r.count||0),0);
  const matCt = state.objects.filter(o=>o.type==='mat').length;
  const bits=[];
  if(panelCt) bits.push(`${panelCt} climbing panel${panelCt!==1?'s':''}`);
  if(barCt) bits.push(`${barCt} monkey bar${barCt!==1?'s':''}`);
  if(matCt) bits.push(`${matCt} climbing pad${matCt!==1?'s':''}`);
  if(bits.length) y = wrap('Includes: '+bits.join(', '), y) + 18;
  else y += 12;
  const heroW=cw, heroH=Math.min(heroW*ar, 320);
  doc.addImage(shotFront, 'JPEG', M, y, heroW, heroH);
  y += heroH + 22;
  setC(SUB); doc.setFontSize(9.5);
  doc.text('View or edit this design online: ', M, y);
  setC(LINK); doc.setFont('helvetica','bold');
  doc.textWithLink('click here', M + doc.getTextWidth('View or edit this design online: '), y, {url:location.href});
  setC(SUB); doc.setFont('helvetica','normal');

  /* ---- PAGE 2: FOUR VIEWS ---- */
  y = section('Your Design', true);
  const gW=(cw-14)/2, gH=Math.min(gW*ar, 210);
  const drawCell=(img,x,y,label)=>{
    doc.addImage(img,'JPEG',x,y,gW,gH);
    setC(SUB); doc.setFontSize(8); doc.setFont('helvetica','bold');
    doc.text(label, x+gW/2, y+gH+12, {align:'center'});
    setC(INK); doc.setFont('helvetica','normal'); doc.setFontSize(10.5);
  };
  drawCell(shotTop,    M,          y, 'TOP-DOWN');
  drawCell(shotFront,  M+gW+14,    y, 'FRONT ELEVATION');
  y += gH + 30;
  drawCell(shotIsoR,   M,          y, 'ISOMETRIC \u2014 FRONT RIGHT');
  drawCell(shotIsoL,   M+gW+14,    y, 'ISOMETRIC \u2014 FRONT LEFT');
  y += gH + 30;
  setC(SUB); doc.setFontSize(9);
  wrap('Room shape shown to scale. Move any item, swap colors, or rework the plan online.', y);

  /* ---- PAGE 3: SHOPPING LIST ---- */
  y = section('Your Shopping List', true);
  const items = buildCartPlan();
  const {lumber}=bomCounts();
  doc.setFont('helvetica','bold'); doc.setFontSize(9); setC(SUB);
  doc.text('ITEM', M, y);
  doc.text('QTY', M+350, y);
  doc.text('PRICE', PW-M, y, {align:'right'});
  y += 8; setC(SUB); doc.setDrawColor(200,200,200); doc.setLineWidth(0.5);
  doc.line(M, y, PW-M, y); y += 14;
  setC(INK); doc.setFont('helvetica','normal'); doc.setFontSize(10.5);
  const INCL={
    'half-climbing-panel':'Mounting screws, layout tool, printed instructions',
    'full-climbing-panel':'Mounting screws, layout tool, printed instructions',
    'double-climbing-panel':'Mounting screws, layout tool, printed instructions',
    'monkey-bar':'2.5" M6 lag bolts and washers (4 per bar), printed instructions',
    'climbing-pad':'Zippered protective cover',
    'classic-climbing-holds':'3/8"-16 bolts and washers for every hold',
    'rock-climbing-holds':'3/8"-16 bolts and washers for every hold',
    'leaf-climbing-holds':'3/8"-16 bolts and washers for every hold',
    'wooden-climbing-holds':'3/8"-16 bolts and washers for every hold',
  };
  let total=0;
  for(const it of items){
    y = need(y, 42);
    doc.text(it.label, M, y, {maxWidth:320});
    doc.text('\u00d7 '+it.qty, M+350, y);
    const line=it.qty*it.price; total+=line;
    doc.text('$'+line.toLocaleString(), PW-M, y, {align:'right'});
    y += 14;
    const inc=INCL[it.handle];
    if(inc){
      setC(SUB); doc.setFontSize(8.5);
      doc.text('Includes: '+inc, M+10, y);
      setC(INK); doc.setFontSize(10.5);
      y += 11;
    }
    y += 6;
  }
  y += 4;
  doc.setDrawColor(30,30,30); doc.setLineWidth(1);
  doc.line(M, y, PW-M, y); y += 18;
  doc.setFont('helvetica','bold'); doc.setFontSize(13); setC(INK);
  doc.text('Estimated total', M, y);
  doc.text('$'+total.toLocaleString(), PW-M, y, {align:'right'});
  y += 24;
  setC(SUB); doc.setFont('helvetica','normal'); doc.setFontSize(9);
  y = wrap('Prices as shown on treefrogplay.com \u2014 verify at checkout.', y) + 10;
  setC(INK); doc.setFontSize(10.5);

  /* ---- WHAT YOU'LL SUPPLY ---- */
  y = need(y, 240);
  const supplyHead = y===M+20;   // just added a page
  if(!supplyHead) y += 6;
  setF(GREEN_DK); doc.rect(M, y, cw, 3, 'F'); y += 22;
  doc.setFont('helvetica','bold'); doc.setFontSize(14); setC(GREEN_DK);
  doc.text('What You\u2019ll Supply', M, y); y += 22;
  setC(INK); doc.setFont('helvetica','normal'); doc.setFontSize(10.5);
  if(lumber.length){
    doc.setFont('helvetica','bold'); doc.text('Lumber for stringers', M, y); y += 15;
    doc.setFont('helvetica','normal');
    y = wrap(`${lumber.length} \u00d7 flat 2\u00d74 lumber, cut to these lengths:`, y) + 4;
    y = wrap(lumber.map(l=>fmtLen(l)).join(',  '), y, cw-20) + 8;
    setC(SUB); doc.setFontSize(9);
    y = wrap('Any home improvement store carries these \u2014 Home Depot, Lowe\u2019s, or a local lumberyard. Please verify each length yourself against your actual studs and joists; all verification should be done by you or a licensed contractor or engineer.', y) + 12;
    setC(INK); doc.setFontSize(10.5);
  }
  doc.setFont('helvetica','bold'); doc.text('Fasteners for stringers', M, y); y += 15;
  doc.setFont('helvetica','normal');
  y = wrap('#10 \u00d7 3.5" wood screws \u2014 no washers needed. Use them to mount stringers into your studs or joists, driven every ~16" along the stringer, hitting every framing member possible.', y) + 12;
  doc.setFont('helvetica','bold'); doc.text('Fasteners for monkey bars', M, y); y += 15;
  doc.setFont('helvetica','normal');
  y = wrap('The included 2.5" M6 lag bolts and washers work great whether you\u2019re mounting directly to studs, directly to joists, or through a 2\u00d74 stringer.', y) + 4;
  setC(SUB); doc.setFontSize(9.5);
  y = wrap('Heads-up: mounting through a stringer with the included 2.5" bolts means the tips will poke through the back of the stringer and into the wall behind, leaving small holes. If you\u2019d rather avoid that, pick up 1.5" \u00d7 1/4" lag bolts at any hardware store \u2014 the same washers work.', y) + 12;
  setC(INK); doc.setFontSize(10.5);
  doc.setFont('helvetica','bold'); doc.text('Paint (optional)', M, y); y += 15;
  doc.setFont('helvetica','normal');
  y = wrap('We recommend Benjamin Moore Scuff-X. It works on both Sanded Birch and Textured White panels: Birch will show wood grain through the paint; Textured White keeps its grip texture. It\u2019s also our recommended paint for stringers if you want them to blend into the wall.', y) + 4;
  setC(SUB); doc.setFontSize(9.5);
  y = wrap('Tip: on Textured White panels, each additional coat of paint slightly smooths out the texture \u2014 use fewer coats to keep more grip, or more coats for a smoother feel.', y) + 8;
  setC(INK); doc.setFontSize(10.5);

  /* ---- INSTALLATION GUIDE ---- */
  y = section('Installation Guide', true);
  doc.setFont('helvetica','bold'); doc.setFontSize(11); setC(INK);
  doc.text('Before you start', M, y); y += 15;
  doc.setFont('helvetica','normal'); doc.setFontSize(10.5);
  y = wrap('Read the general installation guide before you begin. It walks through wall prep, mounting technique, and safety checks. If any of your mounting walls are masonry (brick, concrete, cinder block), you\u2019ll need Tapcon screws instead of the standard hardware \u2014 see the masonry-specific guide.', y) + 12;
  setC(GREEN_DK); doc.setFont('helvetica','bold');
  doc.text('General install guide:', M, y);
  setC(LINK); doc.setFont('helvetica','normal');
  doc.textWithLink('treefrogplay.com/pages/installation-guide', M+134, y,
    {url:'https://treefrogplay.com/pages/installation-guide'});
  y += 15;
  setC(GREEN_DK); doc.setFont('helvetica','bold');
  doc.text('Masonry install:', M, y);
  setC(LINK); doc.setFont('helvetica','normal');
  doc.textWithLink('treefrogplay.com/pages/installing-climbing-panels-on-masonry', M+134, y,
    {url:'https://treefrogplay.com/pages/installing-climbing-panels-on-masonry'});
  y += 22;
  setC(INK);

  doc.setFont('helvetica','bold'); doc.setFontSize(11);
  doc.text('Your walls', M, y); y += 16;
  doc.setFont('helvetica','normal'); doc.setFontSize(10.5);
  for(const w of ws){
    y = need(y, 42);
    const label = 'Wall '+String.fromCharCode(65+w.i)+' \u2014 '+fmtLen(Math.round(w.len));
    doc.setFont('helvetica','bold'); doc.setFontSize(11); setC(GREEN_DK);
    doc.text(label, M, y); y += 13;
    setC(INK); doc.setFont('helvetica','normal'); doc.setFontSize(10);
    const panelsOn = state.objects.filter(o=>o.surf.k==='wall' && o.surf.i===w.i && CATALOG[o.type] && CATALOG[o.type].kind==='panel');
    const laddersOn = state.objects.filter(o=>o.type==='bar_run' && o.surf.k==='wall' && o.surf.i===w.i);
    const parts=[];
    if(panelsOn.length) parts.push(`${panelsOn.length} climbing panel${panelsOn.length>1?'s':''}`);
    if(laddersOn.length) parts.push(`${laddersOn.length} transfer ladder${laddersOn.length>1?'s':''}`);
    doc.text(parts.length ? '\u2022 Mounts: '+parts.join(', ') : '\u2022 No items mount to this wall', M+10, y);
    y += 20;
  }

  // Stringer installation notes
  const notes = stringerNotes();
  if(notes.length){
    y = need(y, 120);
    doc.setFont('helvetica','bold'); doc.setFontSize(11); setC(INK);
    doc.text('Stringer installation', M, y); y += 16;
    doc.setFont('helvetica','normal'); doc.setFontSize(10.5);
    y = wrap('Stringers connect the monkey bars into your ceiling framing. A few rules:', y) + 4;
    doc.setFont('helvetica','bold');
    y = wrap('\u2022 Connect to as many studs or joists as possible.', y) + 2;
    y = wrap('\u2022 Each rail must cross at least two framing members, OR sit centered directly on one running the same direction.', y) + 2;
    y = wrap('\u2022 Never let a monkey bar hang past the last stud/joist connection \u2014 no cantilevering.', y) + 12;
    doc.setFont('helvetica','normal');
    doc.setFont('helvetica','bold'); doc.text('For your design:', M, y); y += 15;
    doc.setFont('helvetica','normal');
    for(const nt of notes){
      y = need(y, 40);
      const lines=doc.splitTextToSize('\u2022 '+nt, cw);
      doc.text(lines, M, y); y += lines.length*13 + 6;
    }
  }

  // Overhead bar-run positioning: distance from wall depends on whether the wall has panels
  const hasCeilingRun = state.objects.some(o => o.type==='bar_run' && o.surf.k==='ceil');
  if(hasCeilingRun){
    y = need(y, 90);
    doc.setFont('helvetica','bold'); doc.setFontSize(11); setC(INK);
    doc.text('Overhead bar-run positioning', M, y); y += 15;
    doc.setFont('helvetica','normal'); doc.setFontSize(10.5);
    y = wrap('The rail closest to a wall should sit different distances back depending on what\u2019s on that wall:', y) + 4;
    doc.setFont('helvetica','bold');
    y = wrap('\u2022 Wall with climbing panels: keep the near rail at least 18" from the wall.', y) + 2;
    y = wrap('\u2022 Wall with no climbing panels: 12" from the wall is enough.', y) + 2;
    doc.setFont('helvetica','normal');
    setC(SUB); doc.setFontSize(9.5);
    y = wrap('This gives climbers room to swing past the wall without their feet catching the panels.', y) + 12;
    setC(INK); doc.setFontSize(10.5);
  }

  /* ---- DESIGN GUIDELINES ---- */
  y = section('Design Guidelines', true);
  doc.setFont('helvetica','normal'); doc.setFontSize(10.5); setC(INK);
  y = wrap('These are the sizing rules the planner used for your design. They come from CPSC playground guidelines and Tree Frog Play\u2019s own installation experience. Numbers are recommendations \u2014 use them as a starting point and adjust for your climbers.', y) + 12;

  doc.setFont('helvetica','bold'); doc.setFontSize(11); setC(GREEN_DK);
  doc.text('Climbing panels', M, y); y += 15;
  setC(INK); doc.setFont('helvetica','normal'); doc.setFontSize(10.5);
  y = wrap('The first hold should be easily reachable at about knee height when the child is standing on the pad. Heights assume a 5" Tree Frog climbing pad; the first row of tee-nuts sits about 3.75" up from the panel\u2019s bottom edge.', y) + 6;
  doc.setFont('helvetica','bold'); doc.setFontSize(9.5); setC(SUB);
  doc.text('YOUNGEST CLIMBER', M, y);
  doc.text('BOTTOM OF PANEL', M+180, y);
  doc.text('FIRST HOLD', M+320, y); y += 4;
  doc.setDrawColor(200,200,200); doc.line(M, y, PW-M, y); y += 12;
  setC(INK); doc.setFont('helvetica','normal'); doc.setFontSize(10);
  const panelRows=[['2\u20135 years','12"','15.75"'],['6\u20138 years','15"','18.75"'],['9+ years','18"','21.75"']];
  for(const [a,b,c] of panelRows){ doc.text(a,M,y); doc.text(b,M+180,y); doc.text(c,M+320,y); y+=13; }
  y += 8;

  doc.setFont('helvetica','bold'); doc.setFontSize(11); setC(GREEN_DK);
  doc.text('Climbing holds', M, y); y += 15;
  setC(INK); doc.setFont('helvetica','normal'); doc.setFontSize(10.5);
  y = wrap('Every hold ships with 3/8"\u201316 bolts and washers. Rotate holds to change grip difficulty \u2014 use easier positive grips low on the wall and small edges or pinches higher up.', y) + 12;

  doc.setFont('helvetica','bold'); doc.setFontSize(11); setC(GREEN_DK);
  doc.text('Overhead monkey bars', M, y); y += 15;
  setC(INK); doc.setFont('helvetica','normal'); doc.setFontSize(10.5);
  y = wrap('Recommended bar spacing (center to center):', y) + 4;
  doc.setFont('helvetica','bold'); doc.setFontSize(9.5); setC(SUB);
  doc.text('YOUNGEST CLIMBER', M, y);
  doc.text('BAR SPACING', M+180, y); y += 4;
  doc.setDrawColor(200,200,200); doc.line(M, y, PW-M, y); y += 12;
  setC(INK); doc.setFont('helvetica','normal'); doc.setFontSize(10);
  const barRows=[['2\u20135 years','12"'],['6\u20138 years','15"'],['9+ years','up to 18"']];
  for(const [a,b] of barRows){ doc.text(a,M,y); doc.text(b,M+180,y); y+=13; }
  y += 8;
  y = wrap('Keep the nearest rail at least 18" out from a wall that has climbing panels, or at least 12" from a bare wall. Any spacing between 3.5" and 9" is a head-entrapment risk \u2014 space bars 10" or more apart.', y) + 12;

  doc.setFont('helvetica','bold'); doc.setFontSize(11); setC(GREEN_DK);
  doc.text('Wall-to-ceiling ladders', M, y); y += 15;
  setC(INK); doc.setFont('helvetica','normal'); doc.setFontSize(10.5);
  y = wrap('Recommended rung spacing (center to center):', y) + 4;
  doc.setFont('helvetica','bold'); doc.setFontSize(9.5); setC(SUB);
  doc.text('YOUNGEST CLIMBER', M, y);
  doc.text('RUNG SPACING', M+180, y); y += 4;
  doc.setDrawColor(200,200,200); doc.line(M, y, PW-M, y); y += 12;
  setC(INK); doc.setFont('helvetica','normal'); doc.setFontSize(10);
  const rungRows=[['2\u20135 years','12"'],['6\u20138 years','18"'],['9+ years','up to 18"']];
  for(const [a,b] of rungRows){ doc.text(a,M,y); doc.text(b,M+180,y); y+=13; }
  y += 8;

  doc.setFont('helvetica','bold'); doc.setFontSize(11); setC(GREEN_DK);
  doc.text('Climbing pads', M, y); y += 15;
  setC(INK); doc.setFont('helvetica','normal'); doc.setFontSize(10.5);
  y = wrap('Cover as much of the fall zone as you can. Tree Frog pads are 72" \u00d7 36" \u00d7 5". Add extras anywhere a child could fall \u2014 in front of every climbing wall, under every overhead run, and around the base of every ladder.', y) + 16;

  /* ---- SAFETY & CARE ---- */
  y = section('Safety & Care', true);
  doc.setFont('helvetica','bold'); doc.setFontSize(11); setC(INK);
  doc.text('Age guidance', M, y); y += 15;
  doc.setFont('helvetica','normal'); doc.setFontSize(10.5);
  const rules=ageRules(S.age);
  y = wrap('General age guidance for home playroom setups:', y) + 6;
  y = wrap('\u2022 Under 4: no overhead monkey bars or transfer ladders \u2014 climbing panels and bouldering only. Kids this young haven\u2019t built the upper-body strength for hanging, and can slip through gaps that are safe for older kids.', y) + 2;
  y = wrap('\u2022 Ages 2\u20135: 12" bar and rung spacing, shorter reach limits, holds positioned low on the wall. Adult supervision at all times.', y) + 2;
  y = wrap('\u2022 Ages 6\u20138: 15" bar spacing, 18" rung spacing, longer reach. Kids can climb independently but should be supervised near overhead features.', y) + 2;
  y = wrap('\u2022 Ages 9+: full-height panels and wider spacing are fine. Focus on hold difficulty, route variety, and grip strength progression.', y) + 10;
  const ageText = rules.noBars
    ? `For your design: youngest climber is ${S.age} years. Because ${S.age < 4 ? 'they\u2019re under 4' : 'age settings put them in the preschool range'}, the planner kept your design to climbing panels only \u2014 no overhead bars or transfer ladders.`
    : `For your design: youngest climber is ${S.age} years. The planner has sized bar spacing at ${rules.barSpacing}", ladder rung spacing at ${rules.ladderSpacing}", first rung height at ${rules.firstRung}", and set the safe grip-reach limit at ${rules.maxGrip}" above the landing surface. If your kids are older or stronger than average, you can widen bar spacing in the 3D editor.`;
  y = wrap(ageText, y) + 18;

  doc.setFont('helvetica','bold'); doc.text('Landing surface', M, y); y += 15;
  doc.setFont('helvetica','normal');
  const padText = effectivePad()
    ? 'Your design includes 5" climbing pads. Always keep them in place beneath the climbing area \u2014 they are your primary fall protection. We recommend using as many pads as you can fit under the climbing surface so the landing zone is fully covered, and adding extra pads on any wall where children may fall.'
    : 'Your design does not include climbing pads. We strongly recommend adding them \u2014 the more area you cover, the safer the landing zone. Use as many pads as you can fit under the climbing surface, and add extra where children may fall.';
  y = wrap(padText, y) + 8;
  setC(SUB); doc.setFontSize(9.5);
  y = wrap('Creating a safe climbing environment is your responsibility. Inspect the setup regularly, replace worn hardware, and supervise young climbers.', y) + 18;
  setC(INK); doc.setFontSize(10.5);

  doc.setFont('helvetica','bold'); doc.text('Warranty & support', M, y); y += 15;
  doc.setFont('helvetica','normal');
  y = wrap('All Tree Frog Play products include a 1-year money-back guarantee. Questions any time \u2014 visit treefrogplay.com.', y) + 18;

  // Legal disclaimer
  y = need(y, 100);
  doc.setFont('helvetica','bold'); setC(INK);
  doc.text('Disclaimer', M, y); y += 15;
  doc.setFont('helvetica','normal'); setC(SUB); doc.setFontSize(9.5);
  y = wrap('This document is provided as supplementary guidance only. It is not a substitute for a professional installation review. All measurements, hardware selections, framing assumptions, and safety decisions are ultimately the responsibility of the installer. Any installation should be verified by a licensed contractor or engineer prior to use.', y) + 6;
  y = wrap('Tree Frog Play assumes no legal liability for the guidance provided in this document. Creating a safe climbing or play environment is solely the responsibility of the installer and the property owner.', y) + 12;
  setC(INK); doc.setFontSize(10.5);

  y = need(y, 30);
  setC(SUB); doc.setFontSize(9.5);
  doc.text('Open this design online \u2014 ', M, y);
  setC(LINK); doc.setFont('helvetica','bold');
  doc.textWithLink('click here', M + doc.getTextWidth('Open this design online \u2014 '), y, {url:location.href});
  setC(SUB); doc.setFont('helvetica','normal');

  doc.save('Your-Tree-Frog-Playroom.pdf');
  toast('PDF saved');
}
$('btnPdf').onclick=makePdf;
$('goBuild').onclick=()=>showPlan();

function centerSelection(axis){   // 'h' = along the surface, 'v' = up-down (or depth on ceiling)
  const objs=[...selIds].map(objById).filter(Boolean);
  if(!objs.length) return;
  const surf=objs[0].surf;
  if(!objs.every(o=>sameSurf(o.surf,surf))){ toast('Selection spans multiple surfaces'); return; }
  const s=surfaceSize(surf), S=state.settings;
  let lo=1e9, hi=-1e9;
  for(const o of objs){ const dm=dims(o);
    if(axis==='h'){ lo=Math.min(lo,o.u-dm.w/2); hi=Math.max(hi,o.u+dm.w/2); }
    else          { lo=Math.min(lo,o.v-dm.h/2); hi=Math.max(hi,o.v+dm.h/2); } }
  const target = axis==='h' ? (s.u0+s.u1)/2
    : (surf.k==='wall' ? (S.base+S.ceil)/2 : (s.v0+s.v1)/2);
  const delta = target-(lo+hi)/2;
  const excl=new Set(selIds);
  for(const o of objs){
    const u = axis==='h' ? o.u+delta : o.u;
    const v = axis==='v' ? o.v+delta : o.v;
    if(!inBounds(o,u,v) || collides(o,u,v,excl)){ toast('Not enough room to center'); return; }
  }
  pushUndo();
  for(const o of objs){ if(axis==='h') o.u+=delta; else o.v+=delta; }
  refreshObjects();
}
function refreshSelBar(){
  const bar=$('selBar');
  if(!selIds.size){ bar.classList.add('hidden'); return; }
  bar.classList.remove('hidden');
  $('selCH').onclick=()=>centerSelection('h');
  $('selCV').onclick=()=>centerSelection('v');
  const single=selSingle();
  const rows=['selURow','selVRow','selLenRow','selSpRow','selCntRow','selStrRow'];
  if(!single){
    // ---- multi-selection ----
    rows.forEach(id=>$(id).style.display='none');
    $('selRot').style.display='none'; $('selDup').style.display='none';
    $('selDel').style.display='';
    const objs=[...selIds].map(objById).filter(Boolean);
    const surf=objs[0] && objs[0].surf;
    $('selTitle').textContent=`${selIds.size} pieces selected` +
      (surf ? (surf.k==='ceil' ? ' · ceiling' : ' · Wall '+String.fromCharCode(65+surf.i)) : '');
    $('selDel').onclick=()=>{ pushUndo(); state.objects=state.objects.filter(q=>!selIds.has(q.id));
      selIds.clear(); refreshObjects(); };
    const sup=$('selSup');
    sup.className='supstat'; sup.textContent='Drag any piece to move the group · arrows nudge';
    return;
  }
  // ---- single selection ----
  const o=single;
  $('selRot').style.display=''; $('selDup').style.display=''; $('selDel').style.display='';
  $('selURow').style.display='flex'; $('selVRow').style.display='flex';
  $('selTitle').textContent=CATALOG[o.type].name + (o.surf.k==='ceil'?' · ceiling':' · Wall '+String.fromCharCode(65+o.surf.i));
  $('selULabel').textContent = o.surf.k==='ceil'?'Position X':'Along wall';
  $('selVLabel').textContent = o.surf.k==='ceil'?'Position Y':'Height (center)';
  const uIn=$('selU'), vIn=$('selV');
  uIn.value=fmtLen(Math.round(o.u*100)/100); vIn.value=fmtLen(Math.round(o.v*100)/100);
  uIn.onchange=()=>{ const val=parseLen(uIn.value); if(isNaN(val)) return;
    pushUndo(); if(inBounds(o,val,o.v)&&!collides(o,val,o.v)){o.u=val;} refreshObjects(); };
  vIn.onchange=()=>{ const val=parseLen(vIn.value); if(isNaN(val)) return;
    pushUndo(); if(inBounds(o,o.u,val)&&!collides(o,o.u,val)){o.v=val;} refreshObjects(); };
  const lenRow=$('selLenRow');
  if(o.type==='two_by_four'){
    lenRow.style.display='flex';
    const li=$('selLen'); li.value=fmtLen(o.len||48);
    li.onchange=()=>{ const val=parseLen(li.value); if(isNaN(val)||val<6) return;
      pushUndo(); o.len=val;
      const p=snapPos(o,o.u,o.v,true); o.u=p.u; o.v=p.v; refreshObjects(); };
  } else lenRow.style.display='none';
  const isRun = o.type==='bar_run';
  $('selSpRow').style.display = isRun?'flex':'none';
  $('selCntRow').style.display = isRun?'flex':'none';
  $('selStrRow').style.display = isRun?'flex':'none';
  if(isRun){
    const sp=$('selSp'), cnt=$('selCnt'), str=$('selStr');
    sp.value=fmtLen(o.spacing); cnt.value=String(o.count); str.value=o.stringers||'none';
    const applyRun=(mut)=>{
      pushUndo(); mut();
      const p=snapPos(o,o.u,o.v,true); o.u=p.u; o.v=p.v;   // clamp back in bounds after resize
      refreshObjects();
    };
    sp.onchange=()=>{ const val=parseLen(sp.value);
      if(isNaN(val)||val<6){ sp.value=fmtLen(o.spacing); toast('Spacing must be at least 6\"'); return; }
      applyRun(()=>{ o.spacing=val; }); };
    cnt.onchange=()=>{ const val=parseInt(cnt.value,10);
      if(isNaN(val)||val<2||val>40){ cnt.value=String(o.count); toast('Bars: 2\u201340'); return; }
      applyRun(()=>{ o.count=val; }); };
    str.onchange=()=> applyRun(()=>{ o.stringers=str.value; });
  }
  $('selRot').onclick=()=>{ pushUndo(); o.rot=rotWrap(o.rot+rotStep(o), o);
    const p=snapPos(o,o.u,o.v,true); o.u=p.u; o.v=p.v; refreshObjects(); };
  $('selDup').onclick=()=> window.dispatchEvent(new KeyboardEvent('keydown',{key:'d'}));
  $('selDel').onclick=()=>{ pushUndo(); state.objects=state.objects.filter(q=>q.id!==o.id);
    selIds.clear(); refreshObjects(); };
  const sup=$('selSup');
  if(o.type==='monkey_bar'||o.type==='two_by_four'||o.type==='bar_run'){
    if(o._unsupported){ sup.className='supstat bad'; sup.textContent='\u26A0 Not landing on framing'; }
    else{ sup.className='supstat ok';
      if(o.type==='two_by_four')
        sup.textContent = o._aligned ? '\u2713 Centered on a stud/joist \u2014 line it up with framing at install'
                                     : `\u2713 Crosses ${o._crossings} studs/joists`;
      else if(o.type==='bar_run')
        sup.textContent = o.stringers==='rails'
          ? (o._railsAligned ? '\u2713 Rails centered on studs/joists \u2014 they must line up with framing at install'
                             : '\u2713 Rails cross the studs/joists')
          : (o.stringers==='per_bar' ? '\u2713 Per-bar stringers land on framing' : '\u2713 All holes land on framing');
      else
        sup.textContent = o._onStringer?'\u2713 Mounted on 2\u00D74 stringer':'\u2713 Holes land on framing';
    }
  } else sup.textContent='';
}

function refreshPanels(){ refreshWarnings(); refreshBom(); refreshSelBar(); }
const objKeyNow=()=> JSON.stringify(state.objects)+'|'+holdsOn()+'|'+(state.settings.holdStyle||'classic')+'|'+(state.settings.holdAdj||0)+'|'+JSON.stringify(state.settings.colors||{});
const roomKeyNow=()=> JSON.stringify({p:state.points,c:state.closed,s:state.settings});
let lastObjKey=null, lastRoomKey=null;
function refreshObjects(){ rebuildObjects(); lastObjKey=objKeyNow(); refreshPanels(); syncHash(); }
function refreshAll(){
  renderWallList(); renderColorRows(); drawSketch();
  if(!$('planView').classList.contains('hidden')){
    buildRoom(); lastRoomKey=roomKeyNow();
    rebuildObjects(); lastObjKey=objKeyNow();
    refreshPanels();
  }
  syncSettingInputs();
  syncRoomSection();
  syncHash();
}

/* =====================================================================
   SHARE LINK, PNG EXPORT, TOP BAR
   ===================================================================== */
function copyText(txt, msg){
  if(navigator.clipboard && navigator.clipboard.writeText)
    navigator.clipboard.writeText(txt).then(()=>toast(msg), ()=>fallbackCopy(txt,msg));
  else fallbackCopy(txt,msg);
}
function fallbackCopy(txt,msg){
  const ta=document.createElement('textarea'); ta.value=txt; document.body.appendChild(ta);
  ta.select(); try{document.execCommand('copy'); toast(msg);}catch(e){toast('Copy failed');}
  ta.remove();
}
function serializeState(){
  return {v:1, settings:state.settings, points:state.points, closed:state.closed,
    wallOpts:state.wallOpts, genStamp:state.genStamp,
    objects:state.objects.map(o=>({id:o.id,type:o.type,surf:o.surf,u:o.u,v:o.v,rot:o.rot,
                                   len:o.len,spacing:o.spacing,count:o.count,stringers:o.stringers,
                                   auto:o.auto||undefined}))};
}
function encodeState(){ return btoa(unescape(encodeURIComponent(JSON.stringify(serializeState())))); }
function syncHash(){
  if(loadingHash) return;
  clearTimeout(hashTimer);
  hashTimer=setTimeout(()=>{
    try{ history.replaceState(null,'','#'+encodeState()); }catch(e){}
  },300);
}
function loadHash(){
  if(!location.hash || location.hash.length<4) return false;
  try{
    const s=JSON.parse(decodeURIComponent(escape(atob(location.hash.slice(1)))));
    if(s.v!==1) return false;
    loadingHash=true;
    state.settings={...state.settings, ...s.settings};
    state.settings.colors={...DEFAULT_COLORS, ...((s.settings&&s.settings.colors)||{})};
    if(!HOLD_STYLES[state.settings.holdStyle]) state.settings.holdStyle='classic';
    state.points=s.points||[]; state.closed=!!s.closed;
    state.wallOpts=s.wallOpts||[];
    state.genStamp=s.genStamp||null;
    state.objects=(s.objects||[]).map(o=>({...o}));
    nextId=Math.max(0,...state.objects.map(o=>o.id||0))+1;
    skDrawing = state.points.length===0;
    loadingHash=false;
    return true;
  }catch(e){ loadingHash=false; return false; }
}
$('btnShare').onclick=()=>{ clearTimeout(hashTimer);
  const enc=encodeState();
  history.replaceState(null,'','#'+enc);
  copyText(location.origin+location.pathname+'#'+enc,'Share link copied'); };

/* ---------------- Save this design ----------------
   Klaviyo's /client/ endpoints are built for browser use and take the PUBLIC
   key (site ID), so no backend is needed. Drop the 6-character public key in
   below and the planner fires a "Saved Playroom Design" event carrying the
   design link; a Flow in Klaviyo triggered on that metric sends the email.

   NEVER put a Klaviyo private key in this file — it ships to the customer.

   With no key set (or if the call fails) it falls back to copying the link and
   opening the customer's own mail client. */
const KLAVIYO_PUBLIC_KEY = 'RFMCv3';
const KLAVIYO_METRIC     = 'Saved Playroom Design';
const KLAVIYO_REVISION   = '2026-07-15';
/* Klaviyo list that the optional "keep me posted" checkbox subscribes people to.
   Find it in Klaviyo: Audience → Lists & Segments → open the list → the ID is in
   the URL and under the list's Settings. Leave blank to disable the opt-in.
   The design email itself does NOT depend on this — it's transactional. */
const KLAVIYO_LIST_ID    = 'Ya6mxS';

function designSummary(){
  const S=state.settings, ws=walls();
  const count=t=>state.objects.filter(o=>o.type===t).length;
  return {
    room_size: ws.map(w=>fmtLen(w.len)).join(' × '),
    ceiling_height: fmtLen(S.ceil),
    youngest_climber_age: S.age,
    panels: state.objects.filter(o=>CATALOG[o.type] && CATALOG[o.type].kind==='panel').length,
    mats: count('mat'),
    estimated_total: ($('bomPrice').textContent||'').replace(/^\D*/,'').trim()
  };
}
async function sendToKlaviyo(email, url){
  const body={ data:{ type:'event', attributes:{
    properties: Object.assign({design_url:url}, designSummary()),
    metric:  { data:{ type:'metric',  attributes:{ name:KLAVIYO_METRIC } } },
    profile: { data:{ type:'profile', attributes:{ email:email } } }
  }}};
  const res=await fetch('https://a.klaviyo.com/client/events?company_id='+
      encodeURIComponent(KLAVIYO_PUBLIC_KEY), {
    method:'POST',
    headers:{'Content-Type':'application/json', 'revision':KLAVIYO_REVISION},
    body:JSON.stringify(body)
  });
  if(!res.ok){
    const detail=await res.text().catch(()=>'');
    throw new Error('Klaviyo '+res.status+' '+detail.slice(0,300));
  }
}

/* Optional marketing opt-in. Only runs when the customer actively ticks the box
   AND a list ID is configured. Best-effort: a failure here never blocks the
   design link (which is transactional and always sends). */
async function klaviyoSubscribe(email){
  if(!KLAVIYO_PUBLIC_KEY || !KLAVIYO_LIST_ID) return;
  const body={ data:{ type:'subscription', attributes:{
    custom_source:'Playroom Planner — Save design',
    profile:{ data:{ type:'profile', attributes:{
      email:email,
      subscriptions:{ email:{ marketing:{ consent:'SUBSCRIBED' } } }
    }}}
  }, relationships:{ list:{ data:{ type:'list', id:KLAVIYO_LIST_ID } } } }};
  try{
    const res=await fetch('https://a.klaviyo.com/client/subscriptions?company_id='+
        encodeURIComponent(KLAVIYO_PUBLIC_KEY), {
      method:'POST',
      headers:{'Content-Type':'application/json', 'revision':KLAVIYO_REVISION},
      body:JSON.stringify(body)
    });
    if(!res.ok){ const d=await res.text().catch(()=>''); console.warn('[Tree Frog Planner] Klaviyo subscribe '+res.status+' '+d.slice(0,200)); }
  }catch(err){ try{ console.warn('[Tree Frog Planner] Klaviyo subscribe failed:', err.message); }catch(_){} }
}
function designUrl(){
  clearTimeout(hashTimer);
  const enc=encodeState();
  history.replaceState(null,'','#'+enc);
  return location.origin+location.pathname+'#'+enc;
}
function validEmail(v){ return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim()); }
function openSaveBox(mode){
  // 'desktop' = the continue-on-a-computer pitch; default = plain save
  if(mode==='desktop'){
    $('saveTitle').textContent='Continue on a computer';
    $('saveDesc').textContent='Enter your email and we’ll send you a link that opens this exact design in the full desktop editor — move, add, and remove every panel, bar, and mat.';
  } else {
    $('saveTitle').textContent='Save this design';
    $('saveDesc').textContent='Pop in your email and we’ll send you a link straight back to this playroom, so you can pick it up later or share it with someone else.';
  }
  $('saveErr').classList.add('hidden');
  if($('saveOptIn')) $('saveOptIn').checked=false;   // never pre-checked; always starts empty
  try{ $('saveEmail').value=localStorage.getItem('tfp_email')||''; }catch(_){}
  $('saveBox').classList.remove('hidden');
  setTimeout(()=>$('saveEmail').focus(), 30);
}
$('btnDesktop').onclick=()=>openSaveBox('desktop');
function closeSaveBox(){ $('saveBox').classList.add('hidden'); }
async function saveDesign(){
  const email=$('saveEmail').value.trim();
  if(!validEmail(email)){ $('saveErr').classList.remove('hidden'); $('saveEmail').focus(); return; }
  $('saveErr').classList.add('hidden');
  try{ localStorage.setItem('tfp_email', email); }catch(_){}
  const url=designUrl();
  // Fire-and-forget marketing opt-in — only if they ticked the box. Never blocks
  // or delays the design link, and does nothing unless a list ID is configured.
  if($('saveOptIn') && $('saveOptIn').checked) klaviyoSubscribe(email);
  if(KLAVIYO_PUBLIC_KEY){
    const go=$('saveGo'); const label=go.textContent;
    go.disabled=true; go.textContent='Sending…';
    try{
      await sendToKlaviyo(email, url);
      closeSaveBox(); toast('Sent — check your inbox');
      return;
    }catch(err){
      try{ console.warn('[Tree Frog Planner] Klaviyo send failed:', err.message); }catch(_){}
      // fall through to the mail-client route so the customer still gets their link
    }finally{
      go.disabled=false; go.textContent=label;
    }
  }
  // No sender configured: hand the link to the customer's own mail app.
  const subject='My Tree Frog playroom design';
  const body='Here is the link back to my playroom design:\n\n'+url+
             '\n\nOpen it any time to keep editing.';
  copyText(url, 'Link copied — opening your email');
  closeSaveBox();
  window.location.href='mailto:'+encodeURIComponent(email)+
    '?subject='+encodeURIComponent(subject)+'&body='+encodeURIComponent(body);
}
$('btnSave').onclick=openSaveBox;
$('saveClose').onclick=closeSaveBox;
$('saveGo').onclick=saveDesign;
$('saveEmail').addEventListener('keydown', e=>{ if(e.key==='Enter') saveDesign(); });
$('saveBox').addEventListener('click', e=>{ if(e.target===$('saveBox')) closeSaveBox(); });

$('btnPng').onclick=()=>{
  if(!glOK){ toast('3D view is not running'); return; }
  if($('planView').classList.contains('hidden')){ toast('Switch to the 3D planner first'); return; }
  const w=canvas.clientWidth, h=canvas.clientHeight;
  renderer.setPixelRatio(1);
  renderer.setSize(w*2,h*2,false); camera.aspect=w/h; camera.updateProjectionMatrix();
  renderer.render(scene,camera);
  const out=document.createElement('canvas'); out.width=w*2; out.height=h*2;
  const cx=out.getContext('2d');
  cx.drawImage(renderer.domElement,0,0,out.width,out.height);
  cx.font='bold 26px sans-serif'; cx.fillStyle='rgba(47,122,51,.85)';
  cx.fillText('Tree Frog Play', 26, out.height-26);
  renderer.setSize(w,h,false); renderer.setPixelRatio(DPR);
  const a=document.createElement('a'); a.download='treefrog-plan.png';
  a.href=out.toDataURL('image/png'); a.click();
  toast('PNG exported');
};

['tglSnap','tglGrid','tglStuds','tglHolds'].forEach(id=>{
  $(id).onclick=()=>{
    $(id).classList.toggle('on');
    if(id==='tglGrid') helperGroup.visible=$(id).classList.contains('on');
    if(id==='tglStuds' && framingGroup) syncFramingVis();
    if(id==='tglHolds') holdsGroup.visible=$(id).classList.contains('on');
  };
});
/* Two separate popovers: Shortcuts = how to drive the tool, Need help = talk to a human.
   Opening one closes the other so they can never overlap. */
$('btnShortcuts').onclick=()=>{
  $('supportPop').classList.add('hidden');
  $('helpPop').classList.toggle('hidden');
};
$('btnHelp').onclick=()=>{
  $('helpPop').classList.add('hidden');
  $('supportPop').classList.toggle('hidden');
};
document.addEventListener('mousedown', e=>{
  for(const [pop,btn] of [['helpPop','btnShortcuts'],['supportPop','btnHelp']]){
    const p=$(pop);
    if(!p.classList.contains('hidden') && !p.contains(e.target) && e.target!==$(btn))
      p.classList.add('hidden');
  }
});
$('ageSel').onchange=()=>{ state.settings.age=+$('ageSel').value; refreshPanels(); syncHash(); };

/* -------- settings inputs -------- */
function bindSetting(id, key, min){
  bindLenInput($(id), ()=>state.settings[key], v=>{
    state.settings[key]=Math.max(min??0, v); refreshAll();
  });
}
bindSetting('inCeil','ceil',48);
bindSetting('inBase','base',0);

function bindInchesInput(input, get, set){
  input.value = fmtInches(get());
  input.addEventListener('change', ()=>{
    const v = parseLen(input.value);
    if(isNaN(v)){ input.value = fmtInches(get()); toast('Enter a length in inches, e.g. 16'); return; }
    set(v); input.value = fmtInches(get());
  });
}
function bindInchesSetting(id, key, min){
  bindInchesInput($(id), ()=>state.settings[key], v=>{
    state.settings[key]=Math.max(min??0, v); refreshAll();
  });
}
bindInchesSetting('inStudOff','studOff',0);
bindInchesSetting('inJoistOff','joistOff',0);
$('inStudSp').onchange=()=>{ const v=parseLen($('inStudSp').value);
  if(isNaN(v)||v<4){ $('inStudSp').value=fmtInches(state.settings.studSp); return; }
  state.settings.studSp=v; $('inStudSp').value=fmtInches(v); refreshAll(); };
$('inJoistSp').onchange=()=>{ const v=parseLen($('inJoistSp').value);
  if(isNaN(v)||v<4){ $('inJoistSp').value=fmtInches(state.settings.joistSp); return; }
  state.settings.joistSp=v; $('inJoistSp').value=fmtInches(v); refreshAll(); };
$('inJoistWall').onchange=()=>{ state.settings.joistWall=+$('inJoistWall').value; state.settings.joistWallExplicit=true; refreshAll(); };

/* -------- Steppers on numeric inputs -------- */
function attachStepper(input, opts){
  if(!input || input.dataset.stepper) return;
  const parent=input.parentElement;
  if(!parent) return;
  input.dataset.stepper='1';
  const wrap=document.createElement('span');
  wrap.className='stepper';
  parent.insertBefore(wrap, input);
  wrap.appendChild(input);
  const arrows=document.createElement('span');
  arrows.className='stpArrows';
  const up=document.createElement('button');
  up.type='button'; up.className='stpBtn'; up.innerHTML='\u25B4'; up.tabIndex=-1;   // ▴
  const dn=document.createElement('button');
  dn.type='button'; dn.className='stpBtn'; dn.innerHTML='\u25BE'; dn.tabIndex=-1;   // ▾
  arrows.appendChild(up); arrows.appendChild(dn);
  wrap.appendChild(arrows);
  const bump=(delta)=>{
    const parse=opts.parse||(v=>parseFloat(v)||0);
    const format=opts.format||(v=>String(v));
    let cur=parse(input.value);
    if(!isFinite(cur)) cur=opts.min!==undefined?opts.min:0;
    // Snap current value to nearest step boundary, then add delta — keeps framing on natural increments
    const step=opts.step;
    let next=Math.round(cur/step)*step + delta;
    if(opts.min!==undefined) next=Math.max(opts.min,next);
    if(opts.max!==undefined) next=Math.min(opts.max,next);
    input.value=format(next);
    input.dispatchEvent(new Event('change',{bubbles:true}));
  };
  dn.addEventListener('click', e=>{ e.preventDefault(); bump(-opts.step); });
  up.addEventListener('click', e=>{ e.preventDefault(); bump(+opts.step); });
}
const _lenOpts=(step,min,max)=>({step, min, max, parse:parseLen, format:fmtLen});
const _inOpts =(step,min,max)=>({step, min, max, parse:parseLen, format:fmtInches});
attachStepper($('ageSel'),      {step:1, min:2, max:8, parse:v=>parseInt(v,10)||6, format:v=>String(Math.round(v))});
attachStepper($('inStudSp'),    _inOpts (8,    8,   48));
attachStepper($('inStudOff'),   _inOpts (0.5,  0,   24));
attachStepper($('inJoistSp'),   _inOpts (8,    8,   48));
attachStepper($('inJoistOff'),  _inOpts (0.5,  0,   24));
attachStepper($('wcAge'),       {step:1, min:2, max:8, parse:v=>parseInt(v,10)||6, format:v=>String(Math.round(v))});
attachStepper($('wcCeil'),      _lenOpts(1,   48,  240));
attachStepper($('wcBase'),      _lenOpts(0.25, 0,   24));
attachStepper($('selSp'),       _lenOpts(1,    6,   60));
attachStepper($('selLen'),      _lenOpts(6,   12,  600));
attachStepper($('selCnt'),      {step:1, min:2, max:40, parse:v=>parseInt(v,10)||2, format:v=>String(Math.round(v))});

/* -------- view switching -------- */
function showPlan(){
  if(!glOK && !tryInitGL(false)){
    toast('3D unavailable — close other 3D tabs or restart your browser, then try again');
    return;
  }
  if(!walls().length){ toast('Sketch at least one wall first'); return; }
  $('sketchView').classList.add('hidden'); $('planView').classList.remove('hidden');
  $('segSketch').classList.remove('on'); $('segPlan').classList.add('on');
  try{
    autoPopulate();
    const rk=roomKeyNow(), ok=objKeyNow();
    if(rk!==lastRoomKey){ buildRoom(); lastRoomKey=rk; }
    if(ok!==lastObjKey){ rebuildObjects(); lastObjKey=ok; }
    refreshPanels(); syncHash(); syncRoomSection();
  }catch(err){
    if(window.__banner) window.__banner('Build room error: '+err.message+'\n'+(err.stack||'').split('\n').slice(0,3).join('\n'));
    throw err;
  }
  requestAnimationFrame(()=>{ resize();
    if(!showPlan._framed){ frameRoom(); showPlan._framed=true; } });
  wizUpdateTopbar();
}
function showSketch(){
  // A suspended wizard is coming back — only a manual jump to 2D ends it.
  if(wiz.active && !wiz.suspended) wizFinish(false);
  $('planView').classList.add('hidden'); $('sketchView').classList.remove('hidden');
  $('segPlan').classList.remove('on'); $('segSketch').classList.add('on');
  setPlacing(null);
  renderWallList(); drawSketch();
  $('wizResumeBar').classList.toggle('hidden', !wiz.suspended);
  wizUpdateTopbar();
}
$('segPlan').onclick=()=>{ if(wiz.suspended) wizResumeFrom2D(); else showPlan(); };
$('segSketch').onclick=showSketch;
$('backToSketch').onclick=showSketch;
$('secRoom').addEventListener('toggle', ()=>{
  if(wiz.active) return;   // wizard steps manage guides themselves
  if($('secRoom').open && !$('planView').classList.contains('hidden')) showGuides('walls', 2600);
});

/* =====================================================================
   ROOM SECTION (3D sidebar) + HOLD DENSITY + GUIDED SETUP WIZARD + REVIEW
   ===================================================================== */
function clampHoldAdj(){
  const panels=state.objects.filter(o=>CATALOG[o.type] && CATALOG[o.type].kind==='panel');
  if(!panels.length){ state.settings.holdAdj=0; return; }
  const def=panels.reduce((s,o)=>s+(CATALOG[o.type].holds||0),0);
  const cap=panels.reduce((s,o)=>s+holdCap(o),0);
  const minAdj=Math.ceil((10-def)/10), maxAdj=Math.floor((cap-def)/10);
  state.settings.holdAdj=Math.max(minAdj, Math.min(maxAdj, state.settings.holdAdj||0));
}
function updateHoldRow(){
  const row=$('holdRow'); if(!row) return;
  const hasPanels=state.objects.some(o=>CATALOG[o.type] && CATALOG[o.type].kind==='panel');
  row.classList.toggle('hidden', !hasPanels);
  if(!hasPanels) return;
  const t=holdTargets(), sets=Math.ceil(t.total/10);
  $('holdInfo').textContent = t.total+' holds \u00b7 sold as '+sets+' set'+(sets===1?'':'s')+' of 10';
}
function holdAdjBy(d){
  state.settings.holdAdj=(state.settings.holdAdj||0)+d;
  clampHoldAdj();
  rebuildHolds(); lastObjKey=objKeyNow();
  refreshPanels(); syncHash();
}
/* holdMinus/holdPlus lived in the old Products section; the Build section's
   rendered controls call holdAdjBy directly now. */

/* ---- Room section: age / ceiling / baseboard / wall lengths in the 3D view ---- */
function wizRefresh(){
  try{
    autoPopulate();
    const rk=roomKeyNow(); if(rk!==lastRoomKey){ buildRoom(); lastRoomKey=rk; }
    const ok=objKeyNow(); if(ok!==lastObjKey){ rebuildObjects(); lastObjKey=ok; }
    refreshPanels(); syncHash();
  }catch(e){ try{console.error('refresh error', e);}catch(_){} }
}
function renderRmWallList(){
  const box=$('rmWallList'); if(!box) return;
  const ws=walls(); box.innerHTML='';
  if(state.closed){
    box.innerHTML='<p class="hint">This room is a closed shape \u2014 adjust wall lengths in the 2D editor below.</p>';
    return;
  }
  ws.forEach(w=>{
    const row=document.createElement('div'); row.className='field';
    row.dataset.wall=w.i;
    const lab=document.createElement('label'); lab.textContent='Wall '+String.fromCharCode(65+w.i);
    row.appendChild(lab);
    const lenId='rmWall'+w.i;
    row.insertAdjacentHTML('beforeend', ftInMarkup(lenId, 0, 50, 1));
    box.appendChild(row);
    // wire after attach — wireFtIn looks the selects up by id, so they must be in the document
    wireFtIn(lenId, ()=>walls()[w.i].len, len=>{
      pushUndo(); setWallLength(w.i, Math.max(24, Math.min(600, len)));
      afterSketchChange(); wizRefresh(); syncRoomSection();
    }, 24, 600, 1);
  });
}
function syncRoomSection(){
  const S=state.settings;
  if(!$('rmAge')) return;
  $('rmAge').value=String(S.age);
  $('rmCeil').value=fmtLen(S.ceil);
  $('rmBase').value=fmtLen(S.base);
  syncFtInPair('rmCeil', S.ceil, 1);
  syncFtInPair('rmBase', S.base, 0.5);
  renderRmWallList();                       // recreates the wall rows…
  if(isMob()) mobEnsureStrip('secRoom');    // …so the strip must re-sync after
}
function feedCanonical(id, val){ const el=$(id); if(el){ el.value=val; el.dispatchEvent(new Event('change')); } }
if($('rmAge')){
  attachStepper($('rmAge'), {step:1, min:2, max:8, parse:v=>parseInt(v,10)||6, format:v=>String(Math.round(v))});
  $('rmAge').addEventListener('change', ()=>{
    const v=Math.max(2, Math.min(8, parseInt($('rmAge').value,10)||6));
    $('rmAge').value=String(v);
    state.settings.age=v;
    if($('ageSel')) $('ageSel').value=String(v);
    wizRefresh();
  });
  bindLenInput($('rmCeil'), ()=>state.settings.ceil, v=>{ feedCanonical('inCeil', fmtLen(Math.max(48, Math.min(240, v)))); wizRefresh(); });
  bindLenInput($('rmBase'), ()=>state.settings.base, v=>{ feedCanonical('inBase', fmtLen(Math.max(0, Math.min(24, v)))); wizRefresh(); });
}
/* Focus mode helpers — only the section under discussion stays bright and clickable. */
function collapseRails(){
  document.querySelectorAll('#palette details.railSec').forEach(d=>{ d.open=false; });
  if(isMob()) mobShowSheet(null);
}
function collapseRightSections(){
  document.querySelectorAll('#rightPanel details.rightSec').forEach(d=>{ d.open=false; });
}
function wizFocusSet(secId){
  document.querySelectorAll('#palette details.railSec').forEach(d=>d.classList.remove('wizLive'));
  if(secId && $(secId)) $(secId).classList.add('wizLive');
}
function wizFocusOff(){
  document.body.classList.remove('wizFocus');
  wizFocusSet(null);
  const w=$('warnBox'); if(w) w.open=true;      // checks matter again once they're designing
}
function openRail(id){
  document.querySelectorAll('#palette details.railSec').forEach(d=>{ d.open=(d.id===id); });
  // phones: sections live in a bottom sheet, so opening one shows the sheet
  if(isMob()){
    mobShowSheet(id==='secProducts' ? null : 'palette');
    mobEnsureStrip(id);
  }
  if(id==='secRoom' && !wiz.active && !$('planView').classList.contains('hidden')) showGuides('walls', 2600);
}

/* =====================================================================
   MOBILE (max-width:760px) — configurator-first layout.
   The wizard, viewer, colors, room and shopping list all work; free-form
   product placement is desktop-only and gets a handoff prompt instead.
   ===================================================================== */
const MOBQ=window.matchMedia('(max-width:760px)');
let mobTab=null;                    // declared before mobSync() runs below — TDZ
function isMob(){ return document.body.classList.contains('mob'); }
function mobSync(){
  document.body.classList.toggle('mob', MOBQ.matches);
  mobPlaceBom();
  $('btnReview').textContent = MOBQ.matches ? 'Review →' : 'Review my playroom →';
  // phones render clean: no stud/joist centerlines (toggle lives on desktop only)
  $('tglStuds').classList.toggle('on', !MOBQ.matches);
  try{ syncFramingVis(); }catch(_){}
  if(!MOBQ.matches){ mobShowSheet(null); $('mobMenu').classList.add('hidden'); }
}
MOBQ.addEventListener('change', mobSync);
window.addEventListener('resize', mobSync);   // some embedded browsers miss matchMedia change events
mobSync();

function mobShowSheet(which){          // 'palette' | 'right' | 'build' | null
  $('palette').classList.toggle('mobOpen', which==='palette');
  $('rightPanel').classList.toggle('mobOpen', which==='right');
  $('mobBuild').classList.toggle('mobOpen', which==='build');
  if(which===null){
    mobTab=null;
    document.querySelectorAll('#mobTabs button').forEach(b=>b.classList.remove('on'));
  }
}
function mobOpenTab(name){
  if(mobTab===name){ mobShowSheet(null); return; }   // tap the active tab to close
  mobTab=name;
  const map={colors:'secColors', room:'secRoom'};
  if(name==='cart'){ mobTab=null; $('btnCart').click(); return; }   // action, not a sheet
  if(name==='build'){
    mobBuildMenu();
    mobShowSheet('build');
  } else if(name==='list'){
    $('warnBox').open=true; $('bomBox').open=true;
    mobShowSheet('right');
  } else {
    document.querySelectorAll('#palette details.railSec').forEach(d=>{ d.open=(d.id===map[name]); });
    mobShowSheet('palette');
    if(name==='room'){ syncRoomSection(); if(!$('planView').classList.contains('hidden')) showGuides('walls', 2600); }
    mobEnsureStrip(map[name]);
  }
  document.querySelectorAll('#mobTabs button').forEach(b=>b.classList.toggle('on', b.dataset.tab===name));
}

/* ---- category strip: one horizontal row of swatch chips; tapping one reveals
   just that category's options above it, so the sheet stays low ---- */
const mobLiteActive={};              // section id -> label of the open category
/* Strips and swatch rows are rebuilt after every selection, which resets their
   horizontal scroll to 0 and makes the user lose their place. Track scroll
   continuously (capture phase — scroll doesn't bubble) and restore on rebuild. */
const mobScroll={};                  // section id -> {strip, row}
function mobTrackScroll(secId){
  $(secId).addEventListener('scroll', e=>{
    if(!isMob() || !e.target.classList) return;
    const S=mobScroll[secId] || (mobScroll[secId]={});
    if(e.target.classList.contains('mobStrip')) S.strip=e.target.scrollLeft;
    else if(e.target.classList.contains('chips')) S.row=e.target.scrollLeft;
  }, true);
}
mobTrackScroll('secColors');
mobTrackScroll('secRoom');
function mobRowsFor(secId){
  return secId==='secColors'
    ? [...document.querySelectorAll('#colorRows .colorRow')]
    : [...document.querySelectorAll('#secRoom .railBody .field')];
}
const MOB_SHORT={'Youngest climber age':'Age','Ceiling height':'Ceiling','Baseboard height':'Baseboard'};
function mobDescribe(secId, row){
  const labEl=row.querySelector('label');
  const label=(labEl?labEl.textContent:'').trim();
  let swHtml='', val='';
  if(secId==='secColors'){
    const on=row.querySelector('.chip.on');
    if(on){ swHtml='<span class="'+on.className.replace(' on','')+'" style="'+(on.getAttribute('style')||'')+'"></span>'; }
    else { const t=row.querySelector('.chipTxt.on'); if(t) val=t.textContent.trim(); }
  } else {
    val=[...row.querySelectorAll('select,input')]
      .filter(e=>e.type!=='hidden')
      .map(e=> e.tagName==='SELECT' ? (e.selectedOptions[0]?e.selectedOptions[0].text:e.value) : e.value)
      .join(' ').trim();
  }
  return {label, short:MOB_SHORT[label]||label, swHtml, val};
}
function mobEnsureStrip(secId){
  if(!isMob() || (secId!=='secColors' && secId!=='secRoom')) return;
  const sec=$(secId); if(!sec) return;
  const body=sec.querySelector('.railBody'); if(!body) return;
  const rows=mobRowsFor(secId);
  rows.forEach(r=>r.classList.toggle('mobActive',
    mobLiteActive[secId]!==undefined && mobDescribe(secId,r).label===mobLiteActive[secId]));
  const old=body.querySelector('.mobStrip'); if(old) old.remove();
  const strip=document.createElement('div'); strip.className='mobStrip';
  rows.forEach(row=>{
    const d=mobDescribe(secId,row);
    const b=document.createElement('button'); b.type='button'; b.className='mobStripBtn';
    b.innerHTML=d.swHtml+'<span class="lb">'+d.short+'</span>'+
      (d.val?'<span class="vl">'+d.val+'</span>':'')+'<span class="ar">▾</span>';
    if(row.classList.contains('mobActive')) b.classList.add('on');
    b.onclick=()=>{
      const was=row.classList.contains('mobActive');
      if(!was && mobScroll[secId]) mobScroll[secId].row=0;   // a new category opens at its left edge
      mobLiteActive[secId]=was ? undefined : d.label;
      mobEnsureStrip(secId);
    };
    strip.appendChild(b);
  });
  body.appendChild(strip);
  // rebuilt DOM starts at scroll 0 — put the user back where they were
  const S=mobScroll[secId]||{};
  if(S.strip) strip.scrollLeft=S.strip;
  const act=rows.find(r=>r.classList.contains('mobActive'));
  if(act && S.row){ const ch=act.querySelector('.chips'); if(ch) ch.scrollLeft=S.row; }
}
// chip clicks rebuild #colorRows from scratch — restore the strip afterwards
$('secColors').addEventListener('click', e=>{
  if(!isMob() || e.target.closest('.mobStrip')) return;
  setTimeout(()=>mobEnsureStrip('secColors'), 0);
});

/* ---- Build section (desktop rail): inline controls above the place-by-hand
   products. Same actions as the wizard steps, rendered as compact rows. ---- */
function bRow(host, opts){            // {minus, plus, label, sub, onMinus, onPlus, minDis, plusDis}
  const row=document.createElement('div'); row.className='wsRow';
  const mk=(txt,dis,fn)=>{ const b=document.createElement('button'); b.type='button';
    b.className='wsBtn2'; b.textContent=txt; b.disabled=!!dis; b.onclick=fn; return b; };
  row.appendChild(mk('−', opts.minDis, opts.onMinus));
  const lbl=document.createElement('span'); lbl.className='wsLbl';
  lbl.innerHTML=opts.label+(opts.sub?'<small>'+opts.sub+'</small>':'');
  row.appendChild(lbl);
  row.appendChild(mk('+', opts.plusDis, opts.onPlus));
  host.appendChild(row);
}
function bToggle(host, on, onChange){
  const b=document.createElement('button'); b.type='button';
  b.className='bTgl'+(on?' on':'');
  b.textContent=on?'On':'Off';
  b.onclick=()=>onChange(!on);
  host.appendChild(b);
}
function renderBuildControls(){
  const wallHost=$('bWallCtl'); if(!wallHost) return;
  const rerender=()=>renderBuildControls();
  // climbing wall: width / height rows, or an add button when it's off
  wallHost.innerHTML='';
  const i=wallSizeInfo();
  if(i && i.on){
    bRow(wallHost, {label:i.cols+' wide', minDis:i.cols<=1, plusDis:i.cols>=i.maxCols,
      onMinus:()=>{ wallSizeAdj(-1,0); rerender(); }, onPlus:()=>{ wallSizeAdj(+1,0); rerender(); }});
    bRow(wallHost, {label:i.rows+' tall', minDis:i.rows<=1, plusDis:i.rows>=i.maxRows,
      onMinus:()=>{ wallSizeAdj(0,-1); rerender(); }, onPlus:()=>{ wallSizeAdj(0,+1); rerender(); }});
    const rm=document.createElement('button'); rm.type='button'; rm.className='linkChoice';
    rm.textContent='Remove the climbing wall';
    rm.onclick=()=>{ setWallOpt(1,'p',false); rerender(); };
    wallHost.appendChild(rm);
  } else if(i){
    const add=document.createElement('button'); add.type='button'; add.className='wizChoice centered';
    add.textContent='Add a climbing wall';
    add.onclick=()=>{ setWallOpt(1,'p',true); rerender(); };
    wallHost.appendChild(add);
  }
  // monkey bars + ladder: plain on/off
  const barsHost=$('bBarsCtl'); barsHost.innerHTML='';
  bToggle(barsHost, !!wallOpt(1).t, on=>{ setWallOpt(1,'t',on); rerender(); });
  const ladHost=$('bLadderCtl'); ladHost.innerHTML='';
  bToggle(ladHost, !!wallOpt(2).s, on=>{ setWallOpt(2,'s',on); rerender(); });
  // mats: count row
  const matsHost=$('bMatsCtl'); matsHost.innerHTML='';
  const mats=state.objects.filter(o=>o.type==='mat').length;
  bRow(matsHost, {label:mats+' mat'+(mats===1?'':'s'), minDis:mats<=0,
    onMinus:()=>{ matsAdj(-1); rerender(); }, onPlus:()=>{ matsAdj(+1); rerender(); }});
  // holds: style chips + count row
  const holdsHost=$('bHoldsCtl'); holdsHost.innerHTML='';
  const styleRow=document.createElement('div'); styleRow.className='chips bStyleRow';
  const cur=state.settings.holdStyle||'classic';
  for(const [k,cfg] of Object.entries(HOLD_STYLES)){
    const sb=document.createElement('button'); sb.type='button'; sb.className='chipTxt';
    sb.textContent=cfg.name;
    if(k===cur) sb.classList.add('on');
    if(prodSoldOut(TFP_CATALOG.holds[k].handle)){
      sb.classList.add('soldOut'); sb.title=cfg.name+' — out of stock';
      sb.onclick=()=>toast(cfg.name+' holds are out of stock');
    } else {
      sb.onclick=()=>{
        state.settings.holdStyle=k; state.settings.colors.hold=HOLD_STYLES[k].def;
        renderColorRows(); syncHash();
        if(!$('planView').classList.contains('hidden')){ buildRoom(); lastRoomKey=roomKeyNow(); refreshObjects(); }
        rerender();
      };
    }
    styleRow.appendChild(sb);
  }
  holdsHost.appendChild(styleRow);
  const ht=holdTargets();
  if(ht.total>0){
    bRow(holdsHost, {label:ht.total+' holds', sub:Math.ceil(ht.total/10)+' sets of 10',
      minDis:ht.total<=10, plusDis:ht.total>=ht.cap,
      onMinus:()=>{ holdAdjBy(-1); rerender(); }, onPlus:()=>{ holdAdjBy(+1); rerender(); }});
  }
}
/* The shopping list lives in the left rail on desktop and in the right sheet
   on phones — one DOM node, moved between homes on breakpoint change. */
function mobPlaceBom(){
  const bom=$('bomBox'); if(!bom) return;
  if(MOBQ.matches){
    if(bom.parentElement!==$('rightScroll')) $('rightScroll').appendChild(bom);
  } else {
    if(bom.parentElement!==$('secListBody')){ $('secListBody').appendChild(bom); bom.open=true; }
  }
}

/* ---- Build tab: reopen the wizard's adjustment panels on demand ----
   Each chip shows the current state and opens the SAME step card the wizard
   used (wall sizing, bars, ladder, mats) in adjust mode: its nav collapses to
   a Done that returns here instead of continuing the wizard flow. */
function mobBuildMenu(){
  const strip=$('mobBuildStrip'); strip.innerHTML='';
  const i=wallSizeInfo();
  const mats=state.objects.filter(o=>o.type==='mat').length;
  const items=[
    {step:wizStepWallSize, label:'Climbing wall', val:(i&&i.on)?(i.cols+'×'+i.rows):'None'},
    {step:wizStepBars,     label:'Monkey bars',   val:wallOpt(1).t?'On':'Off'},
    {step:wizStepLadder,   label:'Ladder',        val:wallOpt(2).s?'On':'Off'},
    {step:wizStepMats,     label:'Mats',          val:String(mats)}
  ];
  for(const it of items){
    const b=document.createElement('button'); b.type='button'; b.className='mobStripBtn';
    b.innerHTML='<span class="lb">'+it.label+'</span><span class="vl">'+it.val+'</span><span class="ar">▾</span>';
    b.onclick=()=>mobAdjustOpen(it.step);
    strip.appendChild(b);
  }
}
function mobAdjustOpen(stepFn){
  wiz.adjust=true;
  stepFn();
}
function mobAdjustBack(){
  wiz.adjust=false; wiz.step=null; wizPersist();
  wizCardHide(); wizDimOff(); guideOff(); wizFocusOff();
  mobBuildMenu();               // values changed — refresh the chips
  mobShowSheet('build');
  document.querySelectorAll('#mobTabs button').forEach(b=>b.classList.toggle('on', b.dataset.tab==='build'));
  mobTab='build';
}
document.querySelectorAll('#mobTabs button').forEach(b=> b.onclick=()=>mobOpenTab(b.dataset.tab));
$('mobMenuBtn').onclick=()=> $('mobMenu').classList.toggle('hidden');
document.querySelectorAll('#mobMenu button').forEach(b=> b.addEventListener('click', ()=>{
  $(b.dataset.proxy).click();
  $('mobMenu').classList.add('hidden');
}));
document.addEventListener('mousedown', e=>{
  const m=$('mobMenu');
  if(!m.classList.contains('hidden') && !m.contains(e.target) && e.target!==$('mobMenuBtn'))
    m.classList.add('hidden');
});

/* ---- Guided setup wizard ---- */
const wiz={active:false, step:null};
function buildDefaultRoom(){
  state.points=[{x:0,z:96},{x:0,z:0},{x:144,z:0},{x:144,z:96}];
  state.closed=false;
  state.settings.ceil=96;
  state.wallOpts=[{p:false,t:false,s:false},{p:true,t:true,s:false},{p:false,t:false,s:true}];
  state.objects=[]; state.genStamp=null;
  skDrawing=false;
}
function applyAgeGate(){
  if(!state.wallOpts[1]) return;
  const bars=state.settings.age>3;
  state.wallOpts[1].t=bars;
  if(state.wallOpts[2]) state.wallOpts[2].s=bars;
  state.genStamp=null;
}
function wizCamToWall(i, zoom){
  const w=walls()[i]; if(!w || typeof camera==='undefined') return;
  const S=state.settings, mid={x:(w.a.x+w.b.x)/2, z:(w.a.z+w.b.z)/2};
  // Measure the LIVE canvas every time. camera.aspect can be stale between wizard
  // steps (a resize() only fires when the pixel buffer changes), which made the fit
  // differ between a fresh reload and clicking Next. Reading clientWidth/Height here
  // — and syncing the camera to it — keeps every entry point framed identically.
  const cw=canvas.clientWidth, ch=canvas.clientHeight;
  const aspect=(cw>0 && ch>0) ? cw/ch : camera.aspect;
  // The opening steps use an angled, elevated 3/4 "hero" view — swung to one side
  // and lifted so both side walls show and the room reads as a real 3D space —
  // rather than a flat head-on shot. Restore the plain framing everywhere else.
  // NOTE: gate on !isMob() (real phone), NOT aspect — a tall desktop window makes
  // the viewport portrait too, and gating on aspect wrongly sent it to phone framing.
  // Every builder step keeps this same view; only the placement tutorial (tut),
  // the tour, and free design drop back to the straight-on framing.
  const fitRoom = wiz.active && /^(wall|ceil|base|fork|roomAdjust|wallsize|bars|ladder|mats)$/.test(wiz.step||'') && !isMob();
  const wantFov = fitRoom ? 46 : 50;
  if(camera.fov!==wantFov || Math.abs(camera.aspect-aspect)>0.001){
    camera.fov=wantFov; camera.aspect=aspect; camera.updateProjectionMatrix();
  }
  const tanV=Math.tan(camera.fov*Math.PI/360);
  if(fitRoom){
    // Fit floor->ceiling and the wall width, then back off extra for the oblique angle.
    const dH=(S.ceil/2)/tanV, dW=(w.len/2)/(aspect*tanV);
    const d=Math.max(dH, dW)*1.9;                    // backed off so the whole room reads
    // Swing the camera ~26° off the wall normal, along the wall, for depth.
    // Minus = viewed from the LEFT (mirrored per feedback).
    const tx=w.b.x-w.a.x, tz=w.b.z-w.a.z, tl=Math.hypot(tx,tz)||1;
    const ux=tx/tl, uz=tz/tl, ang=26*Math.PI/180, ca=Math.cos(ang), sa=Math.sin(ang);
    const dirx=w.n.x*ca-ux*sa, dirz=w.n.z*ca-uz*sa;
    camera.position.set(mid.x+dirx*d, S.ceil*0.80, mid.z+dirz*d);   // lifted, looking down in
    controls.target.set(mid.x, S.ceil*0.42, mid.z);
    controls.update();
    return;
  }
  let z=(zoom||1.5);
  if(aspect<1 && isMob()) z=Math.min(z, 1.6);
  let d=Math.max(w.len, S.ceil)*z;
  if(aspect<1) d*=Math.min(2.2, 1/(aspect*0.95));   // width-fit: geometry, any narrow viewport
  camera.position.set(mid.x+w.n.x*d, S.ceil*.55, mid.z+w.n.z*d);
  controls.target.set(mid.x, S.ceil*.5, mid.z);
  if(aspect<1 && isMob()){
    // phones only: aim lower so the room sits high above the bottom sheet.
    // (A tall DESKTOP window must keep the level aim or the ceiling clips.)
    const vis=2*d*tanV;
    controls.target.y-=vis*0.26;
  }
  controls.update();
}
let wizDimGrp=null, wizDimMat=null, wizDimT=null;
function wizDimOff(){
  if(wizDimT){ clearInterval(wizDimT); wizDimT=null; }
  if(wizDimGrp){
    scene.remove(wizDimGrp);
    wizDimGrp.traverse(o=>{ if(o.geometry) o.geometry.dispose(); });
    if(wizDimMat) wizDimMat.dispose();
    wizDimGrp=null; wizDimMat=null;
  }
}
/* Big pulsing double-headed arrow showing exactly which dimension we're asking for.
   mode 'len' = wall-to-wall across the middle of the wall; 'height' = floor to ceiling. */
function wizDim(i, mode){
  wizDimOff();
  const w=walls()[i]; if(!w || typeof scene==='undefined') return;
  const S=state.settings;
  let a,b;
  if(mode==='height'){
    const mx=(w.a.x+w.b.x)/2+w.n.x*16, mz=(w.a.z+w.b.z)/2+w.n.z*16;
    a=new THREE.Vector3(mx, 1.5, mz); b=new THREE.Vector3(mx, S.ceil-1.5, mz);
  }else{
    const off=3, pad=2, y=S.ceil*.5;
    a=new THREE.Vector3(w.a.x+w.n.x*off+w.dir.x*pad, y, w.a.z+w.n.z*off+w.dir.z*pad);
    b=new THREE.Vector3(w.b.x+w.n.x*off-w.dir.x*pad, y, w.b.z+w.n.z*off-w.dir.z*pad);
  }
  const dir=new THREE.Vector3().subVectors(b,a), len=dir.length(); dir.normalize();
  const rad=1.4, headLen=Math.min(14, len*.18), headRad=rad*3, up=new THREE.Vector3(0,1,0);
  wizDimMat=new THREE.MeshBasicMaterial({color:0x3f9142, transparent:true, opacity:.95, depthTest:false});
  const grp=new THREE.Group();
  const shaft=new THREE.Mesh(new THREE.CylinderGeometry(rad,rad,Math.max(1,len-headLen*2),16), wizDimMat);
  shaft.position.copy(a).addScaledVector(dir, len/2);
  shaft.quaternion.setFromUnitVectors(up, dir);
  const coneGeo=new THREE.ConeGeometry(headRad, headLen, 24);
  const tipB=new THREE.Mesh(coneGeo, wizDimMat);
  tipB.position.copy(b).addScaledVector(dir, -headLen/2);
  tipB.quaternion.setFromUnitVectors(up, dir);
  const tipA=new THREE.Mesh(coneGeo, wizDimMat);
  tipA.position.copy(a).addScaledVector(dir, headLen/2);
  tipA.quaternion.setFromUnitVectors(up, dir.clone().negate());
  grp.add(shaft, tipA, tipB);
  grp.children.forEach(m=>m.renderOrder=999);   // draw on top so it's never hidden by the wall
  wizDimGrp=grp; scene.add(grp);
  wizDimT=setInterval(()=>{ if(wizDimMat) wizDimMat.opacity=wizPulseAlpha(); }, 50);
}
/* One clock drives both the 3D arrow and the card's ring, so they pulse in phase.
   Matches the arrow's original cadence (t += .14 every 50ms => 2.8 rad/sec). */
function wizPulseAlpha(){ return .45 + .5*Math.abs(Math.sin(performance.now()/357)); }
function updateWizCardPulse(){
  const c=$('wizCard');
  if(!c || c.classList.contains('hidden') || !c.classList.contains('wizGlow')) return;
  const ring=isMob()?5:10;   // fat ring reads as chrome on a phone
  c.style.boxShadow='0 10px 30px rgba(0,0,0,.22), 0 0 0 '+ring+'px rgba(63,145,66,'+wizPulseAlpha().toFixed(3)+')';
}
/* Own timer rather than the render loop: the ring must still pulse if WebGL
   never starts, and 50ms matches the arrow's cadence so they stay in lockstep. */
let wizPulseT=null;
function wizPulseStart(){ if(!wizPulseT) wizPulseT=setInterval(updateWizCardPulse, 50); }
function wizPulseStop(){ if(wizPulseT){ clearInterval(wizPulseT); wizPulseT=null; } }
/* Decision steps only — tour stops are counted separately so the total stays honest. */
const WIZ_ORDER=['age','wall','ceil','base','fork','wallsize','bars','ladder','mats'];
function wizStepLabel(key){
  const i=WIZ_ORDER.indexOf(key);
  return i<0 ? '' : 'Step '+(i+1)+' of '+WIZ_ORDER.length;
}
function wizFootLabel(){
  if(wiz.adjust) return 'Adjust';
  if(wiz.step==='handoff') return 'One last thing';
  if(wiz.step==='tut') return 'Quick tutorial';
  if(wiz.step==='tour') return isMob() ? 'Quick tip' : 'Tip '+((wiz.tourN||0)+1)+' of '+WIZ_TOUR.length;
  if(wiz.step==='roomAdjust') return wizStepLabel('fork');
  return wizStepLabel(wiz.step);
}
/* 'center' = under the dimension arrow over the 3D viewport; 'rail' = beside the
   highlighted left-rail section during the tour. */
function wizCardPos(mode, anchorEl, frac){
  const card=$('wizCard'); if(!card || card.classList.contains('hidden')) return;
  if(window.innerWidth<=760) return;                    // mobile CSS pins it to the bottom
  if(wiz.cardDragMoved) return;                         // they dragged it — leave it until the next step
  const h=card.offsetHeight||200, pad=12;
  if(mode==='rail' && anchorEl){
    const a=anchorEl.getBoundingClientRect();
    card.style.transform='none';
    card.style.bottom='auto';
    card.style.left=Math.round(a.right+16)+'px';
    card.style.top=Math.round(Math.max(pad+52, Math.min(window.innerHeight-h-pad, a.top)))+'px';
    return;
  }
  if(mode==='bottom'){
    // one fixed low spot, centred on the viewport — anchored by CSS bottom so the
    // edge stays locked no matter the card's height (no bounce between steps)
    const vpb=$('viewport');
    const rb=vpb ? vpb.getBoundingClientRect() : {left:0, width:window.innerWidth};
    card.style.transform='translateX(-50%)';
    card.style.top='auto';
    card.style.bottom='26px';
    card.style.left=Math.round(rb.left+rb.width/2)+'px';
    return;
  }
  const vp=$('viewport');
  const r=vp ? vp.getBoundingClientRect()
             : {left:0, width:window.innerWidth, top:0, height:window.innerHeight};
  card.style.transform='translateX(-50%)';
  card.style.bottom='auto';
  card.style.left=Math.round(r.left+r.width/2)+'px';
  card.style.top=Math.round(Math.min(r.top+r.height*(frac||0.58), window.innerHeight-h-pad))+'px';
}
/* Drag the wizard card anywhere during a step (desktop only — phones pin it
   to the bottom). Grabbing a button/input still works normally. */
(function(){
  const card=$('wizCard'); if(!card) return;
  let sx=0, sy=0, ox=0, oy=0, moved=false, dragging=false;
  card.addEventListener('pointerdown', e=>{
    if(isMob() || e.button!==0) return;
    if(e.target.closest('button,input,select,textarea,a,label')) return;
    const r=card.getBoundingClientRect();
    sx=e.clientX; sy=e.clientY; ox=r.left; oy=r.top;
    dragging=true; moved=false;
    card.setPointerCapture(e.pointerId);
    card.classList.add('wizDragging');
    e.preventDefault();                    // no text selection mid-drag
  });
  card.addEventListener('pointermove', e=>{
    if(!dragging) return;
    const dx=e.clientX-sx, dy=e.clientY-sy;
    if(!moved && Math.abs(dx)<3 && Math.abs(dy)<3) return;   // a plain click isn't a drag
    moved=true; wiz.cardDragMoved=true;
    const pad=8, w=card.offsetWidth, h=card.offsetHeight;
    card.style.transform='none';
    card.style.bottom='auto';               // switch off bottom-anchoring while dragging
    card.style.left=Math.round(Math.max(pad, Math.min(window.innerWidth -w-pad, ox+dx)))+'px';
    card.style.top =Math.round(Math.max(pad, Math.min(window.innerHeight-h-pad, oy+dy)))+'px';
  });
  const endDrag=()=>{ if(!dragging) return; dragging=false; card.classList.remove('wizDragging'); };
  card.addEventListener('pointerup', endDrag);
  card.addEventListener('pointercancel', endDrag);
})();
/* Remember where the customer is in the wizard so an accidental refresh
   resumes the step instead of dumping them into the full editor.
   Adjust-mode tweaks (post-wizard) are deliberately not remembered. */
function wizPersist(){
  try{ sessionStorage.setItem('tfpWiz', (wiz.active && !wiz.adjust && wiz.step) ? wiz.step : ''); }catch(_){}
}
function wizCardShow(html){
  wizPersist();
  // A dragged card holds its spot for the whole step (including same-step
  // re-renders from ± clicks); a new step snaps back to the designed position.
  const posKey=String(wiz.step)+':'+(wiz.tourN||0)+':'+(wiz.tutN||0);
  if(wiz._cardPosKey!==posKey){ wiz._cardPosKey=posKey; wiz.cardDragMoved=false; }
  $('wizCardBody').innerHTML=html;
  $('wizStepNum').textContent=wizFootLabel();
  $('wizEnd').style.display = wiz.adjust ? 'none' : '';   // "End wizard" is meaningless mid-adjust
  const card=$('wizCard');
  card.classList.remove('hidden');
  card.classList.add('wizGlow');
  wizPulseStart();
  // Dim everything except the section this step is actually about.
  document.body.classList.add('wizFocus');
  collapseRightSections();
  if(wiz.step==='tour')            wizFocusSet(WIZ_TOUR[wiz.tourN||0].sec);
  else if(wiz.step==='roomAdjust') wizFocusSet('secRoom');
  else if(wiz.step==='tut')        wizFocusSet('secProducts');   // tutStep opened it — leave it alone
  else { collapseRails(); wizFocusSet(null); }
  requestAnimationFrame(()=>{
    // Steps that talk about a rail section sit beside it; every measurement step
    // lands in one fixed low spot so the card never bounces between steps.
    if(wiz.step==='tour')            wizCardPos('rail', $(WIZ_TOUR[wiz.tourN||0].sec));
    else if(wiz.step==='tut')        wizCardPos('rail', $('secProducts'));
    else if(wiz.step==='roomAdjust') wizCardPos('rail', $('secRoom'));
    else                             wizCardPos('bottom');
    // phones: park the sheet just above the card so both stay visible
    if(isMob()) document.documentElement.style.setProperty('--mobBottom', (card.offsetHeight+18)+'px');
  });
}
function wizCardHide(){
  const card=$('wizCard');
  wizPulseStop();
  card.classList.add('hidden');
  card.classList.remove('wizGlow');
  card.style.boxShadow='';
  document.documentElement.style.removeProperty('--mobBottom');
}
function wizUpdateTopbar(){
  const inPlan=!$('planView').classList.contains('hidden');
  $('btnReview').classList.toggle('hidden', !(inPlan && !wiz.active));
}
function wizStart(){
  wiz.active=true; wiz.step='age'; wizPersist();
  $('wizAge').value=String(state.settings.age||6);
  $('wizGateNum').textContent=wizStepLabel('age');
  $('wizGate').classList.remove('hidden');
  collapseRails(); collapseRightSections();
  document.body.classList.add('wizFocus');
  wizFocusSet(null);
  wizUpdateTopbar();
}
function wizStepWall(){
  wiz.step='wall';
  wizCamToWall(1, 2.2); wizDim(1,'len');   // backed out so the whole room + overhead bars show
  wizCardShow('<h3>How long is this wall?</h3>'+
    '<p>This will be your climbing wall \u2014 the pulsing arrow shows the wall-to-wall measurement we need. Pick yours and the room updates live.</p>'+
    '<div class="field"><label>Wall length</label>'+ftInMarkup('wizWall',4,40,1)+'</div>'+
    '<div class="wizNav"><button class="wizBack" id="wizBk1">\u2190 Back</button>'+
    '<button class="cta" id="wizNext1">Next: ceiling height \u2192</button></div>');
  wireFtIn('wizWall', ()=>walls()[1].len, len=>{
    pushUndo(); setWallLength(1, Math.max(48, Math.min(480, len)));
    afterSketchChange(); wizRefresh(); wizCamToWall(1, 2.2); wizDim(1,'len');
  }, 48, 480, 1);
  $('wizBk1').onclick=()=>{ wizDimOff(); wizCardHide(); wizStart(); };
  $('wizNext1').onclick=wizStepCeil;
}
function wizStepCeil(){
  wiz.step='ceil';
  wizCamToWall(1); wizDim(1,'height');   // re-fit so a taller ceiling never clips
  wizCardShow('<h3>How tall is your ceiling?</h3>'+
    '<p>Floor to ceiling, right where the arrow is \u2014 bar height and panel layout adjust automatically.</p>'+
    '<div class="field"><label>Ceiling height</label>'+ftInMarkup('wizCeil',6,20,1)+'</div>'+
    '<div class="wizNav"><button class="wizBack" id="wizBk2">\u2190 Back</button>'+
    '<button class="cta" id="wizNext2">Next \u2192</button></div>');
  wireFtIn('wizCeil', ()=>state.settings.ceil, v=>{
    feedCanonical('inCeil', fmtLen(Math.max(72, Math.min(240, v)))); wizRefresh(); wizCamToWall(1); wizDim(1,'height');
  }, 72, 240, 1);
  $('wizBk2').onclick=wizStepWall;
  $('wizNext2').onclick=wizStepBase;
}
function wizStepBase(){
  wiz.step='base';
  wizDimOff();
  wizCardShow('<h3>How tall are your baseboards?</h3>'+
    '<p>Not sure? 3.5&quot; is the most common in U.S. homes. We use this to keep panels and mats clear of your trim.</p>'+
    '<div class="field"><label>Baseboard height</label>'+ftInMarkup('wizBase',0,2,0.5)+'</div>'+
    '<div class="wizNav"><button class="wizBack" id="wizBkBase">\u2190 Back</button>'+
    '<button class="cta" id="wizNextBase">Next \u2192</button></div>');
  wireFtIn('wizBase', ()=>state.settings.base, v=>{
    feedCanonical('inBase', fmtLen(Math.max(0, Math.min(24, v)))); wizRefresh();
  }, 0, 24, 0.5);
  $('wizBkBase').onclick=wizStepCeil;
  $('wizNextBase').onclick=wizStepFork;
}
function wizStepFork(){
  wiz.step='fork'; wizDimOff(); guideOff();
  if(isMob()){
    // phones: no 2D editor and no wall-list detour \u2014 tweak later in the Room
    // tab, or take a complex room to the desktop editor
    wizCardShow('<h3>How\u2019s the rest of the room?</h3>'+
      '<p>Most families use just one climbing wall, plus the wall beside it for the ladder \u2014 '+
      'so you\u2019re probably set. Wall sizes can be tweaked any time in the Room tab. '+
      'Want a more complex layout? Finish on a computer.</p>'+
      '<button class="wizChoice" id="wizFkDesk">My room is more complex \u2014 open on desktop</button>'+
      '<div class="wizNav"><button class="wizBack" id="wizBk3">\u2190 Back</button>'+
      '<button class="cta" id="wizFkGo">Looks right \u2014 keep going \u2192</button></div>');
    $('wizBk3').onclick=wizStepBase;
    $('wizFkDesk').onclick=()=>openSaveBox('desktop');
    $('wizFkGo').onclick=()=>{ wiz.viaRoom=false; wizStepWallSize(); };
    return;
  }
  wizCardShow('<h3>How\u2019s the rest of the room?</h3>'+
    '<p>We assumed two 8-foot side walls.</p>'+
    '<button class="wizChoice" id="wizFkWalls">Adjust the other walls</button>'+
    '<button class="wizChoice" id="wizFk2d">My room is a complex shape</button>'+
    '<div class="wizNav"><button class="wizBack" id="wizBk3">\u2190 Back</button>'+
    '<button class="cta" id="wizFkGo">Looks right \u2014 keep going \u2192</button></div>');
  $('wizBk3').onclick=wizStepBase;
  $('wizFkWalls').onclick=wizStepRoomAdjust;
  $('wizFk2d').onclick=wizSuspendTo2D;
  $('wizFkGo').onclick=()=>{ wiz.viaRoom=false; wizStepWallSize(); };
}
function wallOpt(i){
  if(!state.wallOpts[i]) state.wallOpts[i]={p:false,t:false,s:false};
  return state.wallOpts[i];
}
function setWallOpt(i, key, on){
  wallOpt(i)[key]=!!on;
  if(key==='t' && !on && state.wallOpts[2]) state.wallOpts[2].s=false;
  if(key==='s' && on) wallOpt(1).t=true;
  state.genStamp=null;
  wizRefresh();
  syncRoomSection();
}
function wizStepRoomAdjust(){
  wiz.step='roomAdjust'; wiz.viaRoom=true;
  syncRoomSection();
  openRail('secRoom');
  wizCamToWall(1, 2.5);        // back far enough to read all three walls and their letters
  showGuides('walls');         // letters stay up for this whole stage
  wizCardShow('<h3>Adjust your room</h3>'+
    '<p>Use the Room section to tweak wall lengths, ceiling height, or baseboards.'+
    (isMob() ? ' The room updates behind this panel as you go.'
             : ' The flashing A, B, and C labels make it easier to spot each wall.')+'</p>'+
    '<div class="wizNav"><button class="wizBack" id="wizBkRoom">\u2190 Back</button>'+
    '<button class="cta" id="wizRoomNext">Keep going \u2192</button></div>');
  $('wizBkRoom').onclick=wizStepFork;
  $('wizRoomNext').onclick=wizStepWallSize;
}
/* ---- climbing wall sizing ---- */
function wallSizeInfo(){
  const S=state.settings, ws=walls();
  const w=ws[1]||ws[0]; if(!w) return null;
  const maxCols=Math.floor(w.len/PANEL_W);
  const maxRows=Math.max(1, Math.floor((S.ceil-S.base)/PANEL_H));
  const cols=S.wallCols==null ? maxCols : Math.max(1, Math.min(maxCols, S.wallCols));
  const rows=S.wallRows==null ? maxRows : Math.max(1, Math.min(maxRows, S.wallRows));
  return {w, maxCols, maxRows, cols, rows, on:!!(state.wallOpts[1] && state.wallOpts[1].p)};
}
function wallSizeAdj(dCols, dRows){
  const i=wallSizeInfo(); if(!i) return;
  const S=state.settings;
  if(dCols) S.wallCols=Math.max(1, Math.min(i.maxCols, i.cols+dCols));
  if(dRows) S.wallRows=Math.max(1, Math.min(i.maxRows, i.rows+dRows));
  state.genStamp=null;
  wizRefresh();
}
function wallSizeBy(dCols, dRows){    // wizard variant: re-renders the step card
  wallSizeAdj(dCols, dRows);
  wizStepWallSize();
}
function wizStepWallSize(){
  wiz.step='wallsize';
  wizDimOff(); guideOff();
  const i=wallSizeInfo();
  if(!i){ wizStepBars(); return; }
  wizCamToWall(1, 1.7);
  if(!i.on){
    wizCardShow('<h3>How does the climbing wall look?</h3>'+
      '<p>There is no climbing wall on this wall right now.</p>'+
      '<button class="wizChoice" id="wizWallAdd">Add a climbing wall</button>'+
      '<div class="wizNav"><button class="wizBack" id="wizBkWS">← Back</button>'+
      '<button class="cta" id="wizNextWS">Next →</button></div>');
    $('wizWallAdd').onclick=()=>{ setWallOpt(1,'p',true); wizStepWallSize(); };
  } else if(isMob()){
    // phones: three tight [−  label  +] rows so the card stays ~quarter-screen
    const total=i.cols*i.rows;
    const ht=holdTargets(), hs=Math.ceil(ht.total/10);
    wizCardShow('<h3>Climbing wall · '+i.cols+'×'+i.rows+' — '+total+' panels</h3>'+
      '<div class="wsRow"><button class="wsBtn2" id="wsNarrow"'+(i.cols<=1?' disabled':'')+'>−</button>'+
        '<span class="wsLbl">Width</span>'+
        '<button class="wsBtn2" id="wsWider"'+(i.cols>=i.maxCols?' disabled':'')+'>+</button></div>'+
      '<div class="wsRow"><button class="wsBtn2" id="wsShorter"'+(i.rows<=1?' disabled':'')+'>−</button>'+
        '<span class="wsLbl">Height</span>'+
        '<button class="wsBtn2" id="wsTaller"'+(i.rows>=i.maxRows?' disabled':'')+'>+</button></div>'+
      '<div class="wsRow"><button class="wsBtn2" id="wsHoldLess"'+(ht.total<=10?' disabled':'')+'>−</button>'+
        '<span class="wsLbl">Holds<small>'+ht.total+' · '+hs+' set'+(hs===1?'':'s')+' of 10</small></span>'+
        '<button class="wsBtn2" id="wsHoldMore"'+(ht.total>=ht.cap?' disabled':'')+'>+</button></div>'+
      '<button class="linkChoice" id="wsRemove">Remove the climbing wall</button>'+
      '<div class="wizNav"><button class="wizBack" id="wizBkWS">← Back</button>'+
      '<button class="cta" id="wizNextWS">Next →</button></div>');
    $('wsNarrow').onclick =()=>wallSizeBy(-1,0);
    $('wsWider').onclick  =()=>wallSizeBy(+1,0);
    $('wsShorter').onclick=()=>wallSizeBy(0,-1);
    $('wsTaller').onclick =()=>wallSizeBy(0,+1);
    $('wsHoldLess').onclick=()=>{ holdAdjBy(-1); wizStepWallSize(); };
    $('wsHoldMore').onclick=()=>{ holdAdjBy(+1); wizStepWallSize(); };
    $('wsRemove').onclick =()=>{ setWallOpt(1,'p',false); wizStepWallSize(); };
  } else {
    const total=i.cols*i.rows;
    const ht=holdTargets(), hs=Math.ceil(ht.total/10);
    wizCardShow('<h3>How does the climbing wall look?</h3>'+
      '<p id="wsInfo">'+i.cols+' across × '+i.rows+' high — '+total+' panel'+(total===1?'':'s')+
        '. It stays centred on the wall as you change it.</p>'+
      '<div class="wsGrid">'+
        '<button class="wizChoice wsBtn" id="wsNarrow"'+(i.cols<=1?' disabled':'')+'><span class="sign">−</span>Narrower</button>'+
        '<button class="wizChoice wsBtn" id="wsWider"'+(i.cols>=i.maxCols?' disabled':'')+'><span class="sign">+</span>Wider</button>'+
        '<button class="wizChoice wsBtn" id="wsShorter"'+(i.rows<=1?' disabled':'')+'><span class="sign">−</span>Shorter</button>'+
        '<button class="wizChoice wsBtn" id="wsTaller"'+(i.rows>=i.maxRows?' disabled':'')+'><span class="sign">+</span>Taller</button>'+
        '<button class="wizChoice wsBtn" id="wsHoldLess"'+(ht.total<=10?' disabled':'')+'><span class="sign">−</span>Fewer holds</button>'+
        '<button class="wizChoice wsBtn" id="wsHoldMore"'+(ht.total>=ht.cap?' disabled':'')+'><span class="sign">+</span>More holds</button>'+
      '</div>'+
      '<p class="wsHolds">'+ht.total+' holds · '+hs+' set'+(hs===1?'':'s')+' of 10</p>'+
      '<button class="wizChoice centered" id="wsRemove">Remove the climbing wall</button>'+
      '<div class="wizNav"><button class="wizBack" id="wizBkWS">← Back</button>'+
      '<button class="cta" id="wizNextWS">Next →</button></div>');
    $('wsNarrow').onclick =()=>wallSizeBy(-1,0);
    $('wsWider').onclick  =()=>wallSizeBy(+1,0);
    $('wsShorter').onclick=()=>wallSizeBy(0,-1);
    $('wsTaller').onclick =()=>wallSizeBy(0,+1);
    // holds are sold in sets of 10, so holdAdjBy(±1) moves 10 at a time
    $('wsHoldLess').onclick=()=>{ holdAdjBy(-1); wizStepWallSize(); };
    $('wsHoldMore').onclick=()=>{ holdAdjBy(+1); wizStepWallSize(); };
    $('wsRemove').onclick =()=>{ setWallOpt(1,'p',false); wizStepWallSize(); };
  }
  $('wizBkWS').onclick=()=> wiz.viaRoom ? wizStepRoomAdjust() : wizStepFork();
  $('wizNextWS').onclick=wizStepBars;
  if(wiz.adjust){                       // opened from the Build tab, not the wizard
    $('wizBkWS').onclick=mobAdjustBack;
    $('wizNextWS').textContent='Done';
    $('wizNextWS').onclick=mobAdjustBack;
  }
}
function wizStepBars(){
  wiz.step='bars';
  wizCamToWall(1);             // hold the hero view (also covers resume-after-refresh)
  showGuides('bars');          // existing bars flash until they answer
  wizCardShow('<h3>Do you want to keep the overhead monkey bars?</h3>'+
    '<p>The green preview shows where the overhead rails sit. You can still fine-tune the layout later. '+
    'Heads up: the 2×4 stringers shown are standard lumber you pick up locally — only the bars come in the box.</p>'+
    '<button class="wizChoice keep" id="wizBarsYes">Yes, keep the overhead bars</button>'+
    '<button class="wizChoice" id="wizBarsNo">No, remove them</button>'+
    '<div class="wizNav"><button class="wizBack" id="wizBkBars">\u2190 Back</button></div>');
  $('wizBkBars').onclick=wizStepWallSize;
  $('wizBarsYes').onclick=()=>{ setWallOpt(1, 't', true); wizStepLadder(); };
  $('wizBarsNo').onclick=()=>{ setWallOpt(1, 't', false); wizStepLadder(); };
  if(wiz.adjust){
    $('wizBkBars').onclick=mobAdjustBack;
    $('wizBarsYes').onclick=()=>{ setWallOpt(1, 't', true); mobAdjustBack(); };
    $('wizBarsNo').onclick=()=>{ setWallOpt(1, 't', false); mobAdjustBack(); };
  }
}
function wizStepLadder(){
  wiz.step='ladder';
  wizCamToWall(1);             // hold the hero view
  showGuides('ladder');        // existing ladder flashes until they answer
  const hint=(wallOpt(1).t
    ? 'The green preview shows the monkey bar ladder climbing up to the overhead bars.'
    : 'If you keep the monkey bar ladder, we will turn the overhead bars back on too so the ladder has somewhere to go.')+
    ' The 2×4 stringers shown are standard lumber you pick up locally — only the bars come in the box.';
  wizCardShow('<h3>Do you want to keep the monkey bar ladder?</h3>'+
    '<p>'+hint+'</p>'+
    '<button class="wizChoice keep" id="wizLadYes">Yes, keep the ladder</button>'+
    '<button class="wizChoice" id="wizLadNo">No, remove it</button>'+
    '<div class="wizNav"><button class="wizBack" id="wizBkLad">\u2190 Back</button></div>');
  $('wizBkLad').onclick=wizStepBars;
  $('wizLadYes').onclick=()=>{ setWallOpt(2, 's', true); wizStepMats(); };
  $('wizLadNo').onclick=()=>{ setWallOpt(2, 's', false); wizStepMats(); };
  if(wiz.adjust){
    $('wizBkLad').onclick=mobAdjustBack;
    $('wizLadYes').onclick=()=>{ setWallOpt(2, 's', true); mobAdjustBack(); };
    $('wizLadNo').onclick=()=>{ setWallOpt(2, 's', false); mobAdjustBack(); };
  }
}
/* ---- climbing mats ---- */
function matInfo(){
  const ws=walls(); if(!ws.length) return null;
  const placed=state.objects.filter(o=>o.type==='mat').length;
  return {placed};
}
/* No ceiling on mats — ask for more and they wrap onto the next wall, then work
   inward. The generator simply runs out of floor, which caps it naturally. */
function matsAdj(d){
  const i=matInfo(); if(!i) return;
  const cur=(state.settings.matCount==null ? i.placed : state.settings.matCount);
  const want=Math.max(0, cur+d);
  state.settings.matCount=want;
  state.genStamp=null;
  wizRefresh();
  // if the floor is full, snap the stored value back to what actually fit
  const got=state.objects.filter(o=>o.type==='mat').length;
  if(d>0 && got<want) state.settings.matCount=got;
}
function matsBy(d){                   // wizard variant: re-renders the step card
  matsAdj(d);
  wizStepMats();
}
function wizStepMats(){
  wiz.step='mats';
  wizDimOff();
  wizCamToWall(1);             // hold the hero view
  showGuides('mats');
  const i=matInfo();
  if(!i){ wizTour(0); return; }
  const n=i.placed;
  if(isMob()){
    wizCardShow('<h3>Climbing mats · '+n+'</h3>'+
      '<div class="wsRow"><button class="wsBtn2" id="wizMatLess"'+(n<=0?' disabled':'')+'>−</button>'+
        '<span class="wsLbl">Mats<small>'+(n
          ? 'Add more and they wrap around the room'
          : 'We recommend mats under any climbing surface')+'</small></span>'+
        '<button class="wsBtn2" id="wizMatMore">+</button></div>'+
      (n?'<button class="linkChoice" id="wizMatNone">Remove the mats</button>':'')+
      '<div class="wizNav"><button class="wizBack" id="wizBkMat">← Back</button>'+
      '<button class="cta" id="wizMatNext">Next →</button></div>');
  } else {
  wizCardShow('<h3>Do you want to keep the climbing mats?</h3>'+
    '<p>'+(n
      ? n+' mat'+(n===1?'':'s')+' on the floor. Add more and they wrap around the room.'
      : 'No mats right now — we recommend them under any climbing surface.')+'</p>'+
    '<div class="wsGrid">'+
      '<button class="wizChoice wsBtn" id="wizMatLess"'+(n<=0?' disabled':'')+'><span class="sign">−</span>Fewer mats</button>'+
      '<button class="wizChoice wsBtn" id="wizMatMore"><span class="sign">+</span>More mats</button>'+
    '</div>'+
    (n?'<button class="wizChoice centered" id="wizMatNone">Remove the mats</button>':'')+
    '<div class="wizNav"><button class="wizBack" id="wizBkMat">← Back</button>'+
    '<button class="cta" id="wizMatNext">Next →</button></div>');
  }
  $('wizMatLess').onclick=()=>matsBy(-1);
  $('wizMatMore').onclick=()=>matsBy(+1);
  if($('wizMatNone')) $('wizMatNone').onclick=()=>{
    state.settings.matCount=0; state.genStamp=null; wizRefresh(); wizStepMats();
  };
  $('wizBkMat').onclick=wizStepLadder;
  // phones skip the hands-on tutorial (no drag editing) and the Products tour stop
  $('wizMatNext').onclick=()=> isMob() ? wizTour(1) : tutStart();
  if(wiz.adjust){
    $('wizBkMat').onclick=mobAdjustBack;
    $('wizMatNext').textContent='Done';
    $('wizMatNext').onclick=mobAdjustBack;
  }
}
/* Complex-shape detour: park the wizard, hand the customer the 2D editor, and
   pick back up at the bars question once they say they're done. */
function wizSuspendTo2D(){
  wiz.suspended=true;
  wizDimOff(); guideOff(); wizCardHide(); wizFocusOff();
  showSketch();
}
function wizResumeFrom2D(){
  if(!wiz.suspended) return;
  wiz.suspended=false;
  showPlan();
  $('wizResumeBar').classList.add('hidden');
  if(!walls().length){ wizFinish(true); return; }   // nothing drawn — just let them design
  wiz.viaRoom=false;
  wizStepBars();
}
$('wizResumeGo').onclick=wizResumeFrom2D;

/* ---------- hands-on tutorial: pick a panel, place it, drag it ----------
   Each stage parks on wiz.tutWait and is advanced by tutNotify() from the real
   product click / placement / drag-end handlers, so the customer genuinely does
   the action rather than watching a demo. */
const tut={active:false, wait:null, placedId:null};
function tutNotify(evt, arg){
  if(!tut.active || tut.wait!==evt) return;
  if(evt==='pick' && arg!=='half_panel') return;      // wait for the panel we asked for
  tut.wait=null;
  if(evt==='place' && arg) tut.placedId=arg.id;
  tutStep(evt==='pick' ? 2 : evt==='place' ? 3 : 4);
}
function blankWallIdx(){
  for(const w of walls()) if(!(state.wallOpts[w.i] && state.wallOpts[w.i].p)) return w.i;
  return 0;
}
function tutHighlightProduct(on){
  const btn=document.querySelector('.prod[data-type="half_panel"]');
  if(btn) btn.classList.toggle('tutPoint', !!on);
}
function tutStart(){ tut.active=true; tut.placedId=null; tutStep(0); }
function tutEnd(){
  tut.active=false; tut.wait=null;
  tutHighlightProduct(false);
  document.body.classList.remove('tutPlacing');
}
function tutStep(n){
  wiz.step='tut'; wiz.tutN=n;
  wizDimOff();
  if(n!==2) guideOff();               // step 2 keeps its blank-wall flash
  openRail('secProducts'); tourHiOff();
  $('secProducts').classList.add('tourHi');
  wizFocusSet('secProducts');
  document.body.classList.add('wizFocus');

  if(n===0){
    tut.wait=null; tutHighlightProduct(false);
    frameRoom();
    wizCardShow('<h3 class="tutBig">Let me show you how this works</h3>'+
      '<p>Three quick steps and you will know everything you need to design your own playroom.</p>'+
      '<div class="wizNav"><button class="wizBack" id="tutBk">← Back</button>'+
      '<button class="cta" id="tutGo">Show me →</button></div>');
    $('tutBk').onclick=()=>{ tutEnd(); wizStepMats(); };
    $('tutGo').onclick=()=>tutStep(1);
    return;
  }
  if(n===1){
    tut.wait='pick';
    tutHighlightProduct(true);
    wizCamToWall(blankWallIdx(), 2.4);
    wizCardShow('<h3>Click the Half Panel</h3>'+
      '<p>Everything you can add lives in <b>Build</b> on the left. Click the flashing '+
      '<b>Half Panel</b> to pick it up.</p>'+
      '<div class="wizNav"><button class="wizBack" id="tutBk">← Back</button>'+
      '<button class="wizEnd" id="tutSkip">Skip the tutorial</button></div>');
    $('tutBk').onclick=()=>tutStep(0);
    $('tutSkip').onclick=()=>{ tutEnd(); wizTour(1); };
    return;
  }
  if(n===2){
    tut.wait='place';
    tutHighlightProduct(false);
    document.body.classList.add('tutPlacing');
    wizCamToWall(blankWallIdx(), 2.4);
    showGuides('blankwall');           // flashes until they click it
    wizCardShow('<h3>Now click the empty wall</h3>'+
      '<p>You are holding a panel. Click anywhere on the blank wall in front of you and it '+
      'will snap into place.</p>'+
      '<div class="wizNav"><button class="wizBack" id="tutBk">← Back</button>'+
      '<button class="wizEnd" id="tutSkip">Skip the tutorial</button></div>');
    $('tutBk').onclick=()=>{ setPlacing(null); tutStep(1); };
    $('tutSkip').onclick=()=>{ setPlacing(null); tutEnd(); wizTour(1); };
    return;
  }
  if(n===3){
    tut.wait='drag';
    document.body.classList.remove('tutPlacing');
    setPlacing(null);        // still holding a panel otherwise — you can't drag while placing
    if(tut.placedId!=null){ selIds=new Set([tut.placedId]); refreshObjects(); }
    wizCardShow('<h3>Now drag it around</h3>'+
      '<p>Nice. Drag that panel across the wall to move it — everything in the room works '+
      'the same way. Select it and you can rotate or delete it too.</p>'+
      '<div class="wizNav"><button class="wizEnd" id="tutSkip">Skip the tutorial</button></div>');
    $('tutSkip').onclick=()=>{ tutEnd(); wizTour(1); };
    return;
  }
  // n===4 — done
  tut.wait=null;
  wizCardShow('<h3>That is all there is to it</h3>'+
    '<p>Click a product, click where it goes, drag to adjust. Let me show you the rest of '+
    'the panel.</p>'+
    '<div class="wizNav"><button class="wizBack" id="tutBk">← Back</button>'+
    '<button class="cta" id="tutNext">Next →</button></div>');
  $('tutBk').onclick=()=>tutStep(3);
  $('tutNext').onclick=()=>{ tutEnd(); wizTour(1); };
}

/* Click-through tour of the left rail, in rail order. */
const WIZ_TOUR=[
  {sec:'secProducts', title:'Build your playroom', body:'Everything you can add lives here. Click a product, then click a wall or the ceiling to place it. Drag anything in the room to move it, or use the controls to delete or rotate it.'},
  {sec:'secColors', title:'Make it yours', body:'Use this section to change the colors of your playroom \u2014 walls, climbing panels, and holds update instantly.'},
  {sec:'secRoom', title:'Adjust your room', body:'Use this section to change your room whenever you need \u2014 wall lengths, ceiling height, baseboards, and your youngest climber\u2019s age.'},
  {sec:null, title:'Moving around', body:'Last thing — getting around your room. <b>Left-click and drag</b> to spin it. <b>Scroll</b> to zoom in and out. <b>Right-click and drag</b> (or hold Shift while dragging) to slide the view. Give it a try, then you’re ready to build.'}
];
function tourHiOff(){ document.querySelectorAll('#palette .tourHi').forEach(e=>e.classList.remove('tourHi')); }
function wizTour(n){
  wiz.step='tour'; wiz.tourN=n; wizDimOff(); guideOff();
  wizCamToWall(1, 2.5);   // same pulled-back front view on every tip, so the room stays readable
  // phones: Colors is the only tour stop (Room lives in its tab; no View section)
  // desktop now ends on the section-less "Moving around" tip (index 3); phones stop at Colors
  const t=WIZ_TOUR[n], last=isMob() ? n>=1 : n>=3;
  openRail(t.sec); tourHiOff();
  const sec=$(t.sec);
  if(sec){ sec.classList.add('tourHi'); sec.scrollIntoView({block:'nearest', behavior:'smooth'}); }
  if(t.sec==='secRoom'){ syncRoomSection(); showGuides('walls'); }
  wizCardShow('<h3>'+t.title+'</h3><p>'+t.body+'</p>'+
    '<div class="wizNav"><button class="wizBack" id="wizTB">\u2190 Back</button>'+
    '<button class="cta" id="wizTN">'+(last?(isMob()?'Almost done \u2192':'Got it \u2014 let me play \u2192'):'Next \u2192')+'</button></div>');
  $('wizTB').onclick=()=> n===0 ? wizStepMats()
    : (n===1 ? (isMob() ? wizStepMats() : tutStart()) : wizTour(n-1));
  $('wizTN').onclick=()=> last ? (isMob() ? wizStepHandoff() : wizFinish(true)) : wizTour(n+1);
}
/* Phone-only closing step: the pitch to finish on a computer. */
function wizStepHandoff(){
  wiz.step='handoff';
  wizDimOff(); guideOff(); tourHiOff(); mobShowSheet(null);
  wizCamToWall(1, 2.5);
  wizCardShow('<h3>Want to fine-tune it?</h3>'+
    '<p>Every panel, bar, and mat can be moved, added, or removed on the desktop version. '+
    '<b>Save this design</b> and we’ll email you a link that opens right where you left off.</p>'+
    '<button class="wizChoice" id="wizHandSave">Save this design</button>'+
    '<div class="wizNav"><button class="wizBack" id="wizHandBk">← Back</button>'+
    '<button class="cta" id="wizHandDone">Done — let me look around →</button></div>');
  $('wizHandSave').onclick=()=>openSaveBox('desktop');
  $('wizHandBk').onclick=()=>wizTour(1);   // handoff is phone-only; Colors is the last tour stop there
  $('wizHandDone').onclick=()=>wizFinish(true);
}
function wizResume(step){
  const map={wall:wizStepWall, ceil:wizStepCeil, base:wizStepBase, fork:wizStepFork,
             roomAdjust:wizStepRoomAdjust, wallsize:wizStepWallSize, bars:wizStepBars,
             ladder:wizStepLadder, mats:wizStepMats, handoff:wizStepHandoff,
             tut:()=>tutStep(0), tour:()=>wizTour(0)};
  const go=map[step];
  if(!go){ wizStart(); return; }                    // 'age' (or unknown) → back to the gate
  // Rebuild the wizard context wizStart normally sets up, minus the age gate.
  wiz.active=true;
  collapseRails(); collapseRightSections();
  document.body.classList.add('wizFocus');
  wizUpdateTopbar();
  go();
}
function wizFinish(){
  wiz.active=false; wiz.step=null; wiz.suspended=false; wiz.adjust=false; wizPersist();
  const bar=$('wizResumeBar'); if(bar) bar.classList.add('hidden');
  wizDimOff(); wizCardHide(); tourHiOff();
  guideOff(); wizFocusOff(); tutEnd();
  openRail('secProducts');
  wizCamToWall(1, 2.5);        // leave them on the pulled-back front view, whole room in frame
  $('wizGate').classList.add('hidden');
  wizUpdateTopbar();
}
$('wizEnd').onclick=()=>wizFinish(true);
$('wizGateEnd').onclick=()=>{ $('wizGate').classList.add('hidden'); wizFinish(false); };
window.addEventListener('resize', ()=>{
  if(!wiz.active) return;
  if(wiz.step==='tour')            wizCardPos('rail', $(WIZ_TOUR[wiz.tourN||0].sec));
  else if(wiz.step==='tut')        wizCardPos('rail', $('secProducts'));
  else if(wiz.step==='roomAdjust') wizCardPos('rail', $('secRoom'));
  else                             wizCardPos('bottom');
});
$('wizAgeGo').onclick=()=>{
  const v=Math.max(2, Math.min(8, parseInt($('wizAge').value,10)||6));
  state.settings.age=v;
  if($('ageSel')) $('ageSel').value=String(v);
  applyAgeGate();
  $('wizGate').classList.add('hidden');
  wizRefresh(); syncRoomSection();
  requestAnimationFrame(()=>{ resize(); wizStepWall(); });
};
attachStepper($('wizAge'), {step:1, min:2, max:8, parse:v=>parseInt(v,10)||6, format:v=>String(Math.round(v))});
$('wizAge').addEventListener('keydown', e=>{ if(e.key==='Enter') $('wizAgeGo').click(); });

/* ---- Review modal ---- */
$('btnReview').onclick=()=>{
  frameRoom(); refreshPanels();
  $('reviewBody').innerHTML=$('bomTable').innerHTML;
  $('review').classList.remove('hidden');
};
function closeReview(){ $('review').classList.add('hidden'); }
$('reviewClose').onclick=closeReview;
$('reviewBack').onclick=closeReview;
$('reviewPdf').onclick=()=>{ closeReview(); $('btnPdf').click(); };
$('reviewCart').onclick=()=>{ closeReview(); $('btnCart').click(); };
$('review').addEventListener('click', e=>{ if(e.target===$('review')) closeReview(); });

/* -------- init -------- */
function syncSettingInputs(){
  $('inCeil').value=fmtLen(state.settings.ceil);
  $('inBase').value=fmtLen(state.settings.base);
  syncFtInPair('inCeil', state.settings.ceil, 1);
  syncFtInPair('inBase', state.settings.base, 0.5);

  $('inStudOff').value=fmtInches(state.settings.studOff);
  $('inJoistOff').value=fmtInches(state.settings.joistOff);
  $('inStudSp').value=fmtInches(state.settings.studSp);
  $('inJoistSp').value=fmtInches(state.settings.joistSp);
  $('ageSel').value=String(state.settings.age);
}
const restored=loadHash();
if(!(restored && state.points.length)) buildDefaultRoom();
clampHoldAdj();
syncSettingInputs();
renderWallList(); renderColorRows(); drawSketch();
mountFtIn('inCeilUi', 'inCeil', ()=>state.settings.ceil, v=>feedCanonical('inCeil', fmtLen(v)), 48, 240, 1);
mountFtIn('inBaseUi', 'inBase', ()=>state.settings.base, v=>feedCanonical('inBase', fmtLen(v)), 0, 24, 0.5);
mountFtIn('rmCeilUi', 'rmCeil', ()=>state.settings.ceil, v=>feedCanonical('inCeil', fmtLen(v)), 48, 240, 1);
mountFtIn('rmBaseUi', 'rmBase', ()=>state.settings.base, v=>feedCanonical('inBase', fmtLen(v)), 0, 24, 0.5);
/* Big − / + buttons flanking number boxes (phones hide the tiny inline arrows).
   They just proxy the stepper's own buttons, so bounds and formatting hold. */
function mobBigStepper(inputId){
  const inp=$(inputId); if(!inp) return;
  const wrap=inp.closest('.stepper'); if(!wrap) return;
  if(wrap.previousElementSibling && wrap.previousElementSibling.classList.contains('bigStep')) return;
  const btns=wrap.querySelectorAll('.stpBtn');      // [0] = ▴ up, [1] = ▾ down
  if(btns.length<2) return;
  const mk=(txt,target,label)=>{ const b=document.createElement('button'); b.type='button';
    b.className='bigStep'; b.textContent=txt; b.setAttribute('aria-label',label);
    b.onclick=()=>target.click(); return b; };
  wrap.before(mk('−', btns[1], 'Lower'));
  wrap.after(mk('+', btns[0], 'Raise'));
}
mobBigStepper('wizAge');
mobBigStepper('rmAge');
mobPlaceBom();
renderBuildControls();
loadStock();     // fetch live Shopify stock, then re-render swatches with sold-outs greyed
window.addEventListener('resize', drawSketch);
let wizSaved=null;
try{ wizSaved=sessionStorage.getItem('tfpWiz'); }catch(_){}
if(restored && state.points.length){
  if(wizSaved){                       // refreshed mid-wizard — pick up where they left off
    showPlan._framed=true;
    showPlan();
    if(!$('planView').classList.contains('hidden')){ frameRoom(); wizResume(wizSaved); }
  }else{
    showPlan();
  }
}else{
  showPlan._framed=true;              // the wizard drives the camera
  showPlan();
  if(!$('planView').classList.contains('hidden')){ frameRoom(); wizStart(); }
}
/* App is fully booted — lift the loading splash that covered the raw page. */
(function(){
  const s=document.getElementById('bootSplash');
  if(!s) return;
  s.style.opacity='0';
  setTimeout(()=>s.remove(), 400);
})();
